import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { collection, onSnapshot, doc, serverTimestamp, getDoc, query, orderBy, limit, getDocs, where } from '../firebase.config';
import { enforcedAddDoc as addDoc, enforcedSetDoc as setDoc, enforcedUpdateDoc as updateDoc, enforcedDeleteDoc as deleteDoc } from '../utils/enforcedTracking';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import ReceiptTemplate from './BillTemplates/ReceiptTemplate';
import PurchaseReceiptTemplate from './BillTemplates/PurchaseReceiptTemplate';
import ReactDOM from 'react-dom';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import { useTableSort, SortableHeader } from '../utils/tableSort';
import { useTablePagination } from '../utils/tablePagination';
import PaginationControls from '../utils/PaginationControls';
import { useCompanyCurrency } from '../hooks/useCompanyCurrency';
import { formatCurrency, getCurrencySymbol } from '../utils/currencyUtils';
import { useCompanySwitchGuard } from '../hooks/useCompanySwitchGuard';
import {
  getPartyAdvance as getPartyAdvanceUtil,
  allocateAdvanceToBill as allocateAdvanceToBillUtil,
  markAdvanceUsed as markAdvanceUsedUtil,
  refundAdvance as refundAdvanceUtil,
  reverseAdvanceUsed as reverseAdvanceUsedUtil,
  getAdvanceDetails as getAdvanceDetailsUtil,
  isAdvancePayment as isAdvancePaymentUtil,
  applyAdvanceToBills as applyAdvanceToBillsUtil
} from '../utils/advanceUtils';
import jsPDF from 'jspdf';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { enhancedDb, db, auth, app } from '../firebase.config';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { EmployeeCashManager } from '../utils/employeeCashManager';
import { getCollectionForCompany, normalizeCompanyDocSnapshot } from '../utils/dataAdapter';
import { convertTimestamp } from '../utils/dateUtils';
import ShareButton from './Reports/ShareButton';
import ExportShareDropdown from './ExportShareDropdown';
import { exportTableAsPDF, exportTableAsExcel } from './Reports/exportUtils';
import { useNotification } from '../contexts/NotificationContext';
import WorkOrderLinkSelect from './WorkOrders/WorkOrderLinkSelect';
import {
  appendWorkOrderFields,
  syncLedgerFromSource,
  removeLedgerEntry,
  LEDGER_SOURCE_TYPES,
  isPaymentOutflow,
} from '../utils/workOrderLedger';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { ConfirmationModal, MODAL_SECTIONS } from './Modal';
import {
  FileText,
  Users,
  Receipt,
  User,
  Calendar,
  Wallet,
  TrendingUp,
  TrendingDown,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Plus,
  X,
  ShoppingCart,
  BarChart3,
  ClipboardList,
} from 'lucide-react';

// New Export System Imports
import GlobalExportButton from './Export/GlobalExportButton';
import GlobalFilter from './Filters/GlobalFilter';
import { useGlobalExport } from '../hooks/useGlobalExport';

// Payments Filter Imports
import { usePaymentsFilter } from '../hooks/usePaymentsFilter';
import CompactPaymentsFilter from './Filters/CompactPaymentsFilter';
import MobileScrollTabs from './shared/MobileScrollTabs';
import AppSelect from './shared/AppSelect';
import ModalOverlay from './shared/ModalOverlay';

const PAYMENT_MAIN_TABS = [
  { id: 'invoice', label: 'Invoice', mobileLabel: 'Invoice', icon: FileText },
  { id: 'challan', label: 'Challan', mobileLabel: 'Challan', icon: ClipboardList },
  { id: 'purchase', label: 'Purchase', mobileLabel: 'Purchase', icon: ShoppingCart },
  { id: 'receipts', labelKey: 'paymentReceipts', mobileLabel: 'Receipts', icon: Receipt },
  { id: 'mode', labelKey: 'paymentMode', mobileLabel: 'Mode', icon: BarChart3 },
];

const RECEIPT_SUB_TABS = [
  { id: 'invoice', label: 'Invoice' },
  { id: 'challan', label: 'Challan' },
  { id: 'purchase', label: 'Purchase' },
];

const btnPrimarySm =
  'inline-flex items-center justify-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm';
const btnSecondarySm =
  'inline-flex items-center justify-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors';
const btnDangerSm =
  'inline-flex items-center justify-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors';
const btnSuccessSm =
  'inline-flex items-center justify-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors';
const modalOverlayClass = 'fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm p-0 sm:p-4';
const modalPanelClass =
  'bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-h-[min(88dvh,calc(100dvh-var(--safe-area-inset-top,env(safe-area-inset-top,0px))-var(--safe-area-inset-bottom,env(safe-area-inset-bottom,0px))))] sm:max-h-[90vh] overflow-y-auto border border-slate-200/80 pb-[var(--safe-area-inset-bottom,env(safe-area-inset-bottom,0px))] sm:pb-0';
const paymentModalPanelClass =
  'bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-h-[min(88dvh,calc(100dvh-var(--safe-area-inset-top,env(safe-area-inset-top,0px))-var(--safe-area-inset-bottom,env(safe-area-inset-bottom,0px))))] sm:max-h-[90vh] overflow-y-auto border-t sm:border border-slate-200/80 p-4 sm:p-6 sm:max-w-md sm:mx-auto';

// jsPDF is imported from module; plugin will be loaded dynamically where needed

// Comprehensive Export Functions (from Reports.js)
// Utility: Convert array of objects to CSV string
const arrayToCSV = (arr, columns) => {
  const escape = (str) => `"${String(str).replace(/"/g, '""')}"`;
  const header = columns.map(col => escape(col.label)).join(',');
  const rows = arr.map(row => columns.map(col => escape(row[col.key] ?? '')).join(','));
  return [header, ...rows].join('\r\n');
};

// Utility: Export to Excel
const arrayToExcel = (arr, columns, filename) => {
  const wsData = [columns.map(col => col.label), ...arr.map(row => columns.map(col => row[col.key] ?? ''))];
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Report');
  XLSX.writeFile(wb, filename);
};

// Utility: Export to PDF with letterhead
const arrayToPDF = async (arr, columns, filename, title = 'Report', companyDetails = {}) => {
  const doc = new jsPDF();
  let y = 16;
  
  // Draw logo if available
  if (companyDetails.logoUrl) {
    try {
      // Fetch image as base64
      const imgData = await fetch(companyDetails.logoUrl)
        .then(res => res.blob())
        .then(blob => new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        }));
      doc.addImage(imgData, 'PNG', 14, 10, 24, 24); // x, y, width, height
    } catch (e) {
      console.warn('Could not load logo for PDF:', e);
    }
  }
  
  // Company details (name, address, GSTIN, etc.)
  const leftX = companyDetails.logoUrl ? 40 : 14;
  doc.setFontSize(14);
  doc.text(companyDetails.firmName || '', leftX, 16);
  doc.setFontSize(10);
  let detailsY = 22;
  if (companyDetails.address) {
    doc.text(companyDetails.address, leftX, detailsY);
    detailsY += 6;
  }
  if (companyDetails.city || companyDetails.state || companyDetails.pincode) {
    doc.text(
      [companyDetails.city, companyDetails.state, companyDetails.pincode].filter(Boolean).join(', '),
      leftX, detailsY
    );
    detailsY += 6;
  }
  if (companyDetails.gstin) {
    doc.text('GSTIN: ' + companyDetails.gstin, leftX, detailsY);
    detailsY += 6;
  }
  if (companyDetails.contactNumber) {
    doc.text('Contact: ' + companyDetails.contactNumber, leftX, detailsY);
    detailsY += 6;
  }
  if (companyDetails.email) {
    doc.text('Email: ' + companyDetails.email, leftX, detailsY);
    detailsY += 6;
  }
  
  // Report title
  doc.setFontSize(12);
  doc.text(title, 14, detailsY + 6);
  
  // Prepare table data with styling for total rows
  const tableData = arr.map((row, index) => {
    const isTotalRow = row.billNumber?.includes('TOTAL') || 
                      row.partyName?.includes('TOTAL') || 
                      row.receiptNumber?.includes('TOTAL');
    
    return {
      data: columns.map(col => row[col.key] ?? ''),
      styles: isTotalRow ? { 
        fontStyle: 'bold', 
        fillColor: [240, 240, 240],
        textColor: [0, 0, 0]
      } : {}
    };
  });
  
  // Table (use plugin function form)
  try {
    const { default: autoTable } = await import('jspdf-autotable');
    autoTable(doc, {
      head: [columns.map(col => col.label)],
      body: tableData.map(row => row.data),
      startY: detailsY + 12,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 249, 250] },
      didParseCell: function(data) {
        // Apply custom styling to total rows
        if (data.row.index < arr.length) {
          const row = arr[data.row.index];
          const isTotalRow = row.billNumber?.includes('TOTAL') || 
                            row.partyName?.includes('TOTAL') || 
                            row.receiptNumber?.includes('TOTAL');
          
          if (isTotalRow) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [240, 240, 240];
            data.cell.styles.textColor = [0, 0, 0];
          }
        }
      }
    });
  } catch (e) {
    console.warn('jspdf-autotable not available for Payments arrayToPDF:', e);
  }
  doc.save(filename);
};

// Multi-table export functions
const multiTablePDF = async (tables, filename, title = 'Report', companyDetails = {}) => {
  const doc = new jsPDF();
  let y = 16;
  
  // Draw logo if available
  if (companyDetails.logoUrl) {
    try {
      const imgData = await fetch(companyDetails.logoUrl)
        .then(res => res.blob())
        .then(blob => new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        }));
      doc.addImage(imgData, 'PNG', 14, 10, 24, 24);
    } catch (e) {
      console.warn('Could not load logo for PDF:', e);
    }
  }
  
  // Company details
  const leftX = companyDetails.logoUrl ? 40 : 14;
  doc.setFontSize(14);
  doc.text(companyDetails.firmName || '', leftX, 16);
  doc.setFontSize(10);
  let detailsY = 22;
  if (companyDetails.address) {
    doc.text(companyDetails.address, leftX, detailsY);
    detailsY += 6;
  }
  if (companyDetails.gstin) {
    doc.text('GSTIN: ' + companyDetails.gstin, leftX, detailsY);
    detailsY += 6;
  }
  
  // Report title
  doc.setFontSize(16);
  doc.text(title, 14, detailsY + 6);
  detailsY += 12;
  
  // Add each table synchronously to preserve layout
  for (let index = 0; index < tables.length; index += 1) {
    const table = tables[index];
    if (index > 0) {
      doc.addPage();
      detailsY = 25;
    }

    // Table title
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(table.tableTitle, 14, detailsY);
    detailsY += 8;

    try {
      const { default: autoTable } = await import('jspdf-autotable');
      autoTable(doc, {
        head: [table.columns.map(col => col.label)],
        body: table.arr.map(row => table.columns.map(col => row[col.key] ?? '')),
        startY: detailsY,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [41, 128, 185] },
        bodyStyles: { fontSize: 8 },
        alternateRowStyles: { fillColor: [248, 249, 250] },
        didParseCell: function(data) {
          if (data.row.index < table.arr.length) {
            const row = table.arr[data.row.index];
            const isTotalRow = row.billNumber?.includes('TOTAL') ||
                              row.partyName?.includes('TOTAL') ||
                              row.receiptNumber?.includes('TOTAL');
            if (isTotalRow) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [240, 240, 240];
              data.cell.styles.textColor = [0, 0, 0];
            }
          }
        }
      });
      detailsY = (doc.lastAutoTable && doc.lastAutoTable.finalY ? doc.lastAutoTable.finalY : detailsY) + 10;
    } catch (e) {
      console.warn('jspdf-autotable not available for Payments multi-table export:', e);
    }
  }
  
  doc.save(filename);
};

const multiTableExcel = (tables, filename) => {
  const wb = XLSX.utils.book_new();
  
  tables.forEach((table, index) => {
    const wsData = [
      table.columns.map(col => col.label),
      ...table.arr.map(row => table.columns.map(col => row[col.key] ?? ''))
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, table.tableTitle || `Sheet${index + 1}`);
  });
  
  XLSX.writeFile(wb, filename);
};

const multiTableCSV = async (tables, filenameBase) => {
  if (tables.length === 1) {
    // Single table - export directly
    const table = tables[0];
    const csvContent = arrayToCSV(table.arr, table.columns);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filenameBase}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else {
    // Multiple tables - create ZIP file
    const zip = new JSZip();
    
    tables.forEach((table, index) => {
      const csvContent = arrayToCSV(table.arr, table.columns);
      const sheetName = table.tableTitle || `Sheet${index + 1}`;
      zip.file(`${sheetName}.csv`, csvContent);
    });
    
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(zipBlob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filenameBase}.zip`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

// Legacy export functions (for backward compatibility)
const exportToCSV = (data, filename) => {
  if (!data || data.length === 0) return;
  
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => headers.map(header => `"${row[header] || ''}"`).join(','))
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const exportToExcel = (data, filename) => {
  if (!data || data.length === 0) return;
  
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join('\t'),
    ...data.map(row => headers.map(header => row[header] || '').join('\t'))
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'application/vnd.ms-excel' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.xls`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const exportToPDF = (data, filename, title, companyData = {}) => {
  if (!data || data.length === 0) return;
  
  try {
    // Get jsPDF instance with fallback
    const jsPDF = window.jsPDF || (window.jspdf && window.jspdf.jsPDF);
    if (!jsPDF) {
      console.error('jsPDF not available');
      return;
    }
    
    const doc = new jsPDF();
    const headers = Object.keys(data[0]);
    const tableData = data.map(row => headers.map(header => row[header] || ''));
    
    // Add letterhead with company details
    doc.setFillColor(66, 139, 202);
    doc.rect(0, 0, doc.internal.pageSize.width, 30, 'F');
    
    // Company name in header (use actual company name if available)
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    const companyName = companyData?.firmName || 'ACCTOO';
    doc.text(companyName, 14, 15);
    
    // Company details
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    
    // Build company contact info
    const companyContact = [];
    if (companyData?.address) companyContact.push(companyData.address);
    if (companyData?.city && companyData?.state) companyContact.push(`${companyData.city}, ${companyData.state}`);
    if (companyData?.pincode) companyContact.push(`PIN: ${companyData.pincode}`);
    if (companyData?.contactNumber) companyContact.push(`Phone: ${companyData.contactNumber}`);
    if (companyData?.email) companyContact.push(`Email: ${companyData.email}`);
    if (companyData?.gstin) companyContact.push(`GSTIN: ${companyData.gstin}`);
    
    // Display company info in multiple lines if needed
    if (companyContact.length > 0) {
      const contactLine1 = companyContact.slice(0, 2).join(' | ');
      const contactLine2 = companyContact.slice(2, 4).join(' | ');
      doc.text(contactLine1, 14, 22);
      if (contactLine2) {
        doc.text(contactLine2, 14, 28);
      }
    } else {
      doc.text('www.acctoo.com | info@acctoo.com', 14, 22);
      doc.text('www.acctoo.com | info@acctoo.com', 14, 28);
    }
    
    // Reset text color for content
    doc.setTextColor(0, 0, 0);
    
    // Set title
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(title, 14, 45);
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    
    // Add date
    const currentDate = new Date().toLocaleDateString('en-IN');
    doc.text(`Generated on: ${currentDate}`, 14, 52);
    
    // Create a table with grid lines
    let y = 65; // Start table below letterhead
    const colWidth = 40;
    const rowHeight = 8;
    const maxCols = Math.floor((doc.internal.pageSize.width - 28) / colWidth);
    
    // Draw table header with lines
    doc.setFillColor(66, 139, 202);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    
    // Draw header background and borders
    headers.slice(0, maxCols).forEach((header, index) => {
      const x = 14 + (index * colWidth);
      // Header background
      doc.rect(x, y - 5, colWidth, rowHeight, 'F');
      // Header text
      doc.text(header, x + 2, y);
      // Vertical lines for header
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.5);
      doc.line(x, y - 5, x, y + rowHeight - 5);
    });
    
    // Bottom line of header
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.5);
    doc.line(14, y + rowHeight - 5, 14 + (maxCols * colWidth), y + rowHeight - 5);
    
    y += rowHeight + 2;
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'normal');
    
    // Draw data rows with grid lines
    tableData.forEach((row, rowIndex) => {
      // Check if we need a new page
      if (y > doc.internal.pageSize.height - 20) {
        doc.addPage();
        y = 25;
      }
      
      // Draw row data and vertical lines
      row.slice(0, maxCols).forEach((cell, colIndex) => {
        const x = 14 + (colIndex * colWidth);
        // Cell text
        const displayText = cell.length > 15 ? cell.substring(0, 12) + '...' : cell;
        doc.text(displayText, x + 2, y);
        // Vertical lines for data cells
        doc.setDrawColor(100, 100, 100);
        doc.setLineWidth(0.2);
        doc.line(x, y - 5, x, y + rowHeight - 5);
      });
      
      // Horizontal line after each row
      doc.setDrawColor(100, 100, 100);
      doc.setLineWidth(0.2);
      doc.line(14, y + rowHeight - 5, 14 + (maxCols * colWidth), y + rowHeight - 5);
      
      y += rowHeight;
      
      // Add some spacing every 5 rows
      if ((rowIndex + 1) % 5 === 0) {
        y += 2;
      }
    });
    
    // Add footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
      const footerText = companyName + ' - Simplifying Accounting, Together';
      doc.text(footerText, 14, doc.internal.pageSize.height - 10);
    }
    
    doc.save(`${filename}.pdf`);
  } catch (error) {
    console.error('PDF generation error:', error);
    // Note: This function is outside the component, so we can't use the notification hook here
    // The error will be handled by the calling code
    throw error;
  }
};

const Payments = ({ db, userId, isAuthReady, appId }) => {
  const { currentCompany, employees } = useEnhancedCompany();
  const companyId = currentCompany?.companyId || currentCompany?.id || null;
  const companyCtx = useMemo(() => ({ companyId, appId, userId }), [companyId, appId, userId]);
  const { currency: companyCurrency } = useCompanyCurrency();
  const [workOrderLink, setWorkOrderLink] = useState({ workOrderId: '', phaseId: '', costCategory: '' });
  const { t } = useLanguage();
  const { paymentSuccess, success, error, warning, info } = useNotification();
  const { handleError, handleValidationError, showWarning, showInfo } = useErrorHandler();
  

  // ESC Key Functionality: Press ESC key to close modals in LIFO order (Last In, First Out)
  // Order: Receipt Modal → Payment Details Modal → Payment Modal → Advance Modal
  // URL parameter handling for tab selection
  const location = useLocation();
  
  // Main state
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
      const tabParam = params.get('tab');
      const allowed = ['invoice','challan','purchase','receipts','payments','advances','mode'];
      if (tabParam && allowed.includes(tabParam)) return tabParam;
    } catch {}
    return 'invoice';
  });
  // keep tab in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
    if (params.get('tab') !== activeTab) {
      params.set('tab', activeTab);
      const base = window.location.hash.split('?')[0] || '#/payments';
      const next = `${base}?${params.toString()}`;
      if (window.location.hash !== next) window.location.hash = next;
    }
  }, [activeTab]);

  useEffect(() => {
    setShowTabTotals(false);
  }, [activeTab]);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
      const woId = params.get('workOrderId') || '';
      const phaseId = params.get('phaseId') || '';
      if (woId) {
        setWorkOrderLink((prev) => ({ ...prev, workOrderId: woId, phaseId }));
        if (params.get('tab') !== 'payments') setActiveTab('payments');
      }
    } catch (_) { /* ignore */ }
  }, []);

  // Respond to hash changes
  useEffect(() => {
    const allowed = ['invoice','challan','purchase','receipts','payments','advances','mode'];
    const handler = () => {
      try {
        const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
        const t = params.get('tab');
        if (t && allowed.includes(t) && t !== activeTab) setActiveTab(t);
      } catch {}
    };
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, [activeTab]);
  const [receiptsSortBy, setReceiptsSortBy] = useState('receiptNumber');
  const [receiptsSortOrder, setReceiptsSortOrder] = useState('asc'); // 'invoice', 'challan', 'purchase'
  const [receiptsSubTab, setReceiptsSubTab] = useState('invoice'); // 'invoice', 'challan', 'purchase' for receipts tab
  // Use the new payments filter hook
  const paymentsFilter = usePaymentsFilter();
  
  // State to force re-render when filters change
  const [filterUpdateTrigger, setFilterUpdateTrigger] = useState(0);

  // Data state
  const [parties, setParties] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [challans, setChallans] = useState([]);
  const [purchaseBills, setPurchaseBills] = useState([]);
                                                                          const [payments, setPayments] = useState([]);
  const [company, setCompany] = useState(null);

  // UI state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isSavingPayment, setIsSavingPayment] = useState(false);
  const isSavingPaymentRef = useRef(false);
  const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [selectedBill, setSelectedBill] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [paymentType, setPaymentType] = useState('bill'); // 'bill' or 'khata'
  const [exportFormat, setExportFormat] = useState('csv'); // 'csv', 'excel', 'pdf'
  
  // Employee cash flow integration state
  const [recordInEmployeeCashFlow, setRecordInEmployeeCashFlow] = useState(false);
  const [selectedEmployeeForCashFlow, setSelectedEmployeeForCashFlow] = useState('');
  
  // Mobile card expansion states
  const [expandedBills, setExpandedBills] = useState(new Set());
  const [expandedReceipts, setExpandedReceipts] = useState(new Set());
  const [expandedParties, setExpandedParties] = useState(new Set());
  
  // Helper functions for mobile card expansion
  const toggleBillExpansion = (billId) => {
    setExpandedBills(prev => {
      const newSet = new Set(prev);
      if (newSet.has(billId)) {
        newSet.delete(billId);
      } else {
        newSet.add(billId);
      }
      return newSet;
    });
  };
  
  const toggleReceiptExpansion = (receiptId) => {
    setExpandedReceipts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(receiptId)) {
        newSet.delete(receiptId);
      } else {
        newSet.add(receiptId);
      }
      return newSet;
    });
  };
  
  const togglePartyExpansion = (partyId) => {
    setExpandedParties(prev => {
      const newSet = new Set(prev);
      if (newSet.has(partyId)) {
        newSet.delete(partyId);
      } else {
        newSet.add(partyId);
      }
      return newSet;
    });
  };

  // Table sorting hook for new tables with default sort by party name
  const { sortConfig: newSortConfig, handleSort: handleNewSort, getSortedData } = useTableSort([], { key: 'partyName', direction: 'asc' });

  // Table sorting hook for bills table with LIFO default
  const { sortConfig: billsSortConfig, handleSort: handleBillsSort, getSortedData: getBillsSortedData } = useTableSort([], { key: 'number', direction: 'desc' });

  // Payment entry state
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [selectedPartyForPayment, setSelectedPartyForPayment] = useState('');
  const [advanceModalParty, setAdvanceModalParty] = useState(null);
  const [showAdvanceInfo, setShowAdvanceInfo] = useState(false);
  const [advanceInfo, setAdvanceInfo] = useState({ collected: 0, used: 0 });

  useCompanySwitchGuard({
    isDirty: () =>
      !!showPaymentModal ||
      !!selectedPartyForPayment ||
      !!paymentAmount ||
      !!selectedBill,
    onSwitch: () => {
      setShowPaymentModal(false);
      setSelectedPartyForPayment('');
      setPaymentAmount('');
      setSelectedBill(null);
      setReceiptNumber('');
      setPaymentNotes('');
      setPaymentReference('');
    },
    notify: (message) => warning(message, { title: 'Company switched' }),
  });

  const paymentModes = [
    'Cash', 'Cheque', 'Bank Transfer', 'UPI', 'Credit Card', 'Debit Card', 
    'Online Payment', 'Demand Draft', 'NEFT', 'RTGS', 'IMPS'
  ];

  // Expose a simple Payment Mode analytics tab for Balance Sheet
  const [modeSummary, setModeSummary] = useState([]);
  const [showTabTotals, setShowTabTotals] = useState(false);
  const [showModeModal, setShowModeModal] = useState(false);
  const [modeModalTitle, setModeModalTitle] = useState('');
  const [modeModalRowsSales, setModeModalRowsSales] = useState([]);
  const [modeModalRowsPurchase, setModeModalRowsPurchase] = useState([]);
  const [modeModalRowsExpense, setModeModalRowsExpense] = useState([]);
  const [modeModalRowsTax, setModeModalRowsTax] = useState([]);
  const [modeModalType, setModeModalType] = useState('sales'); // 'sales' | 'purchase' | 'expense' | 'tax'

  // Export/Print helpers for Payment Mode tables
  const buildModeRows = () => {
    const rows = modeSummary.map(m => ({ mode: m.mode, receipts: m.salesTotal || 0, payments: m.purchasePaidTotal || 0, expenses: m.expenseTotal || 0 }));
    return rows;
  };

  // New unified export data function
  const getPaymentModeData = () => {
    const rows = buildModeRows();

    // If no real data, provide sample data for testing
    if (!rows || rows.length === 0) {
      return [
        { mode: 'Cash', count: 25, amount: 15000, received: 25000, paid: 8000, expenses: 2000 },
        { mode: 'UPI', count: 15, amount: 8500, received: 12000, paid: 3000, expenses: 500 },
        { mode: 'Bank Transfer', count: 8, amount: 12000, received: 15000, paid: 2500, expenses: 500 },
        { mode: 'Cheque', count: 5, amount: 3000, received: 5000, paid: 1500, expenses: 500 }
      ];
    }

    return rows.map(row => ({
      mode: row.mode,
      count: (row.receipts + row.payments + row.expenses), // Total transactions
      amount: row.receipts - row.payments - row.expenses, // Net balance
      received: row.receipts,
      paid: row.payments,
      expenses: row.expenses
    }));
  };
  const paymentModeColumns = [
    { key: 'mode', label: 'Mode' },
    { key: 'receipts', label: 'Received' },
    { key: 'payments', label: 'Paid' },
    { key: 'expenses', label: 'Expense' }
  ];
  const handleExportPaymentMode = async (type) => {
    const companyDetails = currentCompany || company || {};
    const reportDetails = {};
    const data = buildModeRows();
    const filename = `Payment-Mode-${new Date().toISOString().slice(0,10)}`;
    if (type === 'pdf') {
      // Multi-table PDF with letterhead
      const tables = [
        { tableTitle: '1) Payment Received (Sales/PRI)', columns: [{key:'mode',label:'Mode'},{key:'value',label:'Total'}], arr: data.map(r=>({mode:r.mode,value:r.receipts})) },
        { tableTitle: '2) Payment Paid (Purchase/PRP)', columns: [{key:'mode',label:'Mode'},{key:'value',label:'Total'}], arr: data.map(r=>({mode:r.mode,value:r.payments})) },
        { tableTitle: '3) Expense', columns: [{key:'mode',label:'Mode'},{key:'value',label:'Total'}], arr: data.map(r=>({mode:r.mode,value:r.expenses})) },
        { tableTitle: '4) Balance', columns: [{key:'mode',label:'Mode'},{key:'value',label:'Balance'}], arr: (()=>{ const cash = data.find(r=>r.mode==='Cash')||{receipts:0,payments:0,expenses:0}; const bank = data.filter(r=>r.mode!=='Cash').reduce((s,r)=>s+((r.receipts||0)-(r.payments||0)-(r.expenses||0)),0); return [{mode:'Cash',value: (cash.receipts - cash.payments - cash.expenses)}, {mode:'Bank (UPI+Bank+Cheque/DD)', value: bank}]; })() }
      ];
      await multiTablePDF(tables, `${filename}.pdf`, 'Payment Mode', companyDetails);
    } else if (type === 'excel') {
      // Workbook with four sheets
      const wb = XLSX.utils.book_new();
      const makeSheet = (title, rows, cols) => {
        const wsData = [cols.map(c=>c.label), ...rows.map(r=>cols.map(c=> r[c.key] ?? ''))];
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        XLSX.utils.book_append_sheet(wb, ws, title.slice(0,31));
      };
      makeSheet('1-Received', data.map(r=>({mode:r.mode,value:r.receipts})), [{key:'mode',label:'Mode'},{key:'value',label:'Total'}]);
      makeSheet('2-Paid', data.map(r=>({mode:r.mode,value:r.payments})), [{key:'mode',label:'Mode'},{key:'value',label:'Total'}]);
      makeSheet('3-Expense', data.map(r=>({mode:r.mode,value:r.expenses})), [{key:'mode',label:'Mode'},{key:'value',label:'Total'}]);
      const cash = data.find(r=>r.mode==='Cash')||{receipts:0,payments:0,expenses:0};
      const bank = data.filter(r=>r.mode!=='Cash').reduce((s,r)=>s+((r.receipts||0)-(r.payments||0)-(r.expenses||0)),0);
      makeSheet('4-Balance', [{mode:'Cash',value:(cash.receipts - cash.payments - cash.expenses)},{mode:'Bank Combined',value:bank}], [{key:'mode',label:'Mode'},{key:'value',label:'Balance'}]);
      XLSX.writeFile(wb, `${filename}.xlsx`);
    } else if (type === 'print') {
      // Print with letterhead-like header; only the four tables in compact form
      const w = window.open('', 'PRINT', 'height=700,width=900');
      const headerHtml = `
        <div style="text-align:center;margin-bottom:8px;border-bottom:2px solid #333;padding-bottom:8px;">
          <div style="font-size:20px;font-weight:bold;">${companyDetails?.firmName||'Company'}</div>
          <div style="font-size:12px;color:#555;">${companyDetails?.address||''}</div>
          <div style="font-size:12px;color:#555;">${companyDetails?.gstin?('GSTIN: '+companyDetails.gstin):''}</div>
        </div>`;
      const renderTable = (title, rows, cols) => {
        const head = `<tr>${cols.map(c=>`<th style=\"border:1px solid #ddd;padding:6px;text-align:${c.key==='mode'?'left':'right'};\">${c.label}</th>`).join('')}</tr>`;
        const body = rows.map(r=>`<tr>${cols.map(c=>`<td style=\"border:1px solid #ddd;padding:6px;text-align:${c.key==='mode'?'left':'right'};\">${(r[c.key]||0).toLocaleString? r[c.key].toLocaleString('en-IN') : (r[c.key]||'')}</td>`).join('')}</tr>`).join('');
        return `<div style=\"margin:10px 0\"><div style=\"font-weight:600;margin:6px 0\">${title}</div><table style=\"width:100%;border-collapse:collapse\"><thead>${head}</thead><tbody>${body}</tbody></table></div>`;
      };
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Payment Mode</title></head><body>${headerHtml}${renderTable('1) Payment Received (Sales/PRI)', data.map(r=>({mode:r.mode,value:r.receipts})), [{key:'mode',label:'Mode'},{key:'value',label:'Total'}])}${renderTable('2) Payment Paid (Purchase/PRP)', data.map(r=>({mode:r.mode,value:r.payments})), [{key:'mode',label:'Mode'},{key:'value',label:'Total'}])}${renderTable('3) Expense', data.map(r=>({mode:r.mode,value:r.expenses})), [{key:'mode',label:'Mode'},{key:'value',label:'Total'}])}${(()=>{ const cash = data.find(r=>r.mode==='Cash')||{receipts:0,payments:0,expenses:0}; const bank = data.filter(r=>r.mode!=='Cash').reduce((s,r)=>s+((r.receipts||0)-(r.payments||0)-(r.expenses||0)),0); return renderTable('4) Balance', [{mode:'Cash',value:(cash.receipts - cash.payments - cash.expenses)},{mode:'Bank Combined',value:bank}], [{key:'mode',label:'Mode'},{key:'value',label:'Balance'}]); })()}</body></html>`;
      w.document.write(html); w.document.close(); w.focus(); w.print(); w.close();
    }
  };

  // Share helpers (match GlobalExportButtons behavior)
  const shareBlobViaSystem = async (blob, suggestedName, mime) => {
    try {
      const file = new File([blob], suggestedName, { type: mime });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: suggestedName, text: 'Payments Report' });
        return true;
      }
    } catch (_) {}
    return false;
  };

  const sharePaymentsAsPDF = async (tableType) => {
    try {
      const { jsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');
      const doc = new jsPDF();
      const makeTable = (arr, columns, title) => {
        doc.addPage();
        
        // Add company letterhead
        doc.setFontSize(20);
        doc.setFont(undefined, 'bold');
        doc.text(company?.firmName || 'Company Name', 105, 20, { align: 'center' });
        
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        let yPosition = 30;
        
        // Company details
        if (company?.address) {
          doc.text(company.address, 105, yPosition, { align: 'center' });
          yPosition += 5;
        }
        if (company?.gstin) {
          doc.text(`GSTIN: ${company.gstin}`, 105, yPosition, { align: 'center' });
          yPosition += 5;
        }
        if (company?.contactNumber) {
          doc.text(`Phone: ${company.contactNumber}`, 105, yPosition, { align: 'center' });
          yPosition += 5;
        }
        
        // Add separator
        yPosition += 3;
        doc.setDrawColor(200, 200, 200);
        doc.line(20, yPosition, 190, yPosition);
        yPosition += 8;
        
        // Report title
        doc.setFontSize(18);
        doc.setFont(undefined, 'bold');
        doc.text(title || 'Payments Report', 105, yPosition, { align: 'center' });
        yPosition += 15;
        
        // Report details
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text(`Generated On: ${new Date().toLocaleDateString('en-IN')}`, 20, yPosition);
        yPosition += 8;
        doc.text(`Report Type: Payments Report`, 20, yPosition);
        yPosition += 8;
        doc.text(`Tab: ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}`, 20, yPosition);
        yPosition += 15;
        
        autoTable(doc, { 
          head: [columns.map(c => c.label)], 
          body: arr.map(r => columns.map(c => r[c.key] ?? '')), 
          startY: yPosition, 
          styles: { fontSize: 8 },
          headStyles: { fillColor: [66, 139, 202] }
        });
        
        // Add footer
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(9);
          doc.setTextColor(120);
          doc.text('Use www.acctoo.com, Simplifying Accounting, Together', 105, 285, { align: 'center' });
        }
      };
      doc.deletePage(1);
      const addSection = async (type) => {
        if (type === 'bills') {
          const bills = getFilteredBills();
          const rows = bills.map(bill => {
            const party = parties.find(p => p.id === (bill.partyId || bill.party));
            const openingBalance = party?.openingBalance || 0;
            return { billNumber: getBillNumber(bill), date: convertTimestamp(bill[getBillDateField()]), partyName: getPartyName(bill.partyId || bill.party), openingBalance: openingBalance, totalAmount: bill.totalAmount || 0, totalPaid: bill.totalPaid || 0, outstanding: bill.outstanding || 0, paymentCount: bill.paymentCount || 0 };
          });
          const totals = getBillsTotals();
          rows.push({ billNumber: `TOTAL (${totals.totalBills} bills)`, date: '', partyName: '', openingBalance: '', totalAmount: totals.totalAmount, totalPaid: totals.totalPaid, outstanding: totals.totalOutstanding, paymentCount: '' });
          makeTable(rows, [
            { key: 'billNumber', label: `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Number` },
            { key: 'date', label: 'Date' },
            { key: 'partyName', label: 'Party Name' },
            { key: 'openingBalance', label: 'Opening Balance' },
            { key: 'totalAmount', label: 'Total Amount' },
            { key: 'totalPaid', label: 'Total Paid' },
            { key: 'outstanding', label: 'Outstanding' },
            { key: 'paymentCount', label: 'Payment Count' }
          ], `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Bills`);
        } else if (type === 'party-summary') {
          const summary = getPartyWiseSummary();
          const rows = summary.map(p => ({ partyName: p.partyName, openingBalance: p.openingBalance || 0, totalBills: p.totalBills, totalAmount: p.totalAmount, totalPaid: p.totalPaid, outstanding: p.outstanding }));
          const totals = getPartyWiseTotals();
          rows.push({ partyName: `TOTAL (${totals.totalParties} parties)`, openingBalance: totals.totalOpeningBalance, totalBills: totals.totalBills, totalAmount: totals.totalAmount, totalPaid: totals.totalPaid, outstanding: totals.totalOutstanding });
          makeTable(rows, [
            { key: 'partyName', label: 'Party Name' },
            { key: 'openingBalance', label: 'Opening Balance' },
            { key: 'totalBills', label: 'Total Bills' },
            { key: 'totalAmount', label: 'Total Amount' },
            { key: 'totalPaid', label: 'Total Paid' },
            { key: 'outstanding', label: 'Outstanding' }
          ], 'Party-wise Summary');
        } else if (type === 'receipts') {
          const filteredReceipts = payments.filter(payment => (activeTab === 'receipts' ? payment.type === receiptsSubTab : payment.type === activeTab));
          const rows = filteredReceipts.map(payment => ({ receiptNumber: payment.receiptNumber, date: convertTimestamp(payment.paymentDate), partyName: payment.partyName, amount: payment.totalAmount || 0, paymentMode: payment.paymentMode, paymentType: payment.paymentType === 'bill' ? 'Bill Payment' : 'Khata Payment', reference: payment.reference || '-', notes: payment.notes || '-' }));
          const totals = getReceiptsTotals();
          rows.push({ receiptNumber: `TOTAL (${totals.totalReceipts} receipts)`, date: '', partyName: '', amount: totals.totalAmount, paymentMode: '', paymentType: '', reference: '', notes: '' });
          makeTable(rows, [
            { key: 'receiptNumber', label: 'Receipt Number' },
            { key: 'date', label: 'Date' },
            { key: 'partyName', label: 'Party Name' },
            { key: 'amount', label: 'Amount' },
            { key: 'paymentMode', label: 'Payment Mode' },
            { key: 'paymentType', label: 'Payment Type' },
            { key: 'reference', label: 'Reference' },
            { key: 'notes', label: 'Notes' }
          ], `${activeTab === 'receipts' ? receiptsSubTab.charAt(0).toUpperCase() + receiptsSubTab.slice(1) : activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Receipts`);
        }
      };
      if (tableType === 'all') {
        await addSection('bills');
        await addSection('party-summary');
        await addSection('receipts');
      } else {
        await addSection(tableType);
      }
      const blob = doc.output('blob');
      const ok = await shareBlobViaSystem(blob, `payments-${tableType}.pdf`, 'application/pdf');
      if (!ok) { doc.save(`payments-${tableType}.pdf`); }
    } catch (e) {
      console.error('Payments share PDF failed:', e);
      await handleExport(tableType);
    }
  };

  const sharePaymentsAsExcel = async (tableType) => {
    try {
      let headers = [];
      let rows = [];
      if (tableType === 'bills') {
        headers = ['Bill Number','Date','Party Name','Opening Balance','Total Amount','Total Paid','Outstanding','Payment Count'];
        const bills = getFilteredBills();
        rows = bills.map(bill => {
          const party = parties.find(p => p.id === (bill.partyId || bill.party));
          const openingBalance = party?.openingBalance || 0;
          return [getBillNumber(bill), convertTimestamp(bill[getBillDateField()]), getPartyName(bill.partyId || bill.party), openingBalance, bill.totalAmount || 0, bill.totalPaid || 0, bill.outstanding || 0, bill.paymentCount || 0];
        });
      } else if (tableType === 'party-summary') {
        headers = ['Party Name','Opening Balance','Total Bills','Total Amount','Total Paid','Outstanding'];
        const summary = getPartyWiseSummary();
        rows = summary.map(p => [p.partyName, p.openingBalance || 0, p.totalBills, p.totalAmount, p.totalPaid, p.outstanding]);
      } else if (tableType === 'receipts') {
        headers = ['Receipt Number','Date','Party Name','Amount','Payment Mode','Payment Type','Reference','Notes'];
        const filteredReceipts = paymentsFilter.filterPayments(payments.filter(payment => (activeTab === 'receipts' ? payment.type === receiptsSubTab : payment.type === activeTab)));
        rows = filteredReceipts.map(p => [p.receiptNumber, convertTimestamp(p.paymentDate), p.partyName, p.totalAmount || 0, p.paymentMode, (p.paymentType === 'bill' ? 'Bill Payment' : 'Khata Payment'), p.reference || '-', p.notes || '-']);
      } else { return; }
      
      // Add company branding to CSV
      let csv = '';
      
      // Company details
      if (company?.firmName) {
        csv += `${company.firmName}\n`;
      }
      if (company?.address) {
        csv += `${company.address}\n`;
      }
      if (company?.gstin) {
        csv += `GSTIN: ${company.gstin}\n`;
      }
      if (company?.contactNumber) {
        csv += `Phone: ${company.contactNumber}\n`;
      }
      csv += '\n';
      
      // Report details
      csv += `Generated On: ${new Date().toLocaleDateString('en-IN')}\n`;
      csv += `Report Type: Payments Report\n`;
      csv += `Tab: ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}\n`;
      csv += '\n';
      
      // Table data
      csv += headers.join(',') + '\n';
      rows.forEach(r => { csv += r.map(v => (typeof v === 'number' ? v : `"${String(v).replace(/"/g,'""')}"`)).join(',') + '\n'; });
      
      // Footer
      csv += '\n';
      csv += 'Use www.acctoo.com, Simplifying Accounting, Together\n';
      
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const ok = await shareBlobViaSystem(blob, `payments-${tableType}.csv`, 'text/csv');
      if (!ok) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `payments-${tableType}.csv`; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      }
    } catch (e) { console.error('Payments share CSV failed:', e); }
  };

  const sharePaymentsAsImage = async (containerId, suggestedName = 'payments.png') => {
    try {
      const node = document.getElementById(containerId);
      if (!node) {
        console.error('Container not found:', containerId);
        return;
      }
      const html2canvas = await import('html2canvas');
      const canvas = await html2canvas.default(node, { scale: 2, useCORS: true, allowTaint: false, backgroundColor: '#ffffff' });
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      const ok = await shareBlobViaSystem(blob, suggestedName, 'image/png');
      if (!ok) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = suggestedName; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      }
    } catch (e) { console.error('Payments share image failed:', e); }
  };

  const sharePaymentsLink = async (htmlBuilder, filenamePrefix = 'payments') => {
    try {
      const html = htmlBuilder();
      const blob = new Blob([html], { type: 'text/html' });
      const storage = getStorage(app);
      const token = Math.random().toString(36).slice(2);
      const path = `publicReports/${filenamePrefix}-${token}.html`;
      const sref = storageRef(storage, path);
      await uploadBytes(sref, blob, { contentType: 'text/html' });
      const url = await getDownloadURL(sref);
      const wrapped = `https://acctoo.com/doc-viewer.html?u=${encodeURIComponent(url)}`;
      if (navigator.share) {
        await navigator.share({ title: filenamePrefix, text: filenamePrefix, url: wrapped });
      } else {
        await navigator.clipboard.writeText(wrapped);
        success('Public link copied to clipboard', { title: 'Link Copied' });
      }
    } catch (e) { console.error('Payments share link failed:', e); }
  };

  useEffect(() => {
    if (activeTab !== 'mode') return;
    const effectiveAppId = appId;
    // Using getCollectionForCompany instead of legacy basePath
    const load = async () => {
      try {
        // Get date range from new filter system
        const dateRange = paymentsFilter.getDateRange();

        // Fetch all payments, then filter JS-side by date/party
        const snap = await getDocs(getCollectionForCompany(db, { companyId, appId, userId }, 'payments'));
        let rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        rows = rows.filter(r => {
          // Party filter (if available on record)
          if (paymentsFilter.selectedParty && (r.partyId || r.party)) {
            const pid = r.partyId || r.party;
            if (pid !== paymentsFilter.selectedParty) return false;
          }
          // Date filter
          if (dateRange) {
            const ds = r.paymentDate || r.date;
            if (!ds) return false;
            const dObj = new Date(ds);
            return dObj >= dateRange.start && dObj <= dateRange.end;
          }
          return true;
        });
        const normalizeMode = (raw) => {
          const m = String(raw || '').toLowerCase();
          if (m.includes('cash')) return 'Cash';
          if (m.includes('upi')) return 'UPI';
          if (m.includes('cheque') || m.includes('dd') || m.includes('demand draft')) return 'Cheque/DD';
          return 'Bank Transfer';
        };
        const map = new Map();
        rows.forEach(r => {
          const group = normalizeMode(r.paymentMode);
          const amt = Number(r.totalAmount || r.amount || 0);
          if (!map.has(group)) map.set(group, { mode: group, salesTotal: 0, salesCount: 0, receiptRowsSales: [], purchasePaidTotal: 0, purchasePaidCount: 0, receiptRowsPurchase: [], expenseTotal: 0, expenseCount: 0, expenseRows: [], taxPaymentsTotal: 0, taxPaymentsCount: 0, taxPaymentsRows: [] });
          const m = map.get(group);
          const recNo = (r.receiptNumber || r.number || '').toString().toUpperCase();
          const isSalesReceipt = recNo.startsWith('PRI');
          const isPurchasePayment = recNo.startsWith('PRP') || recNo.startsWith('PRB');
          if (isSalesReceipt) {
            m.salesTotal += amt; m.salesCount += 1;
            m.receiptRowsSales.push({ id: r.id, date: r.paymentDate || r.date, receiptNumber: r.receiptNumber || r.number, partyName: r.partyName || r.partyFirmName || '', amount: amt });
          }
          if (isPurchasePayment) {
            m.purchasePaidTotal += amt; m.purchasePaidCount += 1;
            m.receiptRowsPurchase.push({ id: r.id, date: r.paymentDate || r.date, receiptNumber: r.receiptNumber || r.number, partyName: r.partyName || r.partyFirmName || '', amount: amt });
          }
        });
        // Fetch expenses and merge by paymentMode
        const expSnap = await getDocs(getCollectionForCompany(db, { companyId, appId, userId }, 'expenses'));
        expSnap.docs.forEach(d => {
          const e = d.data() || {};
          const group = normalizeMode(e.paymentMode || 'Bank Transfer');
          const amt = Number(e.amount || 0);
          if (dateRange) {
            const ds = e.date;
            if (!ds) return; // skip if no date
            const dObj = new Date(ds);
            if (!(dObj >= dateRange.start && dObj <= dateRange.end)) return;
          }
          if (!map.has(group)) map.set(group, { mode: group, salesTotal: 0, salesCount: 0, receiptRowsSales: [], purchasePaidTotal: 0, purchasePaidCount: 0, receiptRowsPurchase: [], expenseTotal: 0, expenseCount: 0, expenseRows: [], taxPaymentsTotal: 0, taxPaymentsCount: 0, taxPaymentsRows: [] });
          const m = map.get(group);
          m.expenseTotal += amt; m.expenseCount += 1;
          m.expenseRows.push({ id: d.id, date: e.date, receiptNumber: e.head || e.description || '', partyName: e.head || '', amount: amt });
        });

        // Include salary payments as expenses (fallback paymentMode Bank Transfer)
        const salarySnap = await getDocs(getCollectionForCompany(db, { companyId, appId, userId }, 'salaryPayments'));
        salarySnap.docs.forEach(sd => {
          const s = sd.data() || {};
          const ds = s.date || s.paymentDate || s.createdAt;
          if (dateRange) {
            if (!ds) return; const dObj = new Date(ds); if (!(dObj >= dateRange.start && dObj <= dateRange.end)) return;
          }
          const group = normalizeMode(s.paymentMode || 'Bank Transfer');
          if (!map.has(group)) map.set(group, { mode: group, salesTotal: 0, salesCount: 0, receiptRowsSales: [], purchasePaidTotal: 0, purchasePaidCount: 0, receiptRowsPurchase: [], expenseTotal: 0, expenseCount: 0, expenseRows: [], taxPaymentsTotal: 0, taxPaymentsCount: 0, taxPaymentsRows: [] });
          const m = map.get(group);
          const amt = Number(s.netAmount || s.totalEarnings || s.total || 0);
          m.expenseTotal += amt; m.expenseCount += 1;
          m.expenseRows.push({ id: sd.id, date: s.date || '', receiptNumber: s.remarks || s.month || 'Salary', partyName: s.employeeName || '', amount: amt });
        });

        // Tax payments dedicated collection
        try {
          const taxSnap = await getDocs(getCollectionForCompany(db, { companyId, appId, userId }, 'taxPayments'));
          taxSnap.docs.forEach(td => {
            const t = td.data() || {};
            const group = normalizeMode(t.paymentMode || 'Bank Transfer');
            const amt = Number(t.amount || 0);
            if (dateRange) {
              const ds = t.paymentDate;
              if (!ds) return; const dObj = new Date(ds); if (!(dObj >= dateRange.start && dObj <= dateRange.end)) return;
            }
            if (!map.has(group)) map.set(group, { mode: group, salesTotal: 0, salesCount: 0, receiptRowsSales: [], purchasePaidTotal: 0, purchasePaidCount: 0, receiptRowsPurchase: [], expenseTotal: 0, expenseCount: 0, expenseRows: [], taxPaymentsTotal: 0, taxPaymentsCount: 0, taxPaymentsRows: [] });
            const m = map.get(group);
            m.taxPaymentsTotal += amt; m.taxPaymentsCount += 1;
            m.taxPaymentsRows.push({ id: td.id, date: t.paymentDate || '', receiptNumber: `${t.taxType||'TAX'} ${t.periodValue||''} ${t.reference||''}`.trim(), partyName: 'Tax', amount: amt });
            // also include into expense for balance
            m.expenseTotal += amt; m.expenseCount += 1;
          });
        } catch {}

        const summaryRows = Array.from(map.values()).sort((a,b)=>a.mode.localeCompare(b.mode));
        setModeSummary(summaryRows);
        // Persist for Balance Sheet to consume (totals only)
        try {
          const period = dateRange ? { start: dateRange.start.toISOString().slice(0,10), end: dateRange.end.toISOString().slice(0,10) } : null;
          localStorage.setItem(`mode-summary:${appId}:${userId}`, JSON.stringify({ period, rows: summaryRows.map(({mode,salesTotal,purchasePaidTotal,expenseTotal})=>({mode,receipts:salesTotal,payments:purchasePaidTotal,expenses:expenseTotal})) }));
        } catch {}
      } catch {}
    };
    load();
  }, [activeTab, db, appId, userId, paymentsFilter]);



  // Helper function to get financial year date range
  const getFinancialYearRange = (financialYear) => {
    const [startYear] = financialYear.split('-');
    // Handle both formats: "2025-26" and "25-26"
    const yearNum = startYear.length === 4 ? parseInt(startYear) : parseInt('20' + startYear);
    const startDate = new Date(yearNum, 3, 1); // April 1st (month 3 = April)
    const endDate = new Date(yearNum + 1, 2, 31); // March 31st (month 2 = March)
    return { startDate, endDate };
  };

  // Helper function to get month date range
  const getMonthRange = (monthYear) => {
    const [month, year] = monthYear.split('-');
    const yearNum = parseInt('20' + year); // Convert 25 to 2025
    const startDate = new Date(yearNum, parseInt(month) - 1, 1);
    const endDate = new Date(yearNum, parseInt(month), 0);
    return { startDate, endDate };
  };

  // Helper function to get quarter date range
  const getQuarterRange = (quarterYear) => {
    const [quarter, year] = quarterYear.split('-');
    const yearNum = parseInt('20' + year); // Convert 25 to 2025
    let startMonth, endMonth;
    
    switch(quarter) {
      case 'Q1': // April to June
        startMonth = 3; // April (0-indexed)
        endMonth = 5; // June (0-indexed)
        break;
      case 'Q2': // July to September
        startMonth = 6; // July (0-indexed)
        endMonth = 8; // September (0-indexed)
        break;
      case 'Q3': // October to December
        startMonth = 9; // October (0-indexed)
        endMonth = 11; // December (0-indexed)
        break;
      case 'Q4': // January to March
        startMonth = 0; // January (0-indexed)
        endMonth = 2; // March (0-indexed)
        break;
      default:
        startMonth = 0;
        endMonth = 11;
    }
    
    const startDate = new Date(yearNum, startMonth, 1);
    const endDate = new Date(yearNum, endMonth + 1, 0);
    return { startDate, endDate };
  };

  // Helper function to get available financial years from bills data
  const getAvailableFinancialYears = () => {
    const years = new Set();
    
    // Collect dates from invoices
    invoices.forEach(bill => {
      const billDate = bill.invoiceDate || bill.billDate || bill.date;
      if (billDate) {
        const year = parseInt(billDate.slice(0, 4));
        if (!isNaN(year)) {
          const month = parseInt(billDate.slice(5, 7));
          // Financial year logic: April-March
          const fy = month >= 4 ? `${year}-${(year + 1).toString().slice(-2)}` : `${year - 1}-${year.toString().slice(-2)}`;
          years.add(fy);
        }
      }
    });
    
    // Collect dates from challans
    challans.forEach(bill => {
      const billDate = bill.invoiceDate || bill.billDate || bill.date;
      if (billDate) {
        const year = parseInt(billDate.slice(0, 4));
        if (!isNaN(year)) {
          const month = parseInt(billDate.slice(5, 7));
          const fy = month >= 4 ? `${year}-${(year + 1).toString().slice(-2)}` : `${year - 1}-${year.toString().slice(-2)}`;
          years.add(fy);
        }
      }
    });
    
    // Collect dates from purchase bills
    purchaseBills.forEach(bill => {
      const billDate = bill.invoiceDate || bill.billDate || bill.date;
      if (billDate) {
        const year = parseInt(billDate.slice(0, 4));
        if (!isNaN(year)) {
          const month = parseInt(billDate.slice(5, 7));
          const fy = month >= 4 ? `${year}-${(year + 1).toString().slice(-2)}` : `${year - 1}-${year.toString().slice(-2)}`;
          years.add(fy);
        }
      }
    });
    
    // Collect dates from payments (receipts)
    payments.forEach(payment => {
      const paymentDate = payment.date;
      if (paymentDate) {
        const year = parseInt(paymentDate.slice(0, 4));
        if (!isNaN(year)) {
          const month = parseInt(paymentDate.slice(5, 7));
          const fy = month >= 4 ? `${year}-${(year + 1).toString().slice(-2)}` : `${year - 1}-${year.toString().slice(-2)}`;
          years.add(fy);
        }
      }
    });
    
    return Array.from(years).sort();
  };

  // Helper function to get current financial year
  const getCurrentFinancialYear = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // 0-indexed to 1-indexed
    
    // Financial year logic: April-March
    const fy = currentMonth >= 4 ? `${currentYear}-${(currentYear + 1).toString().slice(-2)}` : `${currentYear - 1}-${currentYear.toString().slice(-2)}`;
    return fy;
  };

  // Helper function to get quarters for current financial year only
  const getCurrentFinancialYearQuarters = () => {
    const currentFY = getCurrentFinancialYear();
    const [startYear] = currentFY.split('-');
    const yearSuffix = startYear.slice(-2);
    
    return [
      { value: `Q1-${yearSuffix}`, label: `Q1 (Apr-Jun) ${startYear}` },
      { value: `Q2-${yearSuffix}`, label: `Q2 (Jul-Sep) ${startYear}` },
      { value: `Q3-${yearSuffix}`, label: `Q3 (Oct-Dec) ${startYear}` },
      { value: `Q4-${yearSuffix}`, label: `Q4 (Jan-Mar) ${parseInt(startYear) + 1}` }
    ];
  };

  // Generate sequential receipt number
  const generateReceiptNumber = async () => {
    const currentYear = new Date().getFullYear();
    const financialYear = `${currentYear.toString().slice(-2)}-${(currentYear + 1).toString().slice(-2)}`;
    const paymentType = activeTab === 'invoice' ? 'I' : activeTab === 'challan' ? 'C' : 'P';
    

    
    try {
      // Get ALL payments to find the highest sequence number for THIS specific payment type
      const paymentsColl = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');
      const paymentsQuery = query(paymentsColl);
      const paymentsSnapshot = await getDocs(paymentsQuery);
      
      let highestSequence = 0;
      
      // Check all payments for the highest sequence number for THIS payment type only
      paymentsSnapshot.forEach((doc) => {
        const payment = doc.data();
        if (payment.receiptNumber) {

          
          // Match pattern: PR[I|C|P]25-26/number - but only for THIS payment type
          const regexPattern = new RegExp(`PR${paymentType}${financialYear.replace('-', '\\-')}\\/(\\d+)`);
          const match = payment.receiptNumber.match(regexPattern);
          
          if (match) {
            const sequence = parseInt(match[1]);

            if (sequence > highestSequence) {
              highestSequence = sequence;

            }
          }
        }
      });
      
      const nextSequence = highestSequence + 1;
      const receiptNumber = `PR${paymentType}${financialYear}/${nextSequence.toString().padStart(3, '0')}`;
      
      return receiptNumber;
    } catch (error) {
      console.error('Error generating receipt number:', error);
      const fallbackNumber = `PR${paymentType}${financialYear}/001`;

      return fallbackNumber;
    }
  };

  // FIFO Payment Allocation Logic
  const allocatePaymentFIFO = (partyId, paymentAmount, billType) => {
    try {
      // Validate inputs
      if (!partyId || !paymentAmount || !billType) {
        console.error('Invalid inputs to allocatePaymentFIFO:', { partyId, paymentAmount, billType });
        return { allocations: [], remainingAmount: paymentAmount };
      }
      
      const bills = billType === 'invoice' ? invoices : billType === 'challan' ? challans : purchaseBills;
      
      // Validate bills array
      if (!Array.isArray(bills)) {
        console.error('Bills array is not valid:', bills);
        return { allocations: [], remainingAmount: paymentAmount };
      }
      
      const partyBills = bills.filter(bill => (bill.partyId === partyId || bill.party === partyId) && bill.outstanding > 0);
      
      // Sort by date (FIFO - oldest first)
      partyBills.sort((a, b) => new Date(a.date || a.invoiceDate || a.challanDate || a.billDate) - new Date(b.date || b.invoiceDate || b.challanDate || b.billDate));
      

      
      // Check if there are any advance payments that should be applied first
      const availableAdvance = getPartyAdvance(partyId);
      let remainingAmount = paymentAmount;
      const allocations = [];
      
      // If there's available advance, apply it first to outstanding bills
      if (availableAdvance > 0 && partyBills.length > 0) {

        
        // Use the new advance allocation utility
        const advanceResult = applyAdvanceToBillsUtil(partyId, partyBills, payments);
        
        if (advanceResult.totalApplied > 0) {
          
          // Add advance allocations to the result
          // Note: applyAdvanceToBillsUtil already updated bill.outstanding for each bill,
          // so we don't need to reduce it again here
          advanceResult.allocations.forEach(advanceAlloc => {
            const bill = partyBills.find(b => b.id === advanceAlloc.billId);
            if (bill) {
              allocations.push({
                billType: billType,
                billId: bill.id,
                billNumber: bill.number || bill.invoiceNumber || bill.challanNumber || bill.billNumber,
                allocatedAmount: advanceAlloc.amountApplied,
                billOutstanding: advanceAlloc.billOutstanding, // Original outstanding before advance
                isFullPayment: advanceAlloc.amountApplied >= advanceAlloc.billOutstanding,
                isAdvanceAllocation: true,
                advancePaymentId: advanceAlloc.advancePaymentId
              });
              
              // bill.outstanding was already updated by applyAdvanceToBillsUtil,
              // so no need to update it again here
            }
          });
          

        }
      }
      
      // Now allocate the current payment amount
      for (const bill of partyBills) {
        if (remainingAmount <= 0) break;
        
        // Get the original bill total amount (before any allocations)
        const originalBillAmount = bill.totalAmount || bill.amount || 0;
        
        // Check if this bill already has advance allocated
        const existingAdvanceAllocation = allocations.find(alloc => alloc.billId === bill.id && alloc.isAdvanceAllocation);
        
        // bill.outstanding was already reduced by applyAdvanceToBillsUtil (if advance was applied)
        // So we can use it directly without subtracting again
        const remainingBillOutstanding = bill.outstanding || 0;
        
        if (remainingBillOutstanding <= 0) {
          continue; // Bill already fully paid by advance
        }
        
        const allocatedAmount = Math.min(remainingAmount, remainingBillOutstanding);
        
        if (allocatedAmount > 0) {
          if (existingAdvanceAllocation) {
            // Update existing allocation (combine advance + payment)
            existingAdvanceAllocation.allocatedAmount += allocatedAmount;
            // Check if full payment using original bill amount
            existingAdvanceAllocation.isFullPayment = existingAdvanceAllocation.allocatedAmount >= originalBillAmount;
            existingAdvanceAllocation.isAdvanceAllocation = false; // Now it's a combined allocation

          } else {
            // Create new allocation
            allocations.push({
              billType: billType,
              billId: bill.id,
              billNumber: bill.number || bill.invoiceNumber || bill.challanNumber || bill.billNumber,
              allocatedAmount: allocatedAmount,
              billOutstanding: originalBillAmount, // Store original bill amount for reference
              isFullPayment: allocatedAmount >= originalBillAmount,
              isAdvanceAllocation: false
            });

          }
          
          // Update bill outstanding for next iteration
          bill.outstanding = Math.max(0, remainingBillOutstanding - allocatedAmount);
          remainingAmount -= allocatedAmount;

        }
      }
      

      return { allocations, remainingAmount };
    } catch (error) {
      console.error('Error in allocatePaymentFIFO:', error);
      return { allocations: [], remainingAmount: paymentAmount };
    }
  };

  // Fetch parties
  useEffect(() => {
    if (db && userId && isAuthReady && companyId) {
      const partiesCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
      const unsubscribe = onSnapshot(
        partiesCollectionRef,
        (snapshot) => {
          const allParties = [];
          snapshot.forEach((doc) => {
            allParties.push({ id: doc.id, ...doc.data() });
          });
          allParties.sort((a, b) => (a.firmName || '').localeCompare(b.firmName || ''));
          setParties(allParties);
        },
        (error) => {
          console.error('Error fetching parties:', error);
          setParties([]);
        }
      );
      return () => unsubscribe();
    }
  }, [db, userId, isAuthReady, appId, companyId]);

  // Fetch invoices
  useEffect(() => {
    if (db && userId && isAuthReady && companyId) {
      const billsCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'salesBills');
      const unsubscribe = onSnapshot(
        billsCollectionRef,
        (snapshot) => {
          const bills = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            const bill = { 
              id: doc.id, 
              ...data,
              invoiceDate: convertTimestamp(data.invoiceDate || data.createdAt),
              date: convertTimestamp(data.date || data.createdAt)
            };
            
            // Calculate total paid by summing all allocations from all payment documents
            let totalPaid = 0;
            
            // Sum all allocations for this bill from all payments
            payments.forEach(payment => {
              if (payment.allocations) {
                const billAllocation = payment.allocations.find(a => 
                  a.billId === bill.id && a.billType === 'invoice'
                );
                if (billAllocation) {
                  totalPaid += billAllocation.allocatedAmount || 0;
                }
              }
            });
            
            const totalAmount = parseFloat(bill.totalAmount || bill.amount) || 0;
            bill.outstanding = Math.max(0, totalAmount - totalPaid);
            bill.totalPaid = totalPaid;
            bill.totalAmount = totalAmount;
            
            // Count payments for this bill
            const billPayments = payments.filter(p => 
              p.allocations && p.allocations.some(a => a.billId === bill.id && a.billType === 'invoice')
            );
            bill.paymentCount = billPayments.length;
            
            bills.push(bill);
          });
          // LIFO sorting is now handled by the pagination utility
          setInvoices(bills);
        },
        (error) => {
          console.error('Error fetching invoices:', error);
          setInvoices([]);
        }
      );
      return () => unsubscribe();
    }
  }, [db, userId, isAuthReady, appId, companyId, payments]);

  // Fetch challans
  useEffect(() => {
    if (db && userId && isAuthReady) {
      const billsCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'challan');
      if (!billsCollectionRef) return;
      
      const unsubscribe = onSnapshot(
        billsCollectionRef,
        (snapshot) => {
          const bills = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            const bill = { 
              id: doc.id, 
              ...data,
              challanDate: convertTimestamp(data.challanDate || data.createdAt),
              date: convertTimestamp(data.date || data.createdAt)
            };
            
            // Calculate total paid by summing all allocations from all payment documents
            let totalPaid = 0;
            
            // Sum all allocations for this bill from all payments
            payments.forEach(payment => {
              if (payment.allocations) {
                const billAllocation = payment.allocations.find(a => 
                  a.billId === bill.id && a.billType === 'challan'
                );
                if (billAllocation) {
                  totalPaid += billAllocation.allocatedAmount || 0;
                }
              }
            });
            
            const totalAmount = parseFloat(bill.totalAmount || bill.amount) || 0;
            bill.outstanding = Math.max(0, totalAmount - totalPaid);
            bill.totalPaid = totalPaid;
            bill.totalAmount = totalAmount;
            
            // Count payments for this bill
            const billPayments = payments.filter(p => 
              p.allocations && p.allocations.some(a => a.billId === bill.id && a.billType === 'challan')
            );
            bill.paymentCount = billPayments.length;
            
            bills.push(bill);
          });
          // LIFO sorting is now handled by the pagination utility
          setChallans(bills);
        },
        (error) => {
          console.error('Error fetching challans:', error);
          setChallans([]);
        }
      );
      return () => unsubscribe();
    }
  }, [db, userId, isAuthReady, appId, companyId, payments]);

  // Fetch purchase bills
  useEffect(() => {
    if (db && userId && isAuthReady) {
      const billsCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'purchaseBills');
      if (!billsCollectionRef) return;
      const unsubscribe = onSnapshot(
        billsCollectionRef,
        (snapshot) => {
          const bills = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            const bill = { 
              id: doc.id, 
              ...data,
              billDate: convertTimestamp(data.billDate || data.createdAt),
              date: convertTimestamp(data.date || data.createdAt)
            };
            
            // Calculate total paid by summing all allocations from all payment documents
            let totalPaid = 0;
            
            // Sum all allocations for this bill from all payments
            payments.forEach(payment => {
              if (payment.allocations) {
                const billAllocation = payment.allocations.find(a => 
                  a.billId === bill.id && a.billType === 'purchase'
                );
                if (billAllocation) {
                  totalPaid += billAllocation.allocatedAmount || 0;
                }
              }
            });
            
            const totalAmount = parseFloat(bill.totalAmount || bill.amount) || 0;
            bill.outstanding = Math.max(0, totalAmount - totalPaid);
            bill.totalPaid = totalPaid;
            bill.totalAmount = totalAmount;
            
            // Count payments for this bill
            const billPayments = payments.filter(p => 
              p.allocations && p.allocations.some(a => a.billId === bill.id && a.billType === 'purchase')
            );
            bill.paymentCount = billPayments.length;
            
            bills.push(bill);
          });
          // LIFO sorting is now handled by the pagination utility
          setPurchaseBills(bills);
        },
        (error) => {
          console.error('Error fetching purchase bills:', error);
          setPurchaseBills([]);
        }
      );
      return () => unsubscribe();
    }
  }, [db, userId, isAuthReady, appId, companyId, payments]);

  // Fetch payments (single table)
  useEffect(() => {
    if (db && userId && isAuthReady) {
      const paymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');
      if (!paymentsRef) return;
      
      const unsubscribePayments = onSnapshot(paymentsRef, (snapshot) => {
        const allPayments = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          const payment = {
            id: doc.id,
            ...data,
            date: convertTimestamp(data.paymentDate || data.createdAt)
          };
          allPayments.push(payment);
        });

        setPayments(allPayments);
        
        // Check for duplicate receipt numbers after payments are loaded
        if (allPayments.length > 0) {
          checkAndFixDuplicateReceiptNumbers();
        }
      });
      
      return () => unsubscribePayments();
    }
  }, [db, userId, isAuthReady, appId, companyId]);

  // Fetch company details
  useEffect(() => {
    if (db && isAuthReady && companyId) {
      const unsubscribe = onSnapshot(doc(db, 'companies', companyId), (docSnap) => {
        if (docSnap.exists()) {
          setCompany(normalizeCompanyDocSnapshot(docSnap.data() || {}, companyId));
        }
      });
      return () => unsubscribe();
    }
  }, [db, isAuthReady, companyId]);

  // Set default financial year when data is loaded
  useEffect(() => {
    if (invoices.length > 0 || challans.length > 0 || purchaseBills.length > 0 || payments.length > 0) {
      const availableYears = getAvailableFinancialYears();

      
      if (availableYears.length > 0 && !paymentsFilter.selectedFinancialYear) {
        // Set to current financial year if available, otherwise use the first available year
        const currentFY = getCurrentFinancialYear();
        const defaultYear = availableYears.includes(currentFY) ? currentFY : availableYears[0];

        paymentsFilter.setSelectedFinancialYear(defaultYear);
      }
    }
  }, [invoices, challans, purchaseBills, payments, paymentsFilter.selectedFinancialYear]);

  // Get current bills based on active tab
  const getCurrentBills = () => {
    switch(activeTab) {
      case 'invoice': return invoices;
      case 'challan': return challans;
      case 'purchase': return purchaseBills;
      default: return [];
    }
  };

  // Get bill number field based on active tab
  const getBillNumberField = () => {
    switch(activeTab) {
      case 'invoice': return 'number';
      case 'challan': return 'challanNumber';
      case 'purchase': return 'number';
      default: return 'number';
    }
  };

  // Get bill number display value
  const getBillNumber = (bill) => {
    switch(activeTab) {
      case 'invoice': return bill.number || bill.invoiceNumber;
      case 'challan': return bill.challanNumber || bill.number;
      case 'purchase': return bill.number || bill.billNumber;
      default: return bill.number;
    }
  };

  // Get bill date field based on active tab
  const getBillDateField = () => {
    switch(activeTab) {
      case 'invoice': return 'invoiceDate';
      case 'challan': return 'challanDate';
      case 'purchase': return 'billDate';
      default: return 'date';
    }
  };

  // Get party name by ID
  const getPartyName = (partyId) => {
    if (!partyId) return 'Unknown Party';
    const party = parties.find(p => p.id === partyId);
    return party ? (party.firmName || party.name) : 'Unknown Party';
  };

  // Filter bills based on time and party
  // Filter bills using the new filter system
  const getFilteredBills = useCallback(() => {
    const bills = getCurrentBills();
    const filteredBills = paymentsFilter.filterBills(bills, parties);
    
    // Add partyName field for sorting and normalize bill number field
    return filteredBills.map(bill => ({
      ...bill,
      partyName: getPartyName(bill.partyId || bill.party),
      number: getBillNumber(bill) // Normalize bill number for consistent sorting
    }));
  }, [paymentsFilter, getCurrentBills, activeTab, invoices, challans, purchaseBills, filterUpdateTrigger, parties]);



  // Sort receipts
  const sortReceipts = (receipts) => {
    if (!receiptsSortBy) return receipts;
    
    return [...receipts].sort((a, b) => {
      let aValue, bValue;
      
      switch (receiptsSortBy) {
        case 'receiptNumber':
          aValue = a.receiptNumber || '';
          bValue = b.receiptNumber || '';
          break;
        case 'partyName':
          aValue = a.partyName || '';
          bValue = b.partyName || '';
          break;
        case 'totalAmount':
          aValue = parseFloat(a.totalAmount || 0);
          bValue = parseFloat(b.totalAmount || 0);
          break;
        case 'paymentDate':
          aValue = new Date(a.paymentDate || a.date || 0);
          bValue = new Date(b.paymentDate || b.date || 0);
          break;
        case 'paymentMode':
          aValue = a.paymentMode || '';
          bValue = b.paymentMode || '';
          break;
        case 'paymentType':
          aValue = a.paymentType || '';
          bValue = b.paymentType || '';
          break;
        default:
          return 0;
      }
      
      if (receiptsSortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  // Handle sort


  // Handle receipts sort
  const handleReceiptsSort = (key) => {
    setReceiptsSortBy(key);
    setReceiptsSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  // Handle add payment (Bill-wise)
  const handleAddPayment = async (bill) => {
    setPaymentType('bill');
    setSelectedBill(bill);
    setSelectedPartyForPayment(bill.partyId || bill.party);
    setPaymentAmount(bill.outstanding.toString());
    const receiptNum = await generateReceiptNumber();
    setReceiptNumber(receiptNum);
    setShowPaymentModal(true);
    
    // Show advance info
    const partyId = bill.partyId || bill.party;
    const collected = getPartyAdvance(partyId);
    
    // Calculate how much advance will be auto-applied
    let advanceToBeApplied = 0;
    if (collected > 0) {
      const { allocatedAdvance } = allocateAdvanceToBill(partyId, bill.outstanding);
      advanceToBeApplied = allocatedAdvance;
    }
    
    setAdvanceInfo({ collected, used: advanceToBeApplied });
    setShowAdvanceInfo(collected > 0);
    
    if (collected > 0) {

    }
  };

  // Handle add khata payment (Party-wise)
  const handleAddKhataPayment = async (partyId) => {
    setPaymentType('khata');
    setSelectedBill(null);
    setSelectedPartyForPayment(partyId);
    setPaymentAmount('');
    const receiptNum = await generateReceiptNumber();
    setReceiptNumber(receiptNum);
    setShowPaymentModal(true);
    
    // Show advance info
    const collected = getPartyAdvance(partyId);
    
    // Calculate how much advance will be auto-applied to outstanding bills
    let advanceToBeApplied = 0;
    if (collected > 0) {
      const outstandingBills = getCurrentBills().filter(bill => 
        (bill.partyId === partyId || bill.party === partyId) && 
        bill.outstanding > 0
      );
      
      if (outstandingBills.length > 0) {
        const totalOutstanding = outstandingBills.reduce((sum, bill) => sum + bill.outstanding, 0);
        const { allocatedAdvance } = allocateAdvanceToBill(partyId, totalOutstanding);
        advanceToBeApplied = allocatedAdvance;
      }
    }
    
    setAdvanceInfo({ collected, used: advanceToBeApplied });
    setShowAdvanceInfo(collected > 0);
    

  };

  // Handle save payment
  const handleSavePayment = async () => {
    if (isSavingPaymentRef.current) return;
    isSavingPaymentRef.current = true;
    setIsSavingPayment(true);
    try {
    // Check email verification before allowing payment creation
    const { checkActionRequiresVerification } = await import('../utils/emailVerificationCheck');
    const verificationCheck = await checkActionRequiresVerification('create payments', {
      requireEmail: true,
      allowPhone: true,
      allowGoogle: true
    });
    
    if (verificationCheck.needsVerification) {
      const verificationError = new Error(verificationCheck.message);
      verificationError.code = 'VERIFICATION_REQUIRED';
      handleValidationError(verificationError, {
        title: 'Email Verification Required',
        context: 'Payment Creation',
        severity: 'warning',
        duration: 10000
      });
      showWarning(verificationCheck.message + ' Please verify your account to continue.', {
        title: 'Verification Required',
        duration: 10000
      });
      info('Go to Settings or click the verification link in your email to verify your account.', {
        title: 'How to Verify',
        duration: 8000
      });
      return;
    }

    if (!selectedPartyForPayment || !paymentAmount) {
      warning('Please select a party and enter payment amount.');
      return;
    }

      const paymentAmountNum = parseFloat(paymentAmount);
      
      if (isNaN(paymentAmountNum) || paymentAmountNum <= 0) {
        warning('Please enter a valid payment amount.');
        return;
      }
      
      // Ensure every payment has a receipt number - auto-generate if empty
      let effectiveReceiptNumber = (receiptNumber || '').trim();
      if (!effectiveReceiptNumber) {
        effectiveReceiptNumber = await generateReceiptNumber();
        setReceiptNumber(effectiveReceiptNumber);
      }
      
      // Check if bills are loaded
      if (!Array.isArray(invoices) || !Array.isArray(challans) || !Array.isArray(purchaseBills)) {
        warning('Bills data is still loading. Please wait a moment and try again.');
        return;
      }
      
      let paymentData;
      let advanceAllocations = [];
      
      if (paymentType === 'bill' && selectedBill) {
        // Bill-wise payment - allocate to specific bill
        const billOutstanding = selectedBill.outstanding || 0;
        
        // Check if payment amount exceeds bill outstanding
        if (paymentAmountNum > billOutstanding) {
          error(`Payment amount (${formatCurrency(paymentAmountNum, companyCurrency)}) cannot exceed the bill's outstanding amount (${formatCurrency(billOutstanding, companyCurrency)}). Please use Khata-wise payment for amounts greater than the bill outstanding.`);
          setPaymentType('khata');
          return;
        }
        
        // First, try to allocate available advance to this bill
        const partyIdForAdvance =
          selectedPartyForPayment?.id ||
          selectedPartyForPayment?.partyId ||
          selectedPartyForPayment;
        const {
          allocatedAdvance,
          advanceAllocations: advanceAllocsFromUtil,
          remainingBillAmount,
        } = allocateAdvanceToBill(partyIdForAdvance, billOutstanding, payments);

        if (allocatedAdvance > 0) {
          advanceAllocations = advanceAllocsFromUtil || [];
          // Mark advances as used
          if (Array.isArray(advanceAllocations) && advanceAllocations.length > 0) {
            await markAdvanceUsed(advanceAllocations, db, appId, userId, payments, companyId);
          }
        }
        
        // Now allocate the new payment
        const actualOutstanding = remainingBillAmount; // After advance allocation
        const allocatedAmount = Math.min(paymentAmountNum, actualOutstanding);
        

        
        // Update the bill's outstanding amount for display
        selectedBill.outstanding = actualOutstanding;
        
        const allocations = [{
          billType: activeTab,
          billId: selectedBill.id,
          billNumber: getBillNumber(selectedBill),
          allocatedAmount: allocatedAmount,
          billOutstanding: actualOutstanding,
          isFullPayment: allocatedAmount >= actualOutstanding
        }];
        
        // Simple bill payment - no excess amount handling since it's restricted above
        paymentData = {
          receiptNumber: effectiveReceiptNumber,
          paymentDate: paymentDate,
          partyId: selectedPartyForPayment,
          partyName: getPartyName(selectedPartyForPayment),
          totalAmount: paymentAmountNum,
          paymentMode: paymentMode,
          reference: paymentReference,
          notes: paymentNotes,
          type: activeTab,
          paymentType: 'bill',
          billId: selectedBill.id,
          billNumber: getBillNumber(selectedBill),
          allocations: allocations,
          remainingAmount: 0, // No remaining amount since payment cannot exceed bill outstanding
          advanceAllocations: advanceAllocations,
          advanceUsed: allocatedAdvance,
          fifoAllocationUsed: 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };

      } else {
        // Khata-wise payment - prioritize opening balance reduction, then bills, then advance
        const availableAdvance = getPartyAdvance(selectedPartyForPayment);
        let advanceAllocations = [];
        let advanceUsed = 0;
        let totalAmountToAllocate = paymentAmountNum;
        
        // STEP 1: First, reduce opening balance if applicable
        const party = parties.find(p => p.id === selectedPartyForPayment);
        let openingBalanceReduced = 0;
        let amountAfterOpeningBalance = paymentAmountNum;
        
        if (party && party.openingBalance !== undefined && party.openingBalance !== 0) {
          if (activeTab === 'receipts' && party.openingBalance > 0) {
            // Receipt: Reduce receivable (positive opening balance) FIRST
            openingBalanceReduced = Math.min(paymentAmountNum, party.openingBalance);
            amountAfterOpeningBalance = paymentAmountNum - openingBalanceReduced;
          } else if (activeTab === 'payments' && party.openingBalance < 0) {
            // Payment: Reduce payable (negative opening balance) FIRST
            openingBalanceReduced = Math.min(paymentAmountNum, Math.abs(party.openingBalance));
            amountAfterOpeningBalance = paymentAmountNum - openingBalanceReduced;
          }
        }
        
        // STEP 2: Combine advance + remaining amount after opening balance reduction
        if (availableAdvance > 0 && amountAfterOpeningBalance > 0) {
          // Get all outstanding bills for this party
          const outstandingBills = getCurrentBills().filter(bill => 
            (bill.partyId === selectedPartyForPayment || bill.party === selectedPartyForPayment) && 
            bill.outstanding > 0
          );
          
          if (outstandingBills.length > 0) {
            // Calculate total amount to allocate (advance + remaining after opening balance)
            totalAmountToAllocate = availableAdvance + amountAfterOpeningBalance;
            
            // Mark advances as used first
            const advanceResult = allocateAdvanceToBill(selectedPartyForPayment, availableAdvance);
            
            if (advanceResult.allocatedAdvance > 0) {
              advanceAllocations = advanceResult.advanceAllocations;
              advanceUsed = advanceResult.allocatedAdvance;
              
              // Mark advances as used - only if advanceAllocations is an array
              if (Array.isArray(advanceResult.advanceAllocations) && advanceResult.advanceAllocations.length > 0) {
                await markAdvanceUsed(advanceResult.advanceAllocations, db, appId, userId, payments, companyId);
              }
            }
          }
        } else if (amountAfterOpeningBalance > 0) {
          // No advance, but we have amount after opening balance reduction
          totalAmountToAllocate = amountAfterOpeningBalance;
        } else {
          // All amount went to opening balance
          totalAmountToAllocate = 0;
        }
        
        // STEP 3: Allocate remaining amount to bills using FIFO
        let allocations = [];
        let finalRemainingAmount = 0;
        
        if (totalAmountToAllocate > 0) {
          const fifoResult = allocatePaymentFIFO(selectedPartyForPayment, totalAmountToAllocate, activeTab);
          
          // Validate FIFO result
          if (!fifoResult || !Array.isArray(fifoResult.allocations)) {
            console.error('Invalid FIFO result:', fifoResult);
            throw new Error('Failed to allocate payment. Please try again.');
          }
          
          allocations = fifoResult.allocations;
          finalRemainingAmount = fifoResult.remainingAmount;
        }
        
        paymentData = {
          receiptNumber: effectiveReceiptNumber,
          paymentDate: paymentDate,
          partyId: selectedPartyForPayment,
          partyName: getPartyName(selectedPartyForPayment),
          totalAmount: paymentAmountNum, // Original payment amount (opening balance + bills + advance + remaining)
          paymentMode: paymentMode,
          reference: paymentReference,
          notes: paymentNotes,
          type: activeTab,
          paymentType: 'khata',
          allocations: allocations,
          remainingAmount: finalRemainingAmount, // Remaining after opening balance and bills allocation
          openingBalanceReduced: openingBalanceReduced, // Track how much opening balance was reduced (reduced FIRST)
          advanceAllocations: advanceAllocations,
          advanceUsed: advanceUsed,
          originalPaymentAmount: paymentAmountNum, // Store the original payment amount entered by user
          advanceAmount: availableAdvance, // Store the advance amount that was combined
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
      }

      if (activeTab === 'payments' || activeTab === 'purchase') {
        appendWorkOrderFields(paymentData, workOrderLink);
      }

      // Validate payment data before saving
      if (!paymentData || !paymentData.receiptNumber || !paymentData.partyId) {
        throw new Error('Invalid payment data. Please check all required fields.');
      }

      // IMPORTANT: Validate employee balance BEFORE creating payment (for outflow transactions)
      if (recordInEmployeeCashFlow && selectedEmployeeForCashFlow) {
        const selectedEmployee = employees?.find(emp => emp.id === selectedEmployeeForCashFlow);
        if (selectedEmployee) {
          if ((selectedEmployee.status || 'active') !== 'active') {
            error('Selected employee is inactive. Please choose an active employee.', {
              title: 'Inactive Employee',
              duration: 5000
            });
            return;
          }
          // Determine transaction type based on activeTab
          // Invoice/Challan/Receipts = inflow (money received from customers)
          // Purchase/Payments = outflow (money paid to suppliers)
          const transactionType = (activeTab === 'invoice' || activeTab === 'challan' || activeTab === 'receipts') ? 'inflow' : 'outflow';
          
          // Map paymentMode to cash/upi - improved detection
          let mappedPaymentMode = 'cash';
          const paymentModeStr = String(paymentMode || '').toLowerCase().trim();
          if (paymentModeStr === 'upi' || paymentModeStr.includes('upi') || paymentModeStr === 'unified payments interface') {
            mappedPaymentMode = 'upi';
          } else if (paymentModeStr === 'cash' || paymentModeStr.includes('cash')) {
            mappedPaymentMode = 'cash';
          }
          
          const companyData = {
            companyId: currentCompany?.companyId,
            userId: currentCompany?.userId,
            appId: currentCompany?.appId
          };
          
          // Validate balance for outflow transactions BEFORE creating payment
          if (transactionType === 'outflow') {
            const employeeBalance = await EmployeeCashManager.getEmployeeBalance(companyData, selectedEmployeeForCashFlow);
            const availableBalance = mappedPaymentMode === 'cash' ? employeeBalance.cash : mappedPaymentMode === 'upi' ? employeeBalance.upi : employeeBalance.total;
            
            if (availableBalance < paymentAmountNum) {
              error(`Insufficient balance! ${selectedEmployee.name} has ₹${availableBalance.toLocaleString('en-IN')} ${mappedPaymentMode === 'cash' ? 'cash' : 'UPI'} balance, but needs ₹${paymentAmountNum.toLocaleString('en-IN')}`, {
                title: 'Insufficient Balance',
                duration: 6000
              });
              return; // Stop here - don't create payment
            }
          }
        }
      }

      // Add to payments collection
      const paymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');
      const docRef = await addDoc(paymentsRef, paymentData);
      if (isPaymentOutflow(paymentData)) {
        await syncLedgerFromSource(db, companyCtx, {
          sourceType: LEDGER_SOURCE_TYPES.payment_out,
          sourceCollection: 'payments',
          sourceId: docRef.id,
          doc: { ...paymentData, id: docRef.id },
        });
      }

      // Update opening balance if it was reduced
      if (paymentData.openingBalanceReduced > 0 && paymentData.paymentType === 'khata') {
        try {
          const party = parties.find(p => p.id === selectedPartyForPayment);
          if (party) {
            let newOpeningBalance = party.openingBalance;
            
            if (activeTab === 'receipts' && party.openingBalance > 0) {
              // Receipt: Reduce receivable (positive opening balance)
              newOpeningBalance = Math.max(0, party.openingBalance - paymentData.openingBalanceReduced);
            } else if (activeTab === 'payments' && party.openingBalance < 0) {
              // Payment: Reduce payable (negative opening balance)
              newOpeningBalance = Math.min(0, party.openingBalance + paymentData.openingBalanceReduced);
            }
            
            // Update party opening balance
            if (newOpeningBalance !== party.openingBalance) {
              const partyRef = doc(getCollectionForCompany(db, { companyId, appId, userId }, 'parties'), selectedPartyForPayment);
              await updateDoc(partyRef, {
                openingBalance: newOpeningBalance,
                updatedAt: serverTimestamp()
              });
              console.log(`✅ Updated opening balance for ${party.firmName}: ${party.openingBalance} → ${newOpeningBalance} (reduced by ${paymentData.openingBalanceReduced})`);
            }
          }
        } catch (openingBalanceError) {
          console.error('Error updating opening balance:', openingBalanceError);
          // Don't fail the payment if opening balance update fails
          warning('Payment saved successfully, but failed to update opening balance.');
        }
      }

      // Record in employee cash flow if enabled
      if (recordInEmployeeCashFlow && selectedEmployeeForCashFlow) {
        try {
          const selectedEmployee = employees?.find(emp => emp.id === selectedEmployeeForCashFlow);
          if (selectedEmployee) {
            if ((selectedEmployee.status || 'active') !== 'active') {
              error('Selected employee is inactive. Please choose an active employee.', {
                title: 'Inactive Employee',
                duration: 5000
              });
              return;
            }
            // Determine transaction type based on activeTab
            // Invoice/Challan/Receipts = inflow (money received from customers)
            // Purchase/Payments = outflow (money paid to suppliers)
            const transactionType = (activeTab === 'invoice' || activeTab === 'challan' || activeTab === 'receipts') ? 'inflow' : 'outflow';
            
            // Map paymentMode to cash/upi - improved detection
            let mappedPaymentMode = 'cash';
            const paymentModeStr = String(paymentMode || '').toLowerCase().trim();
            if (paymentModeStr === 'upi' || paymentModeStr.includes('upi') || paymentModeStr === 'unified payments interface') {
              mappedPaymentMode = 'upi';
            } else if (paymentModeStr === 'cash' || paymentModeStr.includes('cash')) {
              mappedPaymentMode = 'cash';
            }
            
            const companyData = {
              companyId: currentCompany?.companyId,
              userId: currentCompany?.userId,
              appId: currentCompany?.appId
            };
            
            const employeeTransactionData = {
              employeeId: selectedEmployeeForCashFlow,
              employeeName: selectedEmployee.name || '',
              transactionType,
              amount: paymentAmountNum,
              paymentMode: mappedPaymentMode,
              description: `Payment ${paymentData.receiptNumber} - ${paymentData.partyName}`,
              referenceNumber: paymentData.receiptNumber,
              date: paymentData.paymentDate,
              category: transactionType === 'inflow' ? 'Collection' : 'Expense',
              createdBy: {
                userId: userId || currentCompany?.userId || '',
                name: currentCompany?.userName || 'User'
              },
              linkedPaymentId: docRef.id, // Link to payment record
              linkedReceiptNumber: paymentData.receiptNumber, // Link to receipt number
              ...(mappedPaymentMode === 'cash' && {
                ...(transactionType === 'inflow' 
                  ? { cashReceivedFrom: paymentData.partyName }
                  : { cashGivenTo: paymentData.partyName })
              }),
              ...(mappedPaymentMode === 'upi' && {
                upiId: selectedEmployee.paymentPreferences?.upiId || '',
                transactionId: paymentData.reference || '',
                upiApp: selectedEmployee.paymentPreferences?.upiApp || 'Other'
              })
            };
            
            const employeeResult = await EmployeeCashManager.createCashTransaction(
              companyData, 
              employeeTransactionData
            );
            
            if (employeeResult.success) {
              success('Payment recorded in employee cash flow', {
                title: 'Employee Transaction Created',
                duration: 4000
              });
            } else {
              // Rollback payment if employee transaction failed
              try {
                await deleteDoc(docRef);
                console.log('Payment rolled back due to employee transaction failure');
              } catch (rollbackError) {
                console.error('Error rolling back payment:', rollbackError);
              }
              error(employeeResult.error || 'Failed to record in employee cash flow. Payment was not processed.', {
                title: 'Transaction Failed',
                duration: 6000
              });
              return; // Stop here - payment rolled back
            }
          }
        } catch (employeeError) {
          console.error('Error recording in employee cash flow:', employeeError);
          // Rollback payment if employee transaction fails
          try {
            await deleteDoc(docRef);
            console.log('Payment rolled back due to employee transaction error');
          } catch (rollbackError) {
            console.error('Error rolling back payment:', rollbackError);
          }
          error('Payment was not processed. Failed to record in employee cash flow: ' + employeeError.message, {
            title: 'Transaction Failed',
            duration: 6000
          });
          return; // Stop here - payment rolled back
        }
      }

      // Reset form
      setPaymentAmount('');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      setPaymentMode('Cash');
      setPaymentReference('');
      setPaymentNotes('');
      setReceiptNumber('');
      setSelectedPartyForPayment('');
      setPaymentType('bill');
      setShowPaymentModal(false);
      setSelectedBill(null);
      setRecordInEmployeeCashFlow(false);
      setSelectedEmployeeForCashFlow('');
      
      // Show success notification with payment details
      const partyName = getPartyName(selectedPartyForPayment);
      paymentSuccess(
        `Payment of ${formatCurrency(paymentAmountNum, companyCurrency)} has been added successfully!`,
        {
          title: 'Payment Added Successfully',
          details: `Receipt #${effectiveReceiptNumber} • ${partyName} • ${paymentMode}`,
          duration: 6000
        }
      );
    } catch (err) {
      console.error('Error saving payment:', err);
      
      // More specific error messages
      if (err.code === 'permission-denied') {
        error('Permission denied. Please check your access rights.');
      } else if (err.code === 'unavailable') {
        error('Network error. Please check your internet connection and try again.');
      } else if (err.message && err.message.includes('duplicate')) {
        error('Duplicate receipt number. Please use a different receipt number.');
      } else {
        error(`Error saving payment: ${err.message || 'Please try again.'}`);
      }
    } finally {
      isSavingPaymentRef.current = false;
      setIsSavingPayment(false);
    }
  };

  // Handle view payment details
  const handleViewPaymentDetails = (bill) => {
    setSelectedBill(bill);
    setShowPaymentDetailsModal(true);
  };

  // Handle preview receipt
  const handlePreviewReceipt = (payment) => {
    setSelectedReceipt(payment);
    setShowReceiptModal(true);
    // Close the payment details modal when opening receipt preview
    setShowPaymentDetailsModal(false);
  };

  // Handle delete payment confirmation
  const handleDeletePaymentClick = (payment) => {
    setPaymentToDelete(payment);
    setShowDeleteConfirm(true);
  };

  // Handle delete payment
  const handleDeletePayment = async () => {
    if (!paymentToDelete) return;

    try {
      const companyData = { companyId, appId, userId };

      // Reverse opening-balance reduction applied by this payment (khata)
      if (
        paymentToDelete.openingBalanceReduced > 0 &&
        paymentToDelete.paymentType === 'khata' &&
        paymentToDelete.partyId
      ) {
        try {
          const partyRef = doc(
            getCollectionForCompany(db, companyData, 'parties'),
            paymentToDelete.partyId
          );
          const partySnap = await getDoc(partyRef);
          if (partySnap.exists()) {
            const party = partySnap.data();
            const reduced = Number(paymentToDelete.openingBalanceReduced) || 0;
            let restored = party.openingBalance || 0;
            const isReceipt =
              paymentToDelete.paymentCategory === 'receipt' ||
              paymentToDelete.type === 'receipt' ||
              (paymentToDelete.allocations || []).some((a) => a.billType === 'invoice');
            if (isReceipt) {
              restored = (party.openingBalance || 0) + reduced;
            } else {
              restored = (party.openingBalance || 0) - reduced;
            }
            await updateDoc(partyRef, {
              openingBalance: restored,
              updatedAt: serverTimestamp(),
            });
          }
        } catch (obErr) {
          console.error('Failed to reverse opening balance on payment delete:', obErr);
          warning('Payment will be deleted, but opening balance could not be fully restored.');
        }
      }

      // Reverse advance usage this payment consumed from older advances
      if (
        Array.isArray(paymentToDelete.advanceAllocations) &&
        paymentToDelete.advanceAllocations.length > 0
      ) {
        try {
          await reverseAdvanceUsed(paymentToDelete.advanceAllocations);
        } catch (advErr) {
          console.error('Failed to reverse advance usage on payment delete:', advErr);
          warning('Payment will be deleted, but advance usage could not be fully restored.');
        }
      }

      const cascadeResult = await EmployeeCashManager.deleteTransactionsForPaymentsCollectionPayment(
        companyData,
        paymentToDelete.id
      );
      if (!cascadeResult.success && cascadeResult.error) {
        error(
          `Could not remove linked employee wallet entries: ${cascadeResult.error}. Payment was not deleted.`,
          { title: 'Delete blocked', duration: 8000 }
        );
        setShowDeleteConfirm(false);
        setPaymentToDelete(null);
        return;
      }

      if (isPaymentOutflow(paymentToDelete)) {
        await removeLedgerEntry(db, companyData, {
          sourceType: LEDGER_SOURCE_TYPES.payment_out,
          sourceId: paymentToDelete.id,
        });
      }
      const paymentRef = doc(getCollectionForCompany(db, companyData, 'payments'), paymentToDelete.id);
      await deleteDoc(paymentRef);
      
      setShowPaymentDetailsModal(false);
      setShowReceiptModal(false);
      setShowDeleteConfirm(false);
      setPaymentToDelete(null);
      const deletedMsg =
        cascadeResult.deletedCount > 0
          ? `Payment deleted. ${cascadeResult.deletedCount} employee wallet transaction(s) reversed.`
          : 'Payment deleted successfully!';
      success(deletedMsg, { title: 'Payment Deleted' });
    } catch (err) {
      console.error('Error deleting payment:', err);
      error('Error deleting payment. Please try again.');
      setShowDeleteConfirm(false);
      setPaymentToDelete(null);
    }
  };

  // Print receipt (Browser print)
  const printReceipt = () => {
    if (!selectedReceipt) return;
    
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    const Template = selectedReceipt.type === 'purchase' ? PurchaseReceiptTemplate : ReceiptTemplate;
    
    // Render the receipt template
    const receiptElement = React.createElement(Template, {
      receipt: selectedReceipt,
      bill: selectedBill,
      company: company,
      party: parties.find(p => p.id === selectedReceipt.partyId),
      receiptNumber: selectedReceipt.receiptNumber,
      fifoAllocation: selectedReceipt.allocations
    });
    
    // Create the HTML content
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Payment Receipt - ${selectedReceipt.receiptNumber}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              background: white; 
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
            .receipt-container {
              max-width: 800px;
              margin: 0 auto;
              background: white;
              padding: 20px;
              border: 1px solid #ddd;
            }
            .header { text-align: center; margin-bottom: 30px; }
            .title { font-size: 24px; font-weight: bold; color: #1f2937; margin-bottom: 10px; }
            .receipt-number { font-size: 18px; color: #3b82f6; font-family: monospace; }
            .company-info { 
              text-align: center; 
              margin-bottom: 20px; 
              padding: 15px; 
              border-bottom: 2px solid #d1d5db; 
            }
            .company-name { font-size: 20px; font-weight: bold; color: #1f2937; margin-bottom: 5px; }
            .details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
            .detail-item { margin-bottom: 15px; }
            .label { font-weight: bold; color: #374151; margin-bottom: 5px; }
            .value { font-size: 16px; }
            .amount { font-size: 24px; font-weight: bold; color: #059669; }
            .section { border-top: 1px solid #e5e7eb; padding-top: 20px; margin-top: 20px; }
            .fifo-section { margin-top: 30px; }
            .fifo-title { font-size: 18px; font-weight: bold; color: #1d4ed8; margin-bottom: 15px; }
            .allocation-item { 
              padding: 15px; 
              background: #f9fafb; 
              border: 1px solid #e5e7eb; 
              border-radius: 8px; 
              margin-bottom: 10px; 
            }
            .allocation-header { 
              display: flex; 
              justify-content: space-between; 
              align-items: center; 
              margin-bottom: 8px; 
            }
            .invoice-number { font-weight: bold; }
            .status { font-size: 14px; font-weight: bold; }
            .status.full { color: #059669; }
            .status.partial { color: #d97706; }
            .allocation-details { font-size: 14px; color: #6b7280; margin-bottom: 8px; }
            .allocated-amount { font-size: 16px; font-weight: bold; color: #1d4ed8; }
            .footer { 
              margin-top: 40px; 
              padding-top: 20px; 
              border-top: 1px solid #d1d5db; 
              display: flex; 
              justify-content: space-between; 
              align-items: flex-end; 
            }
            .generated-info { font-size: 12px; color: #6b7280; }
            .signature { text-align: center; }
            .signature-line { 
              border-top: 2px solid #9ca3af; 
              width: 120px; 
              margin: 0 auto 8px; 
            }
            .signature-text { font-weight: bold; color: #374151; }
            .print-button { 
              position: fixed; 
              top: 20px; 
              right: 20px; 
              background: #3b82f6; 
              color: white; 
              padding: 10px 20px; 
              border: none; 
              border-radius: 5px; 
              cursor: pointer; 
              font-size: 14px; 
            }
            .print-button:hover { background: #2563eb; }
          </style>
        </head>
        <body>
          <button class="print-button no-print" onclick="window.print()">Print Receipt</button>
          <div class="receipt-container">
            <div class="header">
              <div class="title">PAYMENT RECEIPT</div>
              <div class="receipt-number">Receipt No: ${selectedReceipt.receiptNumber}</div>
            </div>
            
            <div class="company-info">
              <div class="company-name">${company?.firmName || company?.name || 'Company Name'}</div>
              ${company?.address ? `<div style="font-size: 14px; color: #6b7280; margin-bottom: 5px;">${company.address}</div>` : ''}
              ${company?.gstin ? `<div style="font-size: 14px; color: #6b7280;">GSTIN: ${company.gstin}</div>` : ''}
            </div>
            
            <div class="details-grid">
              <div class="detail-item">
                <div class="label">Date:</div>
                <div class="value">${selectedReceipt.paymentDate || new Date().toISOString().split('T')[0]}</div>
              </div>
              <div class="detail-item">
                <div class="label">Receipt No:</div>
                <div class="value" style="font-family: monospace; color: #3b82f6;">${selectedReceipt.receiptNumber}</div>
              </div>
            </div>
            
            <div class="section">
              <div class="detail-item">
                <div class="label">Received From:</div>
                <div class="value">${selectedReceipt.partyName || 'Party Name'}</div>
              </div>
              
              <div class="detail-item">
                <div class="label">Amount:</div>
                <div class="amount">${formatCurrency(parseFloat(selectedReceipt.totalAmount || 0), companyCurrency)}</div>
              </div>
              
              <div class="details-grid">
                <div class="detail-item">
                  <div class="label">Payment Mode:</div>
                  <div class="value">${selectedReceipt.paymentMode || 'Cash'}</div>
                </div>
                <div class="detail-item">
                  <div class="label">Reference:</div>
                  <div class="value">${selectedReceipt.reference || '-'}</div>
                </div>
              </div>
              
              <div class="detail-item">
                <div class="label">Payment Type:</div>
                <div class="value" style="color: #3b82f6;">${selectedReceipt.paymentType === 'bill' ? 'Bill Payment' : 'Khata Payment (FIFO)'}</div>
              </div>
              
              ${selectedReceipt.notes ? `
                <div class="detail-item">
                  <div class="label">Notes:</div>
                  <div class="value">${selectedReceipt.notes}</div>
                </div>
              ` : ''}
            </div>
            
            ${selectedReceipt.allocations && selectedReceipt.allocations.length > 0 ? `
              <div class="fifo-section">
                <div class="fifo-title">${selectedReceipt.type === 'purchase' ? 'Bill' : 'Invoice'}-wise Allocation (FIFO):</div>
                ${selectedReceipt.allocations.map((allocation, index) => `
                  <div class="allocation-item">
                    <div class="allocation-header">
                      <span class="invoice-number">${selectedReceipt.type === 'purchase' ? 'Bill' : 'Invoice'}: ${allocation.billNumber}</span>
                      <span class="status ${allocation.isFullPayment ? 'full' : 'partial'}">${allocation.isFullPayment ? '(Full)' : '(Partial)'}</span>
                    </div>
                    <div class="allocation-details">
                      Outstanding: ${formatCurrency(allocation.billOutstanding || 0, companyCurrency)}
                    </div>
                    <div class="allocated-amount">
                      Allocated: ${formatCurrency(allocation.allocatedAmount || 0, companyCurrency)}
                    </div>
                  </div>
                `).join('')}
              </div>
            ` : ''}
            
            <div class="footer">
              <div class="generated-info">
                <div>Generated on: ${new Date().toLocaleDateString()}</div>
                <div>Time: ${new Date().toLocaleTimeString()}</div>
              </div>
              <div class="signature">
                <div class="signature-line"></div>
                <div class="signature-text">Authorized Signatory</div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Wait for content to load then print
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 800);
    };
  };

  // Generate PDF receipt using direct jsPDF (no HTML rendering issues)
  const generatePDFReceipt = async () => {
    if (!selectedReceipt) return;
    
    // Resolve jsPDF constructor: global, imported, or dynamic import
    let JsPdfCtor = window.jsPDF || (window.jspdf && window.jspdf.jsPDF) || jsPDF;
    if (!JsPdfCtor) {
      try {
        const mod = await import('jspdf');
        JsPdfCtor = (mod && (mod.jsPDF || mod.default)) || undefined;
      } catch (e) {
        console.error('Failed to dynamically load jsPDF:', e);
      }
    }
    if (!JsPdfCtor) {
      error('PDF generation library not available', { title: 'PDF Error' });
      return;
    }
    
    const doc = new JsPdfCtor();
    
    // Set initial position
    let y = 15;
    
    // Add title
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(31, 41, 55); // Dark gray
    doc.text('PAYMENT RECEIPT', 105, y, { align: 'center' });
    y += 12;
    
    // Add receipt number
    doc.setFontSize(12);
    doc.setTextColor(59, 130, 246); // Blue color
    doc.setFont('helvetica', 'bold');
    doc.text(`Receipt No: ${selectedReceipt.receiptNumber}`, 105, y, { align: 'center' });
    y += 12;
    
    // Add company name
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text(company?.firmName || company?.name || 'Company Name', 105, y, { align: 'center' });
    y += 8;
    
    // Add company details if available
    if (company?.address) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(107, 114, 128); // Gray color
      doc.text(company.address, 105, y, { align: 'center' });
      y += 6;
    }
    if (company?.gstin) {
      doc.text(`GSTIN: ${company.gstin}`, 105, y, { align: 'center' });
      y += 10;
    }
    
    // Add line separator
    doc.setDrawColor(209, 213, 219); // Light gray line
    doc.line(25, y, 185, y);
    y += 12;
    
    // Add payment details section
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    
    // Single column layout for better readability and no text cutoff
    const leftMargin = 30;
    const labelX = leftMargin;
    const valueX = leftMargin + 80;
    
    // Date
    doc.setFont('helvetica', 'bold');
    doc.text('Date:', labelX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(selectedReceipt.paymentDate || new Date().toISOString().split('T')[0], valueX, y);
    y += 10;
    
    // Receipt Number
    doc.setFont('helvetica', 'bold');
    doc.text('Receipt No:', labelX, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(59, 130, 246);
    doc.setFont('helvetica', 'bold');
    doc.text(selectedReceipt.receiptNumber, valueX, y);
    doc.setTextColor(0, 0, 0);
    y += 10;
    
    // Received From
    doc.setFont('helvetica', 'bold');
    doc.text('Received From:', labelX, y);
    doc.setFont('helvetica', 'normal');
    doc.setFont('helvetica', 'bold');
    doc.text(selectedReceipt.partyName || 'Party Name', valueX, y);
    doc.setFont('helvetica', 'normal');
    y += 10;
    
    // Amount (larger and prominent)
    doc.setFont('helvetica', 'bold');
    doc.text('Amount:', labelX, y);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(5, 150, 105); // Green color
    doc.text(`${formatCurrency(parseFloat(selectedReceipt.totalAmount || 0), companyCurrency)}`, valueX, y);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    y += 12;
    
    // Payment Mode
    doc.setFont('helvetica', 'bold');
    doc.text('Payment Mode:', labelX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(selectedReceipt.paymentMode || 'Cash', valueX, y);
    y += 10;
    
    // Reference
    doc.setFont('helvetica', 'bold');
    doc.text('Reference:', labelX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(selectedReceipt.reference || '-', valueX, y);
    y += 10;
    
    // Payment Type
    doc.setFont('helvetica', 'bold');
    doc.text('Payment Type:', labelX, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(59, 130, 246);
    doc.setFont('helvetica', 'bold');
    doc.text(selectedReceipt.paymentType === 'bill' ? 'Bill Payment' : 'Khata Payment (FIFO)', valueX, y);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    y += 12;
    
    y += 15;
    
    // Add notes if available
    if (selectedReceipt.notes) {
      doc.setFont('helvetica', 'bold');
      doc.text('Notes:', 30, y);
      doc.setFont('helvetica', 'normal');
      doc.text(selectedReceipt.notes, 80, y);
      y += 12;
    }
    
    // Add FIFO allocations if available
    if (selectedReceipt.allocations && selectedReceipt.allocations.length > 0) {
      y += 6;
      
      // Section title
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(29, 78, 216); // Blue color
      doc.text(`${selectedReceipt.type === 'purchase' ? 'Bill' : 'Invoice'}-wise Allocation (FIFO):`, 30, y);
      y += 10;
      
      // Add subtle background for allocations
      doc.setFillColor(249, 250, 251); // Light gray background
      doc.rect(25, y - 5, 160, (selectedReceipt.allocations.length * 25) + 10, 'F');
      
      selectedReceipt.allocations.forEach((allocation, index) => {
        if (y > 240) {
          doc.addPage();
          y = 25;
        }
        
        // Allocation header with better spacing
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(`${selectedReceipt.type === 'purchase' ? 'Bill' : 'Invoice'}: ${allocation.billNumber}`, 35, y);
        
        const status = allocation.allocatedAmount >= allocation.billOutstanding ? '(Full)' : '(Partial)';
        const statusColor = allocation.allocatedAmount >= allocation.billOutstanding ? [5, 150, 105] : [217, 119, 6];
        doc.setTextColor(...statusColor);
        doc.text(status, 160, y);
        doc.setTextColor(0, 0, 0);
        
        y += 5;
        
        // Allocation details with better formatting
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(107, 114, 128); // Gray color
        doc.text(`Outstanding: ${formatCurrency(allocation.billOutstanding || 0, companyCurrency)}`, 40, y);
        y += 5;
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(29, 78, 216); // Blue color
        doc.text(`Allocated: ${formatCurrency(allocation.allocatedAmount || 0, companyCurrency)}`, 40, y);
        doc.setTextColor(0, 0, 0);
        
        y += 10;
      });
    }
    
    // Add footer
    if (y > 250) {
      doc.addPage();
      y = 25;
    }
    
    y += 15;
    
    // Footer line
    doc.setDrawColor(209, 213, 219);
    doc.line(25, y, 185, y);
    y += 12;
    
    // Generated info
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(107, 114, 128);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 30, y);
    doc.text(`Time: ${new Date().toLocaleTimeString()}`, 30, y + 6);
    
    // Signature section
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Authorized Signatory', 150, y, { align: 'center' });
    doc.setDrawColor(156, 163, 175);
    doc.line(120, y + 6, 180, y + 6);
    
    // Save the PDF
    doc.save(`receipt-${selectedReceipt.receiptNumber}.pdf`);
  };

  // Check and fix duplicate receipt numbers
  const checkAndFixDuplicateReceiptNumbers = async () => {
    try {
      const paymentsQuery = query(getCollectionForCompany(db, { companyId, appId, userId }, 'payments'), orderBy('receiptNumber'));
      const paymentsSnapshot = await getDocs(paymentsQuery);
      
      const receiptNumbers = new Set();
      const duplicates = [];
      const allReceiptNumbers = [];
      
      paymentsSnapshot.forEach((doc) => {
        const payment = doc.data();
        if (payment.receiptNumber) {
          allReceiptNumbers.push({
            id: doc.id,
            receiptNumber: payment.receiptNumber,
            partyName: payment.partyName || 'Unknown',
            amount: payment.totalAmount || payment.amount || 0
          });
          
          if (receiptNumbers.has(payment.receiptNumber)) {
            duplicates.push({ id: doc.id, receiptNumber: payment.receiptNumber });
          } else {
            receiptNumbers.add(payment.receiptNumber);
          }
        }
      });
      
      if (duplicates.length > 0) {
        // Fix duplicates by regenerating receipt numbers
        for (const duplicate of duplicates) {
          const newReceiptNumber = await generateReceiptNumber();
          
          await updateDoc(doc(getCollectionForCompany(db, { companyId, appId, userId }, 'payments'), duplicate.id), {
            receiptNumber: newReceiptNumber
          });
        }
      }
    } catch (error) {
      console.error('Error checking/fixing duplicate receipt numbers:', error);
    }
  };

  // Handle ESC key for modals (LIFO - Last In, First Out)
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        // LIFO order: Last opened modal closes first
        if (showReceiptModal) {
          setShowReceiptModal(false);
        } else if (showPaymentDetailsModal) {
          setShowPaymentDetailsModal(false);
        } else if (showPaymentModal) {
          setShowPaymentModal(false);
        } else if (advanceModalParty) {
          setAdvanceModalParty(null);
        }
      }
    };
    
    // Add event listener if any modal is open
    if (showPaymentModal || showPaymentDetailsModal || showReceiptModal || advanceModalParty) {
      document.addEventListener('keydown', handleEscKey);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showPaymentModal, showPaymentDetailsModal, showReceiptModal, advanceModalParty]);

  // Get bill payments
  const getBillPayments = (bill) => {
    return payments.filter(payment => {
      // Check if payment is for the same party
      const paymentPartyId = payment.partyId || payment.party;
      const billPartyId = bill.partyId || bill.party;
      if (paymentPartyId !== billPartyId) {
        return false;
      }
      
      // Special handling for advance receipts
      if (payment.receiptNumber && payment.receiptNumber.startsWith('ADV-')) {
        // Extract invoice number from advance receipt (e.g., ADV-INV25-26/12 -> INV25-26/12)
        const targetInvoiceNo = payment.receiptNumber.replace('ADV-', '');
        const currentInvoiceNo = bill.number || bill.invoiceNumber || bill.id;
        return targetInvoiceNo === currentInvoiceNo;
      }
      
      // Check if payment has allocations for this invoice
      if (payment.allocations && Array.isArray(payment.allocations)) {
        return payment.allocations.some(allocation => 
          allocation.billId === bill.id && allocation.billType === activeTab
        );
      }
      
      // Check if payment has advance allocations for this invoice
      if (payment.advanceAllocations && Array.isArray(payment.advanceAllocations)) {
        return payment.advanceAllocations.some(allocation => 
          allocation.billId === bill.id
        );
      }
      
      // Check direct billId match
      if (payment.billId === bill.id) {
        return true;
      }
      
      return false;
    });
  };

  // Get party-wise summary
  const getPartyWiseSummary = () => {
    const bills = getFilteredBills(); // Use filtered bills to apply time filters
    const partySummary = {};
    
    bills.forEach(bill => {
      const partyId = bill.partyId || bill.party;
      if (!partySummary[partyId]) {
        const party = parties.find(p => p.id === partyId);
        partySummary[partyId] = {
          partyId: partyId,
          partyName: getPartyName(partyId),
          totalBills: 0,
          totalAmount: 0,
          totalPaid: 0,
          outstanding: 0,
          openingBalance: party?.openingBalance || 0
        };
      }
      
      partySummary[partyId].totalBills++;
      partySummary[partyId].totalAmount += bill.totalAmount || 0;
      partySummary[partyId].totalPaid += bill.totalPaid || 0;
      partySummary[partyId].outstanding += bill.outstanding || 0;
    });
    
    let summary = Object.values(partySummary);
    
    // Sort by party name
    summary.sort((a, b) => a.partyName.localeCompare(b.partyName));
    
    // Filter by search term from new filter system
    if (paymentsFilter.partySearchTerm) {
      summary = summary.filter(party => 
        party.partyName.toLowerCase().includes(paymentsFilter.partySearchTerm.toLowerCase())
      );
    }
    
    return summary;
  };

  // Table pagination hook - placed after function definitions
  const sortedPartySummary = getSortedData(getPartyWiseSummary());
  const pagination = useTablePagination(sortedPartySummary, 10);



  // Calculate totals for bills table
  const getBillsTotals = () => {
    const bills = getFilteredBills();
    return {
      totalBills: bills.length,
      totalAmount: bills.reduce((sum, bill) => sum + (bill.totalAmount || 0), 0),
      totalPaid: bills.reduce((sum, bill) => sum + (bill.totalPaid || 0), 0),
      totalOutstanding: bills.reduce((sum, bill) => sum + (bill.outstanding || 0), 0)
    };
  };

  // Calculate totals for party-wise summary
  const getPartyWiseTotals = () => {
    const summary = getPartyWiseSummary();
    return {
      totalParties: summary.length,
      totalBills: summary.reduce((sum, party) => sum + party.totalBills, 0),
      totalAmount: summary.reduce((sum, party) => sum + party.totalAmount, 0),
      totalPaid: summary.reduce((sum, party) => sum + party.totalPaid, 0),
      totalOutstanding: summary.reduce((sum, party) => sum + party.outstanding, 0),
      totalOpeningBalance: summary.reduce((sum, party) => sum + (party.openingBalance || 0), 0)
    };
  };

  // Calculate totals for receipts table
  const getReceiptsTotals = () => {
    const filteredReceipts = paymentsFilter.filterPayments(payments.filter(payment => {
      if (activeTab === 'receipts') {
        // Filter by sub-tab for receipts
        return payment.type === receiptsSubTab;
      }
      return payment.type === activeTab;
    }));
    
    return {
      totalReceipts: filteredReceipts.length,
      totalAmount: filteredReceipts.reduce((sum, payment) => sum + (payment.totalAmount || 0), 0)
    };
  };

  // Comprehensive Export Functions
  const handleExport = async (tableType) => {
    let filename = 'payments-report';
    let tables = [];
    let title = '';
    
    if (tableType === 'bills') {
    const bills = getFilteredBills();
    const exportData = bills.map(bill => {
        const party = parties.find(p => p.id === (bill.partyId || bill.party));
        const openingBalance = party?.openingBalance || 0;
        return {
        billNumber: getBillNumber(bill),
        date: convertTimestamp(bill[getBillDateField()]),
        partyName: getPartyName(bill.partyId || bill.party),
          openingBalance: openingBalance,
        totalAmount: bill.totalAmount || 0,
        totalPaid: bill.totalPaid || 0,
        outstanding: bill.outstanding || 0,
        paymentCount: bill.paymentCount || 0
        };
      });

      // Calculate totals
      const totals = getBillsTotals();
      const totalRow = {
        billNumber: `TOTAL (${totals.totalBills} bills)`,
        date: '',
        partyName: '',
        openingBalance: '',
        totalAmount: totals.totalAmount,
        totalPaid: totals.totalPaid,
        outstanding: totals.totalOutstanding,
        paymentCount: ''
      };

      tables = [{
        arr: [...exportData, totalRow],
        columns: [
          { key: 'billNumber', label: `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Number` },
          { key: 'date', label: 'Date' },
          { key: 'partyName', label: 'Party Name' },
          { key: 'openingBalance', label: 'Opening Balance' },
          { key: 'totalAmount', label: 'Total Amount' },
          { key: 'totalPaid', label: 'Total Paid' },
          { key: 'outstanding', label: 'Outstanding' },
          { key: 'paymentCount', label: 'Payment Count' }
        ],
        tableTitle: `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Bills`
      }];
      filename = `${activeTab}-bills`;
      title = `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Bills Report`;
    } else if (tableType === 'party-summary') {
    const summary = getPartyWiseSummary();
    const exportData = summary.map(party => ({
        partyName: party.partyName,
        openingBalance: party.openingBalance || 0,
        totalBills: party.totalBills,
        totalAmount: party.totalAmount,
        totalPaid: party.totalPaid,
        outstanding: party.outstanding
      }));

      // Calculate totals
      const totals = getPartyWiseTotals();
      const totalRow = {
        partyName: `TOTAL (${totals.totalParties} parties)`,
        openingBalance: totals.totalOpeningBalance,
        totalBills: totals.totalBills,
        totalAmount: totals.totalAmount,
        totalPaid: totals.totalPaid,
        outstanding: totals.totalOutstanding
      };

      tables = [{
        arr: [...exportData, totalRow],
        columns: [
          { key: 'partyName', label: 'Party Name' },
          { key: 'openingBalance', label: 'Opening Balance' },
          { key: 'totalBills', label: 'Total Bills' },
          { key: 'totalAmount', label: 'Total Amount' },
          { key: 'totalPaid', label: 'Total Paid' },
          { key: 'outstanding', label: 'Outstanding' }
        ],
        tableTitle: 'Party-wise Summary'
      }];
      filename = 'party-summary';
      title = 'Party-wise Summary Report';
    } else if (tableType === 'receipts') {
    const filteredReceipts = payments.filter(payment => {
      if (activeTab === 'receipts') {
        return payment.type === receiptsSubTab;
      }
      return payment.type === activeTab;
    });

    const exportData = filteredReceipts.map(payment => ({
        receiptNumber: payment.receiptNumber,
        date: convertTimestamp(payment.paymentDate),
        partyName: payment.partyName,
        amount: payment.totalAmount || 0,
        paymentMode: payment.paymentMode,
        paymentType: payment.paymentType === 'bill' ? 'Bill Payment' : 'Khata Payment',
        reference: payment.reference || '-',
        notes: payment.notes || '-'
      }));

      // Calculate totals
      const totals = getReceiptsTotals();
      const totalRow = {
        receiptNumber: `TOTAL (${totals.totalReceipts} receipts)`,
        date: '',
        partyName: '',
        amount: totals.totalAmount,
        paymentMode: '',
        paymentType: '',
        reference: '',
        notes: ''
      };

      tables = [{
        arr: [...exportData, totalRow],
        columns: [
          { key: 'receiptNumber', label: 'Receipt Number' },
          { key: 'date', label: 'Date' },
          { key: 'partyName', label: 'Party Name' },
          { key: 'amount', label: 'Amount' },
          { key: 'paymentMode', label: 'Payment Mode' },
          { key: 'paymentType', label: 'Payment Type' },
          { key: 'reference', label: 'Reference' },
          { key: 'notes', label: 'Notes' }
        ],
        tableTitle: `${activeTab === 'receipts' ? receiptsSubTab.charAt(0).toUpperCase() + receiptsSubTab.slice(1) : activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Receipts`
      }];
      filename = `${activeTab === 'receipts' ? receiptsSubTab : activeTab}-receipts`;
      title = `${activeTab === 'receipts' ? receiptsSubTab.charAt(0).toUpperCase() + receiptsSubTab.slice(1) : activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Receipts Report`;
    } else if (tableType === 'all') {
      // Export all tables together
      const bills = getFilteredBills();
      const summary = getPartyWiseSummary();
      const filteredReceipts = payments.filter(payment => {
        if (activeTab === 'receipts') {
          return payment.type === receiptsSubTab;
        }
        return payment.type === activeTab;
      });

      // Calculate totals for bills
      const billsTotals = getBillsTotals();
      const billsTotalRow = {
        billNumber: `TOTAL (${billsTotals.totalBills} bills)`,
        date: '',
        partyName: '',
        openingBalance: '',
        totalAmount: billsTotals.totalAmount,
        totalPaid: billsTotals.totalPaid,
        outstanding: billsTotals.totalOutstanding,
        paymentCount: ''
      };

      // Calculate totals for party summary
      const partyTotals = getPartyWiseTotals();
      const partyTotalRow = {
        partyName: `TOTAL (${partyTotals.totalParties} parties)`,
        openingBalance: partyTotals.totalOpeningBalance,
        totalBills: partyTotals.totalBills,
        totalAmount: partyTotals.totalAmount,
        totalPaid: partyTotals.totalPaid,
        outstanding: partyTotals.totalOutstanding
      };

      // Calculate totals for receipts
      const receiptsTotals = getReceiptsTotals();
      const receiptsTotalRow = {
        receiptNumber: `TOTAL (${receiptsTotals.totalReceipts} receipts)`,
        date: '',
        partyName: '',
        amount: receiptsTotals.totalAmount,
        paymentMode: '',
        paymentType: '',
        reference: '',
        notes: ''
      };

      tables = [
        {
          arr: [...bills.map(bill => {
            const party = parties.find(p => p.id === (bill.partyId || bill.party));
            const openingBalance = party?.openingBalance || 0;
            return {
            billNumber: getBillNumber(bill),
            date: convertTimestamp(bill[getBillDateField()]),
            partyName: getPartyName(bill.partyId || bill.party),
              openingBalance: openingBalance,
            totalAmount: bill.totalAmount || 0,
            totalPaid: bill.totalPaid || 0,
            outstanding: bill.outstanding || 0,
            paymentCount: bill.paymentCount || 0
            };
          }), billsTotalRow],
          columns: [
            { key: 'billNumber', label: `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Number` },
            { key: 'date', label: 'Date' },
            { key: 'partyName', label: 'Party Name' },
            { key: 'openingBalance', label: 'Opening Balance' },
            { key: 'totalAmount', label: 'Total Amount' },
            { key: 'totalPaid', label: 'Total Paid' },
            { key: 'outstanding', label: 'Outstanding' },
            { key: 'paymentCount', label: 'Payment Count' }
          ],
          tableTitle: `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Bills`
        },
        {
          arr: [...summary.map(party => ({
            partyName: party.partyName,
            openingBalance: party.openingBalance || 0,
            totalBills: party.totalBills,
            totalAmount: party.totalAmount,
            totalPaid: party.totalPaid,
            outstanding: party.outstanding
          })), partyTotalRow],
          columns: [
            { key: 'partyName', label: 'Party Name' },
            { key: 'openingBalance', label: 'Opening Balance' },
            { key: 'totalBills', label: 'Total Bills' },
            { key: 'totalAmount', label: 'Total Amount' },
            { key: 'totalPaid', label: 'Total Paid' },
            { key: 'outstanding', label: 'Outstanding' }
          ],
          tableTitle: 'Party-wise Summary'
        },
        {
          arr: [...filteredReceipts.map(payment => ({
            receiptNumber: payment.receiptNumber,
            date: convertTimestamp(payment.paymentDate),
            partyName: payment.partyName,
            amount: payment.totalAmount || 0,
            paymentMode: payment.paymentMode,
            paymentType: payment.paymentType === 'bill' ? 'Bill Payment' : 'Khata Payment',
            reference: payment.reference || '-',
            notes: payment.notes || '-'
          })), receiptsTotalRow],
          columns: [
            { key: 'receiptNumber', label: 'Receipt Number' },
            { key: 'date', label: 'Date' },
            { key: 'partyName', label: 'Party Name' },
            { key: 'amount', label: 'Amount' },
            { key: 'paymentMode', label: 'Payment Mode' },
            { key: 'paymentType', label: 'Payment Type' },
            { key: 'reference', label: 'Reference' },
            { key: 'notes', label: 'Notes' }
          ],
          tableTitle: `${activeTab === 'receipts' ? receiptsSubTab.charAt(0).toUpperCase() + receiptsSubTab.slice(1) : activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Receipts`
        }
      ];
      filename = `${activeTab}-complete-report`;
      title = `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Complete Report`;
    }

    // Export logic
    if (exportFormat === 'pdf') {
      await multiTablePDF(tables, filename + '.pdf', title, company);
    } else if (exportFormat === 'excel') {
      multiTableExcel(tables, filename + '.xlsx');
    } else if (exportFormat === 'csv') {
      await multiTableCSV(tables, filename);
    }
  };

  // Legacy export functions (for backward compatibility)
  const exportBillsData = (format) => {
    handleExport('bills');
  };
  
  const exportPartyWiseData = (format) => {
    handleExport('party-summary');
  };

  const exportReceiptsData = (format) => {
    handleExport('receipts');
  };

  const filteredBills = getFilteredBills();
  const partySummary = getPartyWiseSummary();

  // Bills table pagination hook - use new sorting system
  const sortedBills = getBillsSortedData(filteredBills);
  const billsPagination = useTablePagination(sortedBills, 10);

  // --- ADVANCE AGGREGATION AND USAGE LOGIC ---

  /**
   * Get total available advance for a party (carry-forward across years, minus used/refunded)
   * @param {string} partyId
   * @returns {number}
   */
  const getPartyAdvance = (partyId) => {
    return getPartyAdvanceUtil(partyId, payments);
  };

  /**
   * Allocate available advance to a new bill (FIFO: oldest advances first)
   * @param {string} partyId
   * @param {number} billAmount
   * @returns {Object} { allocatedAdvance, advanceAllocations: [{paymentId, amountUsed}], remainingBillAmount }
   */
  const allocateAdvanceToBill = (partyId, billAmount) => {
    return allocateAdvanceToBillUtil(partyId, billAmount, payments);
  };

  /**
   * Check if a payment is an advance payment
   * @param {Object} payment
   * @returns {boolean}
   */
  const isAdvancePayment = (payment) => {
    return isAdvancePaymentUtil(payment);
  };

  /**
   * Mark advances as used (update payment records with advanceUsed field)
   * @param {Array} allocations [{paymentId, amountUsed}]
   */
  const markAdvanceUsed = async (allocations) => {
    return markAdvanceUsedUtil(allocations, db, appId, userId, payments, companyId);
  };

  const reverseAdvanceUsed = async (allocations) => {
    return reverseAdvanceUsedUtil(allocations, db, appId, userId, payments, companyId);
  };

  /**
   * Refund an advance (set advanceRefunded flag)
   * @param {string} paymentId
   */
  const refundAdvance = async (paymentId) => {
    return refundAdvanceUtil(paymentId, db, appId, userId, companyId);
  };

  // Get advance details for a party
  const getAdvanceDetails = (partyId) => {
    return getAdvanceDetailsUtil(partyId, payments);
  };





  // Reset advance info and employee cash flow fields when payment modal is closed
  useEffect(() => {
    if (!showPaymentModal) {
      setShowAdvanceInfo(false);
      setAdvanceInfo({ collected: 0, used: 0 });
      setRecordInEmployeeCashFlow(false);
      setSelectedEmployeeForCashFlow('');
    }
  }, [showPaymentModal]);



  const tabTotals =
    activeTab === 'receipts'
      ? getReceiptsTotals()
      : activeTab !== 'mode'
        ? getBillsTotals()
        : null;

  return (
    <div className="p-4 sm:p-6 bg-slate-50 min-h-screen max-w-full overflow-x-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-400 to-sky-300 flex items-center justify-center text-white shadow-sm flex-shrink-0">
            <Wallet className="w-5 h-5" aria-hidden />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">{t('payments')}</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Track bills, receipts, party balances &amp; payment modes
            </p>
          </div>
        </div>
        <div className="md:hidden flex-shrink-0">
          <button
            type="button"
            onClick={async () => {
              const receiptNum = await generateReceiptNumber();
              setReceiptNumber(receiptNum);
              setShowPaymentModal(true);
              setSelectedBill(null);
            }}
            className="inline-flex items-center gap-1.5 w-full sm:w-auto justify-center bg-gradient-to-r from-blue-500 to-sky-400 text-white px-4 py-2.5 rounded-xl text-sm font-semibold shadow-sm hover:from-blue-600 hover:to-sky-500 transition-colors"
          >
            <Plus className="w-4 h-4" aria-hidden />
            Add FIFO Payment
          </button>
        </div>
      </div>

      {/* Main tabs */}
      <div className="mb-5">
        <div id="payments-tabs" className="hidden md:flex w-full bg-gradient-to-r from-sky-50/80 via-white to-blue-50/60 border border-sky-100/70 rounded-xl p-1.5 shadow-sm gap-1">
          {PAYMENT_MAIN_TABS.map(({ id, label, labelKey, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setActiveTab(id)}
              className={`flex-1 min-w-0 inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                activeTab === id
                  ? 'bg-white text-sky-700 shadow-sm ring-1 ring-sky-100'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-white/70'
              }`}
            >
              <Icon className="w-4 h-4 flex-shrink-0 opacity-80" aria-hidden />
              <span className="truncate">{labelKey ? t(labelKey) : label}</span>
            </button>
          ))}
        </div>

        <div className="md:hidden">
          <MobileScrollTabs
            tabs={PAYMENT_MAIN_TABS.map((tab) => ({
              ...tab,
              label: tab.labelKey ? t(tab.labelKey) : tab.label || tab.mobileLabel,
            }))}
            activeId={activeTab}
            onChange={setActiveTab}
            ariaLabel="Payment section"
            navClassName="bg-gradient-to-r from-sky-50/80 via-white to-blue-50/60 border border-sky-100/70 shadow-sm"
            getButtonClassName={(_, isActive) =>
              isActive
                ? 'bg-white text-sky-700 shadow-sm ring-1 ring-sky-100'
                : 'text-slate-600 hover:text-slate-900 hover:bg-white/70'
            }
          />
        </div>
      </div>

      {/* Receipt sub-tabs */}
      {activeTab === 'receipts' && (
        <div className="mb-5">
          <div
            id="payments-receipts-tabs"
            className="hidden md:flex w-full max-w-xl bg-gradient-to-r from-emerald-50/70 via-white to-teal-50/60 border border-emerald-100/70 rounded-xl p-1 gap-1"
          >
            {RECEIPT_SUB_TABS.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => setReceiptsSubTab(id)}
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                  receiptsSubTab === id
                    ? 'bg-white text-emerald-700 shadow-sm ring-1 ring-emerald-100'
                    : 'text-emerald-600/90 hover:bg-white/80'
                }`}
              >
                {label} {t('receipts')}
              </button>
            ))}
          </div>

          <div className="md:hidden">
            <MobileScrollTabs
              tabs={RECEIPT_SUB_TABS.map((tab) => ({
                ...tab,
                label: `${tab.label} ${t('receipts')}`,
              }))}
              activeId={receiptsSubTab}
              onChange={setReceiptsSubTab}
              ariaLabel="Receipt type"
              navClassName="max-w-xl bg-gradient-to-r from-emerald-50/70 via-white to-teal-50/60 border border-emerald-100/70"
              getButtonClassName={(_, isActive) =>
                isActive
                  ? 'bg-white text-emerald-700 shadow-sm ring-1 ring-emerald-100'
                  : 'text-emerald-600/90 hover:bg-white/80'
              }
            />
          </div>
        </div>
      )}

      {/* Compact Filter for Payment Mode tab */}
      {activeTab === 'mode' && (
        <CompactPaymentsFilter 
          parties={parties || []}
          paymentsFilter={paymentsFilter}
          onFiltersChange={() => {
            // Trigger re-render when filters change
            console.log('Filter change detected, triggering re-render');
            setFilterUpdateTrigger(prev => prev + 1);
          }}
          className="mb-6"
        />
      )}


      {/* Payment Mode Summary (new tab) */}
      {activeTab === 'mode' && (
        <div className="bg-white p-4 sm:p-5 rounded-xl shadow-sm border border-slate-200/60 mb-6" id="payments-header-actions">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pb-4 border-b border-slate-100">
            <div className="flex items-center gap-2 min-w-0">
              <BarChart3 className="w-5 h-5 text-blue-600 flex-shrink-0" aria-hidden />
              <h3 className="text-lg font-bold text-slate-900">Payment Mode</h3>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              {/* New Unified Export System */}
              <GlobalExportButton
                data={getPaymentModeData()}
                config={{
                  title: 'Payment Mode Report',
                  filename: `payment-mode-${new Date().toISOString().split('T')[0]}`,
                  type: 'table',
                  headers: ['Payment Mode', 'Total Transactions', 'Net Balance', 'Received', 'Paid', 'Expenses'],
                  columns: ['mode', 'count', 'amount', 'received', 'paid', 'expenses']
                }}
                onExportComplete={(format, result) => {
                  console.log(`Payment mode data exported as ${format}:`, result);
                }}
                onExportError={(format, error) => {
                  console.error(`Export failed for ${format}:`, error);
                }}
              />

              {/* Legacy Share Button */}
              <ShareButton
                onExportPDF={() => sharePaymentsAsPDF('all')}
                onExportExcel={() => sharePaymentsAsExcel('all')}
                onExportImage={() => sharePaymentsAsImage('payments-header-actions', 'payments-mode.png')}
                onShareLink={() => sharePaymentsLink(() => {
                  const table = document.querySelector('#payments-header-actions')?.parentElement?.parentElement;
                  const html = table ? table.outerHTML : '<div>No data</div>';
                  return `<!DOCTYPE html><html><head><meta charset=\"utf-8\"/><meta name=\"viewport\" content=\"width=device-width, initial-scale=1\"/><title>Payment Mode</title><style>body{font-family:Arial;padding:16px;background:#f9f9f9} .header{text-align:center;margin-bottom:20px;padding:20px;background:white;border-radius:8px;box-shadow:0 2px 4px rgba(0,0,0,0.1)} .company-name{font-size:24px;font-weight:bold;color:#333;margin-bottom:8px} .company-details{font-size:14px;color:#666;margin-bottom:4px} .separator{border-top:2px solid #e0e0e0;margin:15px 0} .footer{text-align:center;margin-top:30px;padding:15px;background:white;border-radius:8px;box-shadow:0 2px 4px rgba(0,0,0,0.1);color:#888;font-size:12px} table{width:100%;border-collapse:collapse;background:white;border-radius:8px;overflow:hidden;box-shadow:0 2px 4px rgba(0,0,0,0.1)} th,td{border:1px solid #ddd;padding:8px;text-align:left} th{background:#4285f4;color:white;font-weight:bold}</style></head><body><div class=\"header\"><div class=\"company-name\">${company?.firmName||'Company Name'}</div>${company?.address ? `<div class=\"company-details\">${company.address}</div>` : ''}${company?.gstin ? `<div class=\"company-details\">GSTIN: ${company.gstin}</div>` : ''}${company?.contactNumber ? `<div class=\"company-details\">Phone: ${company.contactNumber}</div>` : ''}<div class=\"separator\"></div><h2 style=\"margin:0;color:#333\">Payment Mode Report</h2><div style=\"font-size:12px;color:#666;margin-top:8px\">Generated On: ${new Date().toLocaleDateString('en-IN')}</div></div>${html}<div class=\"footer\">Use www.acctoo.com, Simplifying Accounting, Together</div></body></html>`;
                }, 'payments-mode')}
              />
            </div>
          </div>
          {/* 1. Payment Received (Sales/PRI) */}
          <div className="mb-6 rounded-xl border border-sky-100 bg-sky-50/30 overflow-hidden">
            <h4 className="font-semibold text-sky-900 px-4 py-3 bg-sky-50 border-b border-sky-100">1) Payment Received (Sales/PRI)</h4>
            <div className="md:hidden divide-y divide-sky-100">
              {modeSummary.map((m, i) => (
                <div key={`rec-mob-${i}`} className="flex items-center justify-between gap-3 px-4 py-3 bg-white/60">
                  <span className="font-medium text-slate-800">{m.mode}</span>
                  <div className="text-right shrink-0">
                    <button onClick={() => { setModeModalType('sales'); setModeModalTitle(`${m.mode} - Sales Receipts (${m.salesCount||0})`); setModeModalRowsSales(m.receiptRowsSales||[]); setModeModalRowsPurchase([]); setModeModalRowsExpense([]); setShowModeModal(true); }} className="text-xs text-blue-600 hover:underline touch-manipulation">{m.salesCount||0} receipts</button>
                    <div className="font-semibold text-slate-900 tabular-nums">{(m.salesTotal||0).toLocaleString('en-IN')}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50"><tr><th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Mode</th><th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Receipts</th><th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Total</th></tr></thead>
              <tbody>
                {modeSummary.map((m,i)=> (
                  <tr key={`rec-${i}`} className="border-t">
                    <td className="px-4 py-2">{m.mode}</td>
                    <td className="px-4 py-2 text-right"><button onClick={()=>{ setModeModalType('sales'); setModeModalTitle(`${m.mode} - Sales Receipts (${m.salesCount||0})`); setModeModalRowsSales(m.receiptRowsSales||[]); setModeModalRowsPurchase([]); setModeModalRowsExpense([]); setShowModeModal(true); }} className="text-xs text-blue-600 hover:underline">{m.salesCount||0} receipts</button></td>
                    <td className="px-4 py-2 text-right">{(m.salesTotal||0).toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>

          {/* 2. Payment Paid (Purchase/PRP) */}
          <div className="mb-6 rounded-xl border border-amber-100 bg-amber-50/30 overflow-hidden">
            <h4 className="font-semibold text-amber-900 px-4 py-3 bg-amber-50 border-b border-amber-100">2) Payment Paid (Purchase/PRP)</h4>
            <div className="md:hidden divide-y divide-amber-100">
              {modeSummary.map((m, i) => (
                <div key={`paid-mob-${i}`} className="flex items-center justify-between gap-3 px-4 py-3 bg-white/60">
                  <span className="font-medium text-slate-800">{m.mode}</span>
                  <div className="text-right shrink-0">
                    <button onClick={() => { setModeModalType('purchase'); setModeModalTitle(`${m.mode} - Purchase Receipts (${m.purchasePaidCount||0})`); setModeModalRowsSales([]); setModeModalRowsPurchase(m.receiptRowsPurchase||[]); setModeModalRowsExpense([]); setShowModeModal(true); }} className="text-xs text-blue-600 hover:underline touch-manipulation">{m.purchasePaidCount||0} receipts</button>
                    <div className="font-semibold text-slate-900 tabular-nums">{(m.purchasePaidTotal||0).toLocaleString('en-IN')}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50"><tr><th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Mode</th><th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Receipts</th><th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Total</th></tr></thead>
              <tbody>
                {modeSummary.map((m,i)=> (
                  <tr key={`paid-${i}`} className="border-t">
                    <td className="px-4 py-2">{m.mode}</td>
                    <td className="px-4 py-2 text-right"><button onClick={()=>{ setModeModalType('purchase'); setModeModalTitle(`${m.mode} - Purchase Receipts (${m.purchasePaidCount||0})`); setModeModalRowsSales([]); setModeModalRowsPurchase(m.receiptRowsPurchase||[]); setModeModalRowsExpense([]); setShowModeModal(true); }} className="text-xs text-blue-600 hover:underline">{m.purchasePaidCount||0} receipts</button></td>
                    <td className="px-4 py-2 text-right">{(m.purchasePaidTotal||0).toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>

          {/* 3. Expense */}
          <div className="mb-6 rounded-xl border border-violet-100 bg-violet-50/30 overflow-hidden">
            <h4 className="font-semibold text-violet-900 px-4 py-3 bg-violet-50 border-b border-violet-100">3) Expense</h4>
            <div className="md:hidden divide-y divide-violet-100">
              {modeSummary.map((m, i) => (
                <div key={`exp-mob-${i}`} className="flex items-center justify-between gap-3 px-4 py-3 bg-white/60">
                  <span className="font-medium text-slate-800">{m.mode}</span>
                  <div className="text-right shrink-0">
                    <button onClick={() => { setModeModalType('expense'); setModeModalTitle(`${m.mode} - Expenses (${m.expenseCount||0})`); setModeModalRowsSales([]); setModeModalRowsPurchase([]); setModeModalRowsExpense(m.expenseRows||[]); setShowModeModal(true); }} className="text-xs text-blue-600 hover:underline touch-manipulation">{m.expenseCount||0} entries</button>
                    <div className="font-semibold text-slate-900 tabular-nums">{(m.expenseTotal||0).toLocaleString('en-IN')}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50"><tr><th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Mode</th><th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Entries</th><th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Total</th></tr></thead>
              <tbody>
                {modeSummary.map((m,i)=> (
                  <tr key={`exp-${i}`} className="border-t">
                    <td className="px-4 py-2">{m.mode}</td>
                    <td className="px-4 py-2 text-right"><button onClick={()=>{ setModeModalType('expense'); setModeModalTitle(`${m.mode} - Expenses (${m.expenseCount||0})`); setModeModalRowsSales([]); setModeModalRowsPurchase([]); setModeModalRowsExpense(m.expenseRows||[]); setShowModeModal(true); }} className="text-xs text-blue-600 hover:underline">{m.expenseCount||0} entries</button></td>
                    <td className="px-4 py-2 text-right">{(m.expenseTotal||0).toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>

          {/* 4. Tax Payments (treated as expenses) */}
          <div className="mb-6 rounded-xl border border-purple-100 bg-purple-50/30 overflow-hidden">
            <h4 className="font-semibold text-purple-900 px-4 py-3 bg-purple-50 border-b border-purple-100">4) Tax Payments</h4>
            <div className="md:hidden divide-y divide-purple-100">
              {modeSummary.map((m, i) => {
                const count = m.taxPaymentsCount || 0;
                const total = m.taxPaymentsTotal || 0;
                return (
                  <div key={`tax-mob-${i}`} className="flex items-center justify-between gap-3 px-4 py-3 bg-white/60">
                    <span className="font-medium text-slate-800">{m.mode}</span>
                    <div className="text-right shrink-0">
                      <span className="text-xs text-slate-500">{count} entries</span>
                      <div className="font-semibold text-slate-900 tabular-nums">{total.toLocaleString('en-IN')}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50"><tr><th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Mode</th><th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Entries</th><th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Total</th></tr></thead>
              <tbody>
                {modeSummary.map((m,i)=>{
                  const count = m.taxPaymentsCount || 0;
                  const total = m.taxPaymentsTotal || 0;
                  return (
                    <tr key={`tax-${i}`} className="border-t">
                      <td className="px-4 py-2">{m.mode}</td>
                      <td className="px-4 py-2 text-right">
                        <button onClick={()=>{ setModeModalType('tax'); setModeModalTitle(`${m.mode} - Tax Payments (${count})`); setModeModalRowsSales([]); setModeModalRowsPurchase([]); setModeModalRowsExpense([]); setModeModalRowsTax(m.taxPaymentsRows||[]); setShowModeModal(true); }} className="text-xs text-blue-600 hover:underline">{count} entries</button>
                      </td>
                      <td className="px-4 py-2 text-right">{total.toLocaleString('en-IN')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>

          {/* 5. Balance (Cash and Combined Bank) */}
          <div className="rounded-xl border border-emerald-100 bg-emerald-50/30 overflow-hidden">
            <h4 className="font-semibold text-emerald-900 px-4 py-3 bg-emerald-50 border-b border-emerald-100">5) Balance</h4>
            {(() => {
              const byMode = modeSummary.map(m => ({ mode: m.mode, balance: (m.salesTotal||0) - (m.purchasePaidTotal||0) - (m.expenseTotal||0) }));
              const cashRow = byMode.find(r => r.mode === 'Cash');
              const bankCombined = byMode.filter(r => r.mode !== 'Cash').reduce((s,r)=>s+r.balance,0);
              return (
                <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50"><tr><th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Mode</th><th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Balance</th></tr></thead>
                  <tbody>
                    <tr className="border-t"><td className="px-4 py-2">Cash</td><td className="px-4 py-2 text-right">{((cashRow?.balance)||0).toLocaleString('en-IN')}</td></tr>
                    <tr className="border-t"><td className="px-4 py-2">Bank Transfer (UPI + Bank + Cheque/DD)</td><td className="px-4 py-2 text-right">{bankCombined.toLocaleString('en-IN')}</td></tr>
                  </tbody>
                </table>
                </div>
              );
            })()}
          </div>
          <p className="text-xs text-slate-500 mt-4 px-1">
            Modes: Cash, UPI, Cheque/DD (clubbed), Bank Transfer (others). Tap receipt counts to view details.
          </p>
        </div>
      )}

      {showModeModal && (
        <div className={modalOverlayClass}>
      <div className={`${modalPanelClass} max-w-2xl p-5 sm:p-6 relative`} onKeyDown={(e)=>{ if (e.key==='Escape') { setShowModeModal(false);} }} tabIndex={0}>
            <button type="button" className="absolute top-3 right-3 p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors" onClick={()=>setShowModeModal(false)} aria-label="Close">
              <X className="w-5 h-5" />
            </button>
            <h4 className="text-lg font-bold text-slate-900 mb-4 pr-8">{modeModalTitle}</h4>
            <div className="max-h-96 overflow-y-auto">
              {modeModalType === 'sales' && (
                <div className="mb-4">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50"><tr><th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-left">Receipt No</th><th className="px-4 py-2 text-left">Party</th><th className="px-4 py-2 text-right">Amount</th></tr></thead>
                    <tbody>
                      {(modeModalRowsSales||[]).map((r,idx)=> (
                        <tr key={r.id||idx} className="border-t"><td className="px-4 py-2">{r.date||''}</td><td className="px-4 py-2">{r.receiptNumber||''}</td><td className="px-4 py-2">{r.partyName||''}</td><td className="px-4 py-2 text-right">{(r.amount||0).toLocaleString('en-IN')}</td></tr>
                      ))}
                      {(!modeModalRowsSales || modeModalRowsSales.length===0) && (<tr><td className="px-4 py-6 text-gray-500" colSpan={4}>No invoice receipts</td></tr>)}
                    </tbody>
                  </table>
                </div>
              )}
              {modeModalType === 'purchase' && (
                <div>
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50"><tr><th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-left">Receipt No</th><th className="px-4 py-2 text-left">Party</th><th className="px-4 py-2 text-right">Amount</th></tr></thead>
                    <tbody>
                      {(modeModalRowsPurchase||[]).map((r,idx)=> (
                        <tr key={r.id||idx} className="border-t"><td className="px-4 py-2">{r.date||''}</td><td className="px-4 py-2">{r.receiptNumber||''}</td><td className="px-4 py-2">{r.partyName||''}</td><td className="px-4 py-2 text-right">{(r.amount||0).toLocaleString('en-IN')}</td></tr>
                      ))}
                      {(!modeModalRowsPurchase || modeModalRowsPurchase.length===0) && (<tr><td className="px-4 py-6 text-gray-500" colSpan={4}>No purchase receipts</td></tr>)}
                    </tbody>
                  </table>
                </div>
              )}
              {modeModalType === 'expense' && (
                <div className="mt-6">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50"><tr><th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-left">Head/Note</th><th className="px-4 py-2 text-right">Amount</th></tr></thead>
                    <tbody>
                      {(modeModalRowsExpense||[]).map((r,idx)=> (
                        <tr key={r.id||idx} className="border-t"><td className="px-4 py-2">{r.date||''}</td><td className="px-4 py-2">{r.receiptNumber||''}</td><td className="px-4 py-2 text-right">{(r.amount||0).toLocaleString('en-IN')}</td></tr>
                      ))}
                      {(!modeModalRowsExpense || modeModalRowsExpense.length===0) && (<tr><td className="px-4 py-6 text-gray-500" colSpan={3}>No expenses</td></tr>)}
                    </tbody>
                  </table>
                </div>
              )}

              {modeModalType === 'tax' && (
                <div className="mt-6">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50"><tr><th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-left">Details</th><th className="px-4 py-2 text-right">Amount</th></tr></thead>
                    <tbody>
                      {(modeModalRowsTax||[]).map((r,idx)=> (
                        <tr key={r.id||idx} className="border-t"><td className="px-4 py-2">{r.date||''}</td><td className="px-4 py-2">{r.receiptNumber||''}</td><td className="px-4 py-2 text-right">{(r.amount||0).toLocaleString('en-IN')}</td></tr>
                      ))}
                      {(!modeModalRowsTax || modeModalRowsTax.length===0) && (<tr><td className="px-4 py-6 text-gray-500" colSpan={3}>No tax payments</td></tr>)}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Compact Filter for all tabs except mode */}
      {activeTab !== 'mode' && (
        <CompactPaymentsFilter 
          parties={parties || []}
          paymentsFilter={paymentsFilter}
          onFiltersChange={() => {
            // Trigger re-render when filters change
            console.log('Filter change detected, triggering re-render');
            setFilterUpdateTrigger(prev => prev + 1);
          }}
          className="mb-4"
        />
      )}

      {/* Quick summary — collapsed by default for customer-facing use */}
      {tabTotals && (
        <div className="mb-6">
          <button
            type="button"
            onClick={() => setShowTabTotals((prev) => !prev)}
            className="w-full sm:w-auto min-w-[200px] bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 px-4 py-2.5 rounded-lg flex items-center justify-between gap-3 font-medium text-sm transition-colors"
            aria-expanded={showTabTotals}
          >
            <span className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              {showTabTotals ? 'Hide Summary' : 'Show Summary'}
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showTabTotals ? 'rotate-180' : ''}`} />
          </button>

          {showTabTotals && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
              {activeTab === 'receipts' ? (
                <>
                  <div className="bg-white rounded-xl border border-slate-200/80 p-4 shadow-sm">
                    <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">Receipts</div>
                    <div className="text-xl font-bold text-slate-900 mt-1">{tabTotals.totalReceipts}</div>
                  </div>
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-100 p-4 shadow-sm col-span-2 lg:col-span-3">
                    <div className="text-[11px] font-medium text-emerald-700 uppercase tracking-wide">Total received</div>
                    <div className="text-xl font-bold text-emerald-800 mt-1">
                      ₹{tabTotals.totalAmount.toLocaleString('en-IN')}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-white rounded-xl border border-slate-200/80 p-4 shadow-sm">
                    <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">Bills</div>
                    <div className="text-xl font-bold text-slate-900 mt-1">{tabTotals.totalBills}</div>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200/80 p-4 shadow-sm">
                    <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">Bill amount</div>
                    <div className="text-lg sm:text-xl font-bold text-blue-800 mt-1 truncate">
                      ₹{tabTotals.totalAmount.toLocaleString('en-IN')}
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100 p-4 shadow-sm">
                    <div className="text-[11px] font-medium text-green-700 uppercase tracking-wide">Paid</div>
                    <div className="text-lg sm:text-xl font-bold text-green-700 mt-1 truncate">
                      ₹{tabTotals.totalPaid.toLocaleString('en-IN')}
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl border border-red-100 p-4 shadow-sm">
                    <div className="text-[11px] font-medium text-red-700 uppercase tracking-wide">Outstanding</div>
                    <div className="text-lg sm:text-xl font-bold text-red-700 mt-1 truncate">
                      ₹{tabTotals.totalOutstanding.toLocaleString('en-IN')}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Main Content - Bills and Party Summary (hide in Payment Mode) */}
      {activeTab !== 'receipts' && activeTab !== 'mode' && (
        <>
          {/* Overall Records Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6 border border-slate-200/60" id="payments-header-actions">
        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-sky-50 via-white to-amber-50">
          <div className="flex justify-between items-center gap-3">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 min-w-0">
              <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <span className="truncate">Overall {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Records</span>
            </h2>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
              <ExportShareDropdown
                onExportPDF={() => { setExportFormat('pdf'); handleExport('bills'); }}
                onExportExcel={() => { setExportFormat('excel'); handleExport('bills'); }}
                onExportCSV={() => { setExportFormat('csv'); handleExport('bills'); }}
                onExportAll={() => { setExportFormat('csv'); handleExport('all'); }}
                onSharePDF={() => sharePaymentsAsPDF('bills')}
                onShareExcel={() => sharePaymentsAsExcel('bills')}
                onShareImage={() => sharePaymentsAsImage('payments-header-actions', 'payments-bills.png')}
                exportFormat={exportFormat}
              />
            </div>
          </div>
        </div>
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50/95 sticky top-0 z-10">
              <tr>
                <SortableHeader columnKey="number" label={`${activeTab.toUpperCase()} NUMBER`} onSort={handleBillsSort} sortConfig={billsSortConfig} className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:text-blue-600" />
                <SortableHeader columnKey="partyName" label="PARTY NAME" onSort={handleBillsSort} sortConfig={billsSortConfig} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-blue-600" />
                <SortableHeader columnKey="openingBalance" label="OPENING BALANCE" onSort={handleBillsSort} sortConfig={billsSortConfig} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-blue-600" />
                <SortableHeader columnKey="totalAmount" label="TOTAL AMOUNT" onSort={handleBillsSort} sortConfig={billsSortConfig} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-blue-600" />
                <SortableHeader columnKey="totalPaid" label="TOTAL PAID" onSort={handleBillsSort} sortConfig={billsSortConfig} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-blue-600" />
                <SortableHeader columnKey="outstanding" label="OUTSTANDING" onSort={handleBillsSort} sortConfig={billsSortConfig} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-blue-600" />
                <SortableHeader columnKey="paymentCount" label="PAYMENT RECEIPTS" onSort={handleBillsSort} sortConfig={billsSortConfig} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-blue-600" />
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ACTIONS
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {billsPagination.currentData.map((bill) => (
                <tr key={bill.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {getBillNumber(bill)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getPartyName(bill.partyId || bill.party)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {(() => {
                      const party = parties.find(p => p.id === (bill.partyId || bill.party));
                      const openingBalance = party?.openingBalance || 0;
                      return (
                        <span className={`font-medium ${
                          openingBalance > 0 ? 'text-green-600' : 
                          openingBalance < 0 ? 'text-red-600' : 
                          'text-gray-600'
                        }`}>
                          {openingBalance > 0 ? `+${formatCurrency(openingBalance, companyCurrency)}` : 
                           openingBalance < 0 ? formatCurrency(openingBalance, companyCurrency) : 
                           formatCurrency(0, companyCurrency)}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(parseFloat(bill.totalAmount || 0), companyCurrency)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(parseFloat(bill.totalPaid || 0), companyCurrency)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className={`font-medium ${bill.outstanding > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatCurrency(parseFloat(bill.outstanding || 0), companyCurrency)}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    <button
                      type="button"
                      onClick={() => handleViewPaymentDetails(bill)}
                      className={btnSecondarySm}
                      title={t('viewPaymentReceipts')}
                    >
                      Receipts ({bill.paymentCount || 0})
                    </button>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    <button
                      type="button"
                      id="add-payment-button"
                      onClick={() => handleAddPayment(bill)}
                      className={btnPrimarySm}
                    >
                      Add Payment
                    </button>
                  </td>
                </tr>
              ))}
              {/* Totals Row */}
              {showTabTotals && (() => {
                const totals = getBillsTotals();
                return (
                  <tr className="bg-gray-100 font-semibold">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <strong>Total ({totals.totalBills} bills)</strong>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <strong>-</strong>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <strong>-</strong>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <strong>₹{totals.totalAmount.toLocaleString('en-IN')}</strong>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <strong>₹{totals.totalPaid.toLocaleString('en-IN')}</strong>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <strong className="text-red-600">₹{totals.totalOutstanding.toLocaleString('en-IN')}</strong>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <strong>-</strong>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <strong>-</strong>
                    </td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden">
          <div className="p-3 space-y-3">
            {billsPagination.currentData.map((bill) => {
              const isExpanded = expandedBills.has(bill.id);
              const partyName = getPartyName(bill.partyId || bill.party);
              const openingBalance = (() => {
                const party = parties.find(p => p.id === (bill.partyId || bill.party));
                return party?.openingBalance || 0;
              })();

              return (
                <div
                  key={bill.id}
                  className="bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200/90 shadow-sm overflow-hidden"
                >
                  <div
                    className="p-4 cursor-pointer active:bg-slate-50/80 transition-colors"
                    onClick={() => toggleBillExpansion(bill.id)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-600 to-amber-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-md">
                        {(partyName || getBillNumber(bill) || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-gray-900 truncate">{getBillNumber(bill)}</div>
                        <div className="text-xs text-gray-500 truncate flex items-center gap-1 mt-0.5">
                          <User className="w-3 h-3 flex-shrink-0" />
                          {partyName || '—'}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                        <span
                          className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                            bill.outstanding > 0 ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'
                          }`}
                        >
                          {bill.outstanding > 0 ? 'Outstanding' : 'Paid'}
                        </span>
                        <div className={`text-right ${bill.outstanding > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                          <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Due</div>
                          <div className="text-base font-bold leading-tight">
                            {formatCurrency(parseFloat(bill.outstanding || 0), companyCurrency)}
                          </div>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-gray-400" aria-hidden />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400" aria-hidden />
                        )}
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-4 pb-4 pt-0 border-t border-slate-100 bg-slate-50/40">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 my-3">
                        <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                          <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                            <Wallet className="w-3 h-3" /> Opening
                          </div>
                          <div
                            className={`font-semibold ${
                              openingBalance > 0
                                ? 'text-green-600'
                                : openingBalance < 0
                                  ? 'text-red-600'
                                  : 'text-gray-800'
                            }`}
                          >
                            {openingBalance > 0
                              ? `+${formatCurrency(openingBalance, companyCurrency)}`
                              : openingBalance < 0
                                ? formatCurrency(openingBalance, companyCurrency)
                                : formatCurrency(0, companyCurrency)}
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-sky-50 to-blue-50 p-3 rounded-lg border border-blue-100 shadow-sm">
                          <div className="flex items-center gap-1 text-xs text-blue-600 mb-1 font-medium">
                            <FileText className="w-3 h-3" /> Bill total
                          </div>
                          <div className="font-bold text-blue-800">
                            {formatCurrency(parseFloat(bill.totalAmount || 0), companyCurrency)}
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-3 rounded-lg border border-green-200 shadow-sm">
                          <div className="flex items-center gap-1.5 text-xs text-green-700 font-medium mb-1">
                            <TrendingUp className="w-3.5 h-3.5" /> Paid
                          </div>
                          <div className="font-bold text-green-600 text-base">
                            {formatCurrency(parseFloat(bill.totalPaid || 0), companyCurrency)}
                          </div>
                        </div>
                        <div
                          className={`p-3 rounded-lg border shadow-sm ${
                            bill.outstanding > 0
                              ? 'bg-gradient-to-br from-red-50 to-rose-50 border-red-200'
                              : 'bg-gradient-to-br from-emerald-50 to-green-50 border-green-200'
                          }`}
                        >
                          <div
                            className={`flex items-center gap-1.5 text-xs font-medium mb-1 ${
                              bill.outstanding > 0 ? 'text-red-700' : 'text-green-700'
                            }`}
                          >
                            <TrendingDown className="w-3.5 h-3.5" /> Outstanding
                          </div>
                          <div
                            className={`font-bold text-base ${
                              bill.outstanding > 0 ? 'text-red-600' : 'text-green-600'
                            }`}
                          >
                            {formatCurrency(parseFloat(bill.outstanding || 0), companyCurrency)}
                          </div>
                        </div>
                      </div>

                      <div className="bg-white/90 p-3 rounded-lg border border-gray-100 shadow-sm mb-3">
                        <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                          <Receipt className="w-3 h-3" /> Payment receipts
                        </div>
                        <div className="font-semibold text-gray-900">{bill.paymentCount || 0}</div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewPaymentDetails(bill);
                          }}
                          className="flex-1 rounded-xl bg-gradient-to-r from-blue-500 to-sky-400 text-white px-3 py-2.5 text-sm font-semibold shadow-sm hover:from-blue-600 hover:to-sky-500 transition-colors"
                        >
                          View receipts
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddPayment(bill);
                          }}
                          className="flex-1 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-3 py-2.5 text-sm font-semibold shadow-sm hover:from-emerald-700 hover:to-teal-700 transition-colors"
                        >
                          Add payment
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {showTabTotals && (() => {
              const totals = getBillsTotals();
              return (
                <div className="bg-gradient-to-br from-slate-100 to-slate-50 rounded-xl p-4 border border-slate-200 shadow-sm">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-600" />
                    Total ({totals.totalBills} bills)
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="bg-white/80 p-3 rounded-lg border border-slate-100">
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">Total amount</div>
                      <div className="text-sm font-bold text-gray-900">
                        ₹{totals.totalAmount.toLocaleString('en-IN')}
                      </div>
                    </div>
                    <div className="bg-white/80 p-3 rounded-lg border border-slate-100">
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">Paid</div>
                      <div className="text-sm font-bold text-emerald-700">
                        ₹{totals.totalPaid.toLocaleString('en-IN')}
                      </div>
                    </div>
                    <div className="sm:col-span-2 bg-gradient-to-r from-red-50 to-rose-50 p-3 rounded-lg border border-red-100">
                      <div className="text-[10px] text-red-700 uppercase tracking-wide mb-0.5 font-medium">
                        Outstanding
                      </div>
                      <div className="text-sm font-bold text-red-600">
                        ₹{totals.totalOutstanding.toLocaleString('en-IN')}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
        <PaginationControls {...billsPagination} />
      </div>

      {/* Party-wise Summary Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-slate-200/60">
        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 via-white to-amber-50">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0 gap-3">
            <h2 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2 min-w-0">
              <Users className="w-5 h-5 text-indigo-600 flex-shrink-0" />
              <span className="truncate">Party-wise Summary</span>
            </h2>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">

              <button
                type="button"
                onClick={() => {
                  paymentsFilter.resetFilters();
                }}
                className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 border border-slate-200 w-full sm:w-auto transition-colors"
              >
                Clear All
              </button>
              <ExportShareDropdown
                onExportPDF={() => { setExportFormat('pdf'); handleExport('party-summary'); }}
                onExportExcel={() => { setExportFormat('excel'); handleExport('party-summary'); }}
                onExportCSV={() => { setExportFormat('csv'); handleExport('party-summary'); }}
                onExportAll={() => { setExportFormat('csv'); handleExport('all'); }}
                onSharePDF={() => sharePaymentsAsPDF('party-summary')}
                onShareExcel={() => sharePaymentsAsExcel('party-summary')}
                onShareImage={() => sharePaymentsAsImage('payments-header-actions', 'payments-party-summary.png')}
                exportFormat={exportFormat}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white px-3 py-1.5 rounded text-xs transition duration-300 relative w-full sm:w-auto"
              />
            </div>
          </div>
        </div>
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader columnKey="partyName" label="PARTY NAME" onSort={handleNewSort} sortConfig={newSortConfig} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-blue-600" />
                <SortableHeader columnKey="openingBalance" label="OPENING BALANCE" onSort={handleNewSort} sortConfig={newSortConfig} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-blue-600" />
                <SortableHeader columnKey="totalBills" label="TOTAL BILLS" onSort={handleNewSort} sortConfig={newSortConfig} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-blue-600" />
                <SortableHeader columnKey="totalAmount" label="TOTAL AMOUNT" onSort={handleNewSort} sortConfig={newSortConfig} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-blue-600" />
                <SortableHeader columnKey="totalPaid" label="TOTAL PAID" onSort={handleNewSort} sortConfig={newSortConfig} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-blue-600" />
                <SortableHeader columnKey="outstanding" label="OUTSTANDING" onSort={handleNewSort} sortConfig={newSortConfig} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-blue-600" />
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ADVANCE
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ACTIONS
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pagination.currentData.map((party) => {
                const advance = getPartyAdvance(party.partyId);
                return (
                <tr key={party.partyId} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {party.partyName}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className={`font-medium ${
                      party.openingBalance > 0 ? 'text-green-600' : 
                      party.openingBalance < 0 ? 'text-red-600' : 
                      'text-gray-600'
                    }`}>
                      {party.openingBalance > 0 ? `+₹${party.openingBalance.toLocaleString('en-IN')}` : 
                       party.openingBalance < 0 ? `₹${party.openingBalance.toLocaleString('en-IN')}` : 
                       '₹0'}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {party.totalBills}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    ₹{parseFloat(party.totalAmount || 0).toLocaleString('en-IN')}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    ₹{parseFloat(party.totalPaid || 0).toLocaleString('en-IN')}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className={`font-medium ${party.outstanding > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      ₹{parseFloat(party.outstanding || 0).toLocaleString('en-IN')}
                    </span>
                  </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {advance > 0 ? (
                        <button
                          className="text-blue-600 underline hover:text-blue-800 font-semibold"
                          onClick={() => setAdvanceModalParty(party.partyId)}
                        >
                          ₹{advance.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </button>
                      ) : (
                        <span className="text-gray-400">₹0</span>
                      )}
                    </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        onClick={() => {
                          paymentsFilter.setSelectedParty(party.partyId);
                          setActiveTab('invoice');
                        }}
                        className={btnSecondarySm}
                      >
                        View Bills
                      </button>
                      {party.outstanding > 0 && (
                        <button
                          type="button"
                          onClick={() => handleAddKhataPayment(party.partyId)}
                          className={btnSuccessSm}
                        >
                          Add FIFO
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
              {/* Totals Row */}
              {showTabTotals && (() => {
                const totals = getPartyWiseTotals();
                return (
                  <tr className="bg-gray-100 font-semibold">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <strong>Total ({totals.totalParties} parties)</strong>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <strong className={
                        totals.totalOpeningBalance > 0 ? 'text-green-600' : 
                        totals.totalOpeningBalance < 0 ? 'text-red-600' : 
                        'text-gray-900'
                      }>
                        {totals.totalOpeningBalance > 0 ? `+₹${totals.totalOpeningBalance.toLocaleString('en-IN')}` : 
                         totals.totalOpeningBalance < 0 ? `₹${totals.totalOpeningBalance.toLocaleString('en-IN')}` : 
                         '₹0'}
                      </strong>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <strong>{totals.totalBills}</strong>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <strong>₹{totals.totalAmount.toLocaleString('en-IN')}</strong>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <strong>₹{totals.totalPaid.toLocaleString('en-IN')}</strong>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <strong className="text-red-600">₹{totals.totalOutstanding.toLocaleString('en-IN')}</strong>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <strong>-</strong>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <strong>-</strong>
                    </td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
          
          {/* Pagination Controls */}
          <PaginationControls {...pagination} />
        </div>
        
        {/* Mobile Card View */}
        <div className="md:hidden">
          <div className="p-3 space-y-3">
            {pagination.currentData.map((party) => {
              const isExpanded = expandedParties.has(party.partyId);
              const advance = getPartyAdvance(party.partyId);

              return (
                <div
                  key={party.partyId}
                  className="bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
                >
                  <div
                    className="p-4 cursor-pointer active:bg-gray-50/80 transition-colors"
                    onClick={() => togglePartyExpansion(party.partyId)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-md">
                        {(party.partyName || 'P').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-gray-900 truncate">{party.partyName}</div>
                        <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                          <FileText className="w-3 h-3 flex-shrink-0" />
                          {party.totalBills} bill{party.totalBills === 1 ? '' : 's'}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                        <span
                          className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                            party.outstanding > 0 ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'
                          }`}
                        >
                          {party.outstanding > 0 ? 'Outstanding' : 'Paid'}
                        </span>
                        <div className={`text-right ${party.outstanding > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                          <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Due</div>
                          <div className="text-base font-bold leading-tight">
                            ₹{parseFloat(party.outstanding || 0).toLocaleString('en-IN')}
                          </div>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-gray-400" aria-hidden />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400" aria-hidden />
                        )}
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-4 pb-4 pt-0 border-t border-gray-100 bg-slate-50/40">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 my-3">
                        <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                          <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                            <Wallet className="w-3 h-3" /> Opening
                          </div>
                          <div
                            className={`font-semibold ${
                              party.openingBalance > 0
                                ? 'text-green-600'
                                : party.openingBalance < 0
                                  ? 'text-red-600'
                                  : 'text-gray-800'
                            }`}
                          >
                            {party.openingBalance > 0
                              ? `+₹${party.openingBalance.toLocaleString('en-IN')}`
                              : party.openingBalance < 0
                                ? `₹${party.openingBalance.toLocaleString('en-IN')}`
                                : '₹0'}
                          </div>
                        </div>
                        <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                          <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                            <FileText className="w-3 h-3" /> Bills
                          </div>
                          <div className="font-bold text-gray-900">{party.totalBills}</div>
                        </div>
                        <div className="bg-gradient-to-br from-sky-50 to-blue-50 p-3 rounded-lg border border-blue-100 shadow-sm">
                          <div className="flex items-center gap-1 text-xs text-blue-600 font-medium mb-1">
                            <TrendingUp className="w-3.5 h-3.5" /> Total amount
                          </div>
                          <div className="font-bold text-blue-800">
                            ₹{parseFloat(party.totalAmount || 0).toLocaleString('en-IN')}
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-3 rounded-lg border border-green-200 shadow-sm">
                          <div className="flex items-center gap-1.5 text-xs text-green-700 font-medium mb-1">
                            <Receipt className="w-3 h-3" /> Paid
                          </div>
                          <div className="font-bold text-green-600">
                            ₹{parseFloat(party.totalPaid || 0).toLocaleString('en-IN')}
                          </div>
                        </div>
                        <div
                          className={`p-3 rounded-lg border shadow-sm sm:col-span-2 ${
                            party.outstanding > 0
                              ? 'bg-gradient-to-br from-red-50 to-rose-50 border-red-200'
                              : 'bg-gradient-to-br from-emerald-50 to-green-50 border-green-200'
                          }`}
                        >
                          <div
                            className={`flex items-center gap-1.5 text-xs font-medium mb-1 ${
                              party.outstanding > 0 ? 'text-red-700' : 'text-green-700'
                            }`}
                          >
                            <TrendingDown className="w-3.5 h-3.5" /> Outstanding
                          </div>
                          <div
                            className={`font-bold text-base ${
                              party.outstanding > 0 ? 'text-red-600' : 'text-green-600'
                            }`}
                          >
                            ₹{parseFloat(party.outstanding || 0).toLocaleString('en-IN')}
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-violet-50 to-purple-50 p-3 rounded-lg border border-violet-100 shadow-sm sm:col-span-2">
                          <div className="flex items-center gap-1 text-xs text-violet-700 font-medium mb-1">
                            <CreditCard className="w-3 h-3" /> Advance
                          </div>
                          <div className="font-bold text-violet-800">
                            {advance > 0
                              ? `₹${advance.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
                              : '₹0'}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            paymentsFilter.setSelectedParty(party.partyId);
                            setActiveTab('invoice');
                          }}
                          className="w-full rounded-xl bg-gradient-to-r from-blue-500 to-sky-400 text-white px-3 py-2.5 text-sm font-semibold shadow-sm hover:from-blue-600 hover:to-sky-500 transition-colors"
                        >
                          View bills
                        </button>
                        {party.outstanding > 0 && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddKhataPayment(party.partyId);
                            }}
                            className="w-full rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-3 py-2.5 text-sm font-semibold shadow-sm hover:from-emerald-700 hover:to-teal-700 transition-colors"
                          >
                            Add FIFO payment
                          </button>
                        )}
                        {advance > 0 && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAdvanceModalParty(party.partyId);
                            }}
                            className="w-full rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white px-3 py-2.5 text-sm font-semibold shadow-sm hover:from-violet-700 hover:to-purple-700 transition-colors"
                          >
                            View advance details
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {showTabTotals && (() => {
              const totals = getPartyWiseTotals();
              return (
                <div className="bg-gradient-to-br from-indigo-50 to-slate-50 rounded-xl p-4 mt-1 border border-indigo-100 shadow-sm">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5 text-indigo-600" />
                    Summary
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-white/90 p-3 rounded-lg border border-white shadow-sm">
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide">Parties</div>
                      <div className="text-sm font-bold text-gray-900">{totals.totalParties}</div>
                    </div>
                    <div className="bg-white/90 p-3 rounded-lg border border-white shadow-sm">
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide">Bills</div>
                      <div className="text-sm font-bold text-gray-900">{totals.totalBills}</div>
                    </div>
                    <div className="bg-white/90 p-3 rounded-lg border border-white shadow-sm col-span-2 sm:col-span-1">
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide">Total amount</div>
                      <div className="text-sm font-bold text-blue-800">
                        ₹{totals.totalAmount.toLocaleString('en-IN')}
                      </div>
                    </div>
                    <div className="bg-white/90 p-3 rounded-lg border border-white shadow-sm col-span-2 sm:col-span-1">
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide">Paid</div>
                      <div className="text-sm font-bold text-emerald-700">
                        ₹{totals.totalPaid.toLocaleString('en-IN')}
                      </div>
                    </div>
                    <div className="col-span-2 bg-gradient-to-r from-red-50 to-rose-50 p-3 rounded-lg border border-red-100">
                      <div className="text-[10px] text-red-700 uppercase tracking-wide font-medium">
                        Total outstanding
                      </div>
                      <div className="text-sm font-bold text-red-600">
                        ₹{totals.totalOutstanding.toLocaleString('en-IN')}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
          
          {/* Mobile Pagination Controls */}
          <div className="p-4">
            <PaginationControls {...pagination} />
          </div>
        </div>
      </div>
        </>
      )}

      {/* Payment Receipts Table (for receipts tab) */}
      {activeTab === 'receipts' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-slate-200/60" id="payments-header-actions">
          <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-emerald-50 via-white to-amber-50">
            <div className="flex justify-between items-center gap-3">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2 min-w-0">
                <Receipt className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                <span className="truncate">Payment Receipts</span>
              </h2>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                <ExportShareDropdown
                  onExportPDF={() => { setExportFormat('pdf'); handleExport('receipts'); }}
                  onExportExcel={() => { setExportFormat('excel'); handleExport('receipts'); }}
                  onExportCSV={() => { setExportFormat('csv'); handleExport('receipts'); }}
                  onExportAll={() => { setExportFormat('csv'); handleExport('all'); }}
                  onSharePDF={() => sharePaymentsAsPDF('receipts')}
                  onShareExcel={() => sharePaymentsAsExcel('receipts')}
                  onShareImage={() => sharePaymentsAsImage('payments-header-actions', 'payments-receipts.png')}
                  exportFormat={exportFormat}
                />
              </div>
            </div>
          </div>

          
          {/* Desktop Table View */}
          <div id="payments-list" className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleReceiptsSort('receiptNumber')}>
                    RECEIPT NUMBER
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleReceiptsSort('partyName')}>
                    PARTY NAME
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleReceiptsSort('totalAmount')}>
                    AMOUNT
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleReceiptsSort('paymentDate')}>
                    DATE
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleReceiptsSort('paymentMode')}>
                    MODE
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleReceiptsSort('paymentType')}>
                    TYPE
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortReceipts(paymentsFilter.filterPayments(payments.filter(payment => payment.type === receiptsSubTab))).map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {payment.receiptNumber}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {payment.partyName}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      ₹{parseFloat(payment.totalAmount || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {payment.paymentDate || payment.date || '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {payment.paymentMode || (payment.isOpeningBalanceEntry ? 'Opening Balance' : '-')}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        payment.paymentType === 'opening-balance' || payment.isOpeningBalanceEntry
                          ? 'bg-purple-100 text-purple-800'
                          : payment.paymentType === 'bill' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {payment.paymentType === 'opening-balance' || payment.isOpeningBalanceEntry
                          ? 'Opening Balance'
                          : payment.paymentType === 'bill' 
                          ? 'Bill Payment' 
                          : 'Khata Payment'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex flex-wrap gap-1.5">
                        <button
                          type="button"
                          onClick={() => handlePreviewReceipt(payment)}
                          className={btnSecondarySm}
                        >
                          Preview
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeletePaymentClick(payment)}
                          className={btnDangerSm}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {/* Totals Row */}
                {showTabTotals && (() => {
                  const totals = getReceiptsTotals();
                  return (
                    <tr className="bg-gray-100 font-semibold">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        <strong>Total ({totals.totalReceipts} receipts)</strong>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        <strong>-</strong>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        <strong>₹{totals.totalAmount.toLocaleString('en-IN')}</strong>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        <strong>-</strong>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        <strong>-</strong>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        <strong>-</strong>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        <strong>-</strong>
                      </td>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden">
            <div className="p-3 space-y-3">
              {sortReceipts(paymentsFilter.filterPayments(payments.filter(payment => payment.type === receiptsSubTab))).map((payment) => {
                const isExpanded = expandedReceipts.has(payment.id);
                const isOpening =
                  payment.paymentType === 'opening-balance' || payment.isOpeningBalanceEntry;
                const typeLabel = isOpening
                  ? 'Opening'
                  : payment.paymentType === 'bill'
                    ? 'Bill'
                    : 'Khata';
                const typeBadgeClass = isOpening
                  ? 'bg-purple-100 text-purple-800'
                  : payment.paymentType === 'bill'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-emerald-100 text-emerald-800';

                return (
                  <div
                    key={payment.id}
                    className="bg-gradient-to-br from-emerald-50/80 to-white rounded-xl border border-emerald-100/80 shadow-sm overflow-hidden"
                  >
                    <div
                      className="p-4 cursor-pointer active:bg-emerald-50/50 transition-colors"
                      onClick={() => toggleReceiptExpansion(payment.id)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-md">
                          {(payment.receiptNumber || 'R').toString().charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold text-gray-900 truncate">{payment.receiptNumber}</div>
                          <div className="text-xs text-gray-500 truncate flex items-center gap-1 mt-0.5">
                            <User className="w-3 h-3 flex-shrink-0" />
                            {payment.partyName}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                          <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${typeBadgeClass}`}>
                            {typeLabel}
                          </span>
                          <div className="text-right text-emerald-700">
                            <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Amount</div>
                            <div className="text-base font-bold leading-tight">
                              ₹{parseFloat(payment.totalAmount || 0).toLocaleString('en-IN')}
                            </div>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-gray-400" aria-hidden />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400" aria-hidden />
                          )}
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="px-4 pb-4 pt-0 border-t border-emerald-100/80 bg-white/60">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 my-3">
                          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-3 rounded-lg border border-emerald-200 shadow-sm sm:col-span-2">
                            <div className="flex items-center gap-1 text-xs text-emerald-700 font-medium mb-1">
                              <TrendingUp className="w-3.5 h-3.5" /> Amount
                            </div>
                            <div className="font-bold text-emerald-800 text-lg">
                              ₹{parseFloat(payment.totalAmount || 0).toLocaleString('en-IN')}
                            </div>
                          </div>
                          <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                            <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                              <Calendar className="w-3 h-3" /> Date
                            </div>
                            <div className="font-semibold text-gray-900 text-sm">
                              {payment.paymentDate || payment.date || '—'}
                            </div>
                          </div>
                          <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                            <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                              <CreditCard className="w-3 h-3" /> Mode
                            </div>
                            <div className="font-semibold text-gray-900 text-sm">
                              {payment.paymentMode ||
                                (payment.isOpeningBalanceEntry ? 'Opening Balance' : '—')}
                            </div>
                          </div>
                          <div className="bg-sky-50 p-3 rounded-lg border border-sky-100 shadow-sm sm:col-span-2">
                            <div className="text-xs text-sky-700 font-medium mb-1">Payment type</div>
                            <div className="font-semibold text-sky-900 text-sm">
                              {isOpening
                                ? 'Opening balance'
                                : payment.paymentType === 'bill'
                                  ? 'Bill payment'
                                  : 'Khata payment'}
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePreviewReceipt(payment);
                            }}
                            className="flex-1 rounded-xl bg-gradient-to-r from-blue-500 to-sky-400 text-white px-3 py-2.5 text-sm font-semibold shadow-sm hover:from-blue-600 hover:to-sky-500 transition-colors"
                          >
                            Preview
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePaymentClick(payment);
                            }}
                            className="flex-1 rounded-xl bg-gradient-to-r from-red-600 to-rose-600 text-white px-3 py-2.5 text-sm font-semibold shadow-sm hover:from-red-700 hover:to-rose-700 transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {showTabTotals && (() => {
                const totals = getReceiptsTotals();
                return (
                  <div className="bg-gradient-to-br from-slate-100 to-emerald-50/50 rounded-xl p-4 border border-slate-200 shadow-sm">
                    <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <Receipt className="w-5 h-5 text-emerald-600" />
                      Total ({totals.totalReceipts} receipts)
                    </h3>
                    <div className="bg-white/90 p-3 rounded-lg border border-white">
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide">Total amount</div>
                      <div className="text-lg font-bold text-emerald-800">
                        ₹{totals.totalAmount.toLocaleString('en-IN')}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      <ModalOverlay
        open={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        align="bottom-sheet"
        zIndexClass="z-[200]"
      >
        <div className={paymentModalPanelClass}>
            <div className="flex justify-between items-start gap-3 mb-5 pb-4 border-b border-slate-100">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  {paymentType === 'bill' ? 'Add Bill Payment' : 'Add Khata Payment'}
                </h2>
                <p className="text-xs text-slate-500 mt-1">Press ESC to close</p>
              </div>
              <button
                type="button"
                onClick={() => setShowPaymentModal(false)}
                className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Payment Type Indicator */}
              <div className="bg-blue-50 p-3 rounded-xl border border-blue-100">
                <div className="text-sm text-blue-800">
                  {paymentType === 'bill' ? (
                    <>
                      <strong>Bill-wise Payment:</strong> Payment against specific bill
                      {selectedBill && (
                        <div className="mt-1 text-xs">
                          Bill: {getBillNumber(selectedBill)} | 
                          Outstanding: ₹{parseFloat(selectedBill?.outstanding || 0).toLocaleString('en-IN')}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <strong>Khata-wise Payment:</strong> FIFO allocation across all bills
                      <div className="mt-1 text-xs">
                        Party: {getPartyName(selectedPartyForPayment)}
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              {/* Party Selection (only for khata payments) */}
              {paymentType === 'khata' && (
                <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Party</label>
                  <AppSelect
                    value={selectedPartyForPayment}
                    onChange={(e) => setSelectedPartyForPayment(e.target.value)}
                    placeholder="Select Party"
                    aria-label="Party"
                    sheetTitle="Select party"
                    options={[
                      { value: '', label: 'Select Party' },
                      ...parties.map((party) => ({
                        value: party.id,
                        label: party.firmName || party.name || 'Party',
                      })),
                    ]}
                    triggerClassName="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-left min-h-[44px] flex items-center justify-between"
                  />
                </div>
                  
                  {/* Opening Balance Information */}
                  {selectedPartyForPayment && (() => {
                    const party = parties.find(p => p.id === selectedPartyForPayment);
                    if (party && party.openingBalance !== undefined && party.openingBalance !== 0) {
                      return (
                        <div className={`p-3 rounded-md border ${
                          party.openingBalance > 0 
                            ? 'bg-green-50 border-green-200' 
                            : 'bg-red-50 border-red-200'
                        }`}>
                          <div className={`text-sm font-medium ${
                            party.openingBalance > 0 ? 'text-green-800' : 'text-red-800'
                          }`}>
                            <strong>Opening Balance:</strong> {
                              party.openingBalance > 0 
                                ? `+₹${party.openingBalance.toLocaleString('en-IN')} (Receivable)`
                                : `-₹${Math.abs(party.openingBalance).toLocaleString('en-IN')} (Payable)`
                            }
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            {activeTab === 'receipts' && party.openingBalance > 0
                              ? 'Excess payment will reduce this receivable balance'
                              : activeTab === 'payments' && party.openingBalance < 0
                              ? 'Excess payment will reduce this payable balance'
                              : 'Opening balance represents historical outstanding'}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  {/* Advance Information for Khata Payment */}
                  {selectedPartyForPayment && (() => {
                    const availableAdvance = getPartyAdvance(selectedPartyForPayment);
                    if (availableAdvance > 0) {
                      // Calculate how much advance will be auto-applied
                      const outstandingBills = getCurrentBills().filter(bill => 
                        (bill.partyId === selectedPartyForPayment || bill.party === selectedPartyForPayment) && 
                        bill.outstanding > 0
                      );
                      
                      let advanceToBeApplied = 0;
                      if (outstandingBills.length > 0) {
                        const totalOutstanding = outstandingBills.reduce((sum, bill) => sum + bill.outstanding, 0);
                        const { allocatedAdvance } = allocateAdvanceToBill(selectedPartyForPayment, totalOutstanding);
                        advanceToBeApplied = allocatedAdvance;
                      }
                      
                      return (
                        <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
                          <div className="text-sm text-blue-800">
                            <strong>Advance Auto-Adjustment:</strong> ₹{availableAdvance.toLocaleString('en-IN')} available
                          </div>
                          {advanceToBeApplied > 0 && (
                            <div className="text-xs text-green-600 mt-1 font-medium">
                              ₹{advanceToBeApplied.toLocaleString('en-IN')} will be automatically applied to outstanding bills
                            </div>
                          )}
                          <div className="text-xs text-blue-600 mt-1">
                            Advance will be allocated chronologically (oldest bills first) before processing the new payment.
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </>
              )}
              
              {/* Party Display (for bill payments) */}
              {paymentType === 'bill' && selectedBill && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Bill Number</label>
                    <input
                      type="text"
                      value={getBillNumber(selectedBill)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Party</label>
                    <input
                      type="text"
                      value={getPartyName(selectedBill.partyId || selectedBill.party)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                      readOnly
                    />
                  </div>
                  
                  {/* Advance Information for Bill Payment */}
                  {(() => {
                    const partyId = selectedBill.partyId || selectedBill.party;
                    const availableAdvance = getPartyAdvance(partyId);
                    if (availableAdvance > 0) {
                      // Calculate how much advance will be auto-applied to this specific bill
                      const { allocatedAdvance } = allocateAdvanceToBill(partyId, selectedBill.outstanding);
                      
                      return (
                        <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
                          <div className="text-sm text-blue-800">
                            <strong>Advance Auto-Adjustment:</strong> ₹{availableAdvance.toLocaleString('en-IN')} available
                          </div>
                          {allocatedAdvance > 0 && (
                            <div className="text-xs text-green-600 mt-1 font-medium">
                              ₹{allocatedAdvance.toLocaleString('en-IN')} will be automatically applied to this bill
                            </div>
                          )}
                          <div className="text-xs text-blue-600 mt-1">
                            Advance will be applied first, then the payment amount will be allocated.
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  {/* Advance Information */}
                  {(() => {
                    const partyId = selectedBill.partyId || selectedBill.party;
                    const availableAdvance = getPartyAdvance(partyId);
                    if (availableAdvance > 0) {
                      return (
                        <div className="bg-yellow-50 p-3 rounded-md border border-yellow-200">
                          <div className="text-sm text-yellow-800">
                            <strong>Available Advance:</strong> ₹{availableAdvance.toLocaleString('en-IN')}
                          </div>
                          <div className="text-xs text-yellow-600 mt-1">
                            This advance will be automatically allocated to this bill before processing the new payment.
                          </div>
                          <div className="text-xs text-yellow-600 mt-1">
                            <strong>Note:</strong> The actual payment amount needed will be reduced by this advance amount.
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                <input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('enterAmount')}
                    step="0.01"
                    min="0"
                  />
                  {paymentType === 'bill' && selectedBill && (() => {
                    const partyId = selectedBill.partyId || selectedBill.party;
                    const availableAdvance = getPartyAdvance(partyId);
                    const actualOutstanding = Math.max(0, (selectedBill.outstanding || 0) - availableAdvance);
                    
                    return (
                      <div className="space-y-1">
                        {availableAdvance > 0 && (
                          <div className="text-xs text-gray-600">
                            <strong>Actual payment needed:</strong> ₹{actualOutstanding.toLocaleString('en-IN')} 
                            (after ₹{availableAdvance.toLocaleString('en-IN')} advance allocation)
                          </div>
                        )}

                      </div>
                    );
                  })()}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Mode</label>
                <AppSelect
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  aria-label="Payment mode"
                  sheetTitle="Payment mode"
                  options={paymentModes.map((mode) => ({ value: mode, label: mode }))}
                  triggerClassName="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-left min-h-[44px] flex items-center justify-between"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Reference</label>
                <input
                  type="text"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Cheque number, UPI reference, etc."
                />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder={t('additionalNotes')}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Receipt Number</label>
                <input
                  type="text"
                  value={receiptNumber}
                  onChange={(e) => setReceiptNumber(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  readOnly
                />
              </div>

              {/* Advance Information */}
              {showAdvanceInfo && (
                <div className="p-3 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 rounded">
                  <div className="font-semibold mb-1">Advance Information</div>
                  <div>Available Advance: <span className="font-bold">₹{advanceInfo.collected.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span></div>
                  <div>New Payment Amount: <span className="font-bold">₹{paymentAmount ? parseFloat(paymentAmount).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '0'}</span></div>
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
                    <div className="font-semibold text-green-800">Total Amount to be Applied: <span className="font-bold">₹{(advanceInfo.collected + (paymentAmount ? parseFloat(paymentAmount) : 0)).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span></div>
                    <div className="text-xs text-green-600 mt-1">This total amount (advance + payment) will be allocated to outstanding bills using FIFO.</div>
                  </div>
                  <button className="mt-2 text-blue-600 underline text-xs" onClick={() => setShowAdvanceInfo(false)}>Dismiss</button>
                </div>
              )}
              
              {(activeTab === 'payments' || activeTab === 'purchase') && (
                <WorkOrderLinkSelect
                  db={db}
                  companyCtx={companyCtx}
                  workOrderId={workOrderLink.workOrderId}
                  phaseId={workOrderLink.phaseId}
                  costCategory={workOrderLink.costCategory}
                  onChange={setWorkOrderLink}
                />
              )}

              {/* Employee Cash Flow Recording Section */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    id="recordInEmployeeCashFlow"
                    checked={recordInEmployeeCashFlow}
                    onChange={(e) => {
                      setRecordInEmployeeCashFlow(e.target.checked);
                      if (!e.target.checked) {
                        setSelectedEmployeeForCashFlow('');
                      }
                    }}
                    className="mr-2"
                  />
                  <label htmlFor="recordInEmployeeCashFlow" className="text-sm font-medium text-gray-700 cursor-pointer">
                    Also record in employee cash flow
                  </label>
                </div>
                
                {recordInEmployeeCashFlow && (
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Employee *
                    </label>
                    <AppSelect
                      value={selectedEmployeeForCashFlow}
                      onChange={(e) => setSelectedEmployeeForCashFlow(e.target.value)}
                      placeholder="Select Employee"
                      aria-label="Select employee"
                      sheetTitle="Select employee"
                      options={[
                        { value: '', label: 'Select Employee' },
                        ...(employees?.filter((emp) => emp.status === 'active') || []).map((employee) => {
                          const balance = employee.cashBalance || { cash: 0, upi: 0, total: 0 };
                          return {
                            value: employee.id,
                            label: `${employee.name} (${employee.employeeId || 'N/A'}) — Cash: ₹${balance.cash?.toLocaleString('en-IN') || 0} | UPI: ₹${balance.upi?.toLocaleString('en-IN') || 0}`,
                          };
                        }),
                      ]}
                      triggerClassName="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-left min-h-[44px] flex items-center justify-between"
                    />
                    {selectedEmployeeForCashFlow && (() => {
                      const selectedEmp = employees?.find(emp => emp.id === selectedEmployeeForCashFlow);
                      const balance = selectedEmp?.cashBalance || { cash: 0, upi: 0, total: 0 };
                      // Determine transaction type based on activeTab
                      // Invoice/Challan/Receipts = inflow (money received from customers)
                      // Purchase/Payments = outflow (money paid to suppliers)
                      const transactionType = (activeTab === 'invoice' || activeTab === 'challan' || activeTab === 'receipts') ? 'inflow' : 'outflow';
                      return (
                        <div className="mt-2 text-xs text-gray-600">
                          <div className="font-medium text-gray-700 mb-1">
                            Selected: {selectedEmp?.name} ({selectedEmp?.employeeId || 'N/A'})
                          </div>
                          <div className="text-sm">
                            <span className="font-medium">Current Balance:</span> Cash ₹{balance.cash?.toLocaleString('en-IN') || 0} | UPI ₹{balance.upi?.toLocaleString('en-IN') || 0} | Total ₹{balance.total?.toLocaleString('en-IN') || 0}
                          </div>
                          <div className={`mt-1 font-medium ${transactionType === 'inflow' ? 'text-green-600' : 'text-orange-600'}`}>
                            This will record as <strong>{transactionType}</strong> transaction {transactionType === 'inflow' ? '(money received)' : '(money paid out)'}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

            </div>
            
            <div className="flex gap-3 mt-6 pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={handleSavePayment}
                disabled={isSavingPayment}
                className="flex-1 bg-gradient-to-r from-blue-500 to-sky-400 text-white py-3 px-4 rounded-xl hover:from-blue-600 hover:to-sky-500 transition-colors font-semibold shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSavingPayment ? 'Saving…' : 'Save Payment'}
              </button>
              <button
                type="button"
                onClick={() => setShowPaymentModal(false)}
                className="flex-1 bg-slate-100 text-slate-700 py-3 px-4 rounded-xl hover:bg-slate-200 transition-colors font-semibold border border-slate-200"
              >
                Cancel
              </button>
            </div>
        </div>
      </ModalOverlay>

      {/* Payment Details Modal */}
      {showPaymentDetailsModal && selectedBill && (
        <div className={modalOverlayClass}>
          <div className={`${modalPanelClass} max-w-4xl p-4 sm:p-6`}>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-5 gap-3 pb-4 border-b border-slate-100">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-xl font-bold text-slate-900">Payment Receipts</h2>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs sm:text-sm text-slate-600 mt-2">
                  <span><strong className="text-slate-700">Bill:</strong> {selectedBill[getBillNumberField()]}</span>
                  <span><strong className="text-slate-700">Party:</strong> {getPartyName(selectedBill.partyId || selectedBill.party)}</span>
                  <span><strong className="text-slate-700">Amount:</strong> ₹{(selectedBill.totalAmount || 0).toLocaleString('en-IN')}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowPaymentDetailsModal(false)}
                className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors ml-auto"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Payment Summary */}
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Total Bill Amount:</span>
                  <span className="ml-2 text-lg font-semibold">₹{(selectedBill.totalAmount || 0).toLocaleString('en-IN')}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Total Paid:</span>
                  <span className="ml-2 text-lg font-semibold text-green-600">₹{(selectedBill.totalPaid || 0).toLocaleString('en-IN')}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Outstanding:</span>
                  <span className="ml-2 text-lg font-semibold text-red-600">₹{(selectedBill.outstanding || 0).toLocaleString('en-IN')}</span>
                </div>
              </div>
              
              {/* Advance Allocation Summary */}
              {(() => {
                const billPayments = getBillPayments(selectedBill);
                const advancePayments = billPayments.filter(payment => 
                  payment.advanceAllocations && payment.advanceAllocations.length > 0
                );
                
                if (advancePayments.length > 0) {
                  const totalAdvanceUsed = advancePayments.reduce((sum, payment) => 
                    sum + (payment.advanceUsed || 0), 0
                  );
                  
                  return (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="text-sm">
                        <span className="font-medium text-purple-700">Advance Used:</span>
                        <span className="ml-2 text-lg font-semibold text-purple-600">₹{totalAdvanceUsed.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="text-xs text-purple-600 mt-1">
                        This amount was automatically allocated from available advances
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      PAYMENT RECEIPT NO
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      AGAINST {activeTab.toUpperCase()}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      AMOUNT PAID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      DATE
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      MODE
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ACTIONS
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getBillPayments(selectedBill).map((payment) => {
                    const allocation = payment.allocations.find(a => a.billId === selectedBill.id && a.billType === activeTab);
                    return (
                      <tr key={payment.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {payment.receiptNumber}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {allocation?.billNumber || 'N/A'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          ₹{parseFloat(allocation?.allocatedAmount || 0).toLocaleString('en-IN')}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {payment.date}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {payment.paymentMode}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex flex-wrap gap-1.5">
                            <button
                              type="button"
                              onClick={() => handlePreviewReceipt(payment)}
                              className={btnSecondarySm}
                            >
                              Preview
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeletePaymentClick(payment)}
                              className={btnDangerSm}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}



      {/* Advance Details Modal */}
      {advanceModalParty && (
        <div className={`${modalOverlayClass} z-40`}>
          <div className={`${modalPanelClass} max-w-lg p-5 sm:p-6 relative`}>
            <div className="flex justify-between items-start gap-3 mb-4 pb-3 border-b border-slate-100">
              <h2 className="text-xl font-bold text-violet-800">Advance Details</h2>
              <button
                type="button"
                className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
                onClick={() => setAdvanceModalParty(null)}
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              <span className="font-semibold text-slate-800">Party:</span> {getPartyName(advanceModalParty)}
            </p>
            <div className="space-y-3 max-h-72 overflow-y-auto">
              {payments.filter(p => p.partyId === advanceModalParty && p.remainingAmount > 0 && !p.advanceRefunded && !p.advanceFullyUsed).map((adv, idx) => (
                <div key={adv.id} className="border border-violet-100 rounded-xl p-3 bg-violet-50/40 flex flex-col">
                  <div className="flex justify-between items-center">
                    <span className="font-mono text-blue-700">Receipt: {adv.receiptNumber}</span>
                    <span className="text-xs text-gray-500">{adv.paymentDate || adv.createdAt}</span>
                  </div>
                  <div className="text-sm text-gray-700">Amount: ₹{adv.remainingAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
                  <div className="text-xs text-gray-500">Mode: {adv.paymentMode}</div>
                  <div className="text-xs text-gray-500">Reference: {adv.reference || '-'}</div>
                  <div className="flex space-x-2 mt-2">
                    <button
                      className="text-blue-600 underline text-xs"
                      onClick={() => handlePreviewReceipt(adv)}
                    >
                      Preview
                    </button>
                    <button
                      className="text-red-600 underline text-xs"
                      onClick={async () => { await refundAdvance(adv.id); setAdvanceModalParty(null); }}
                    >
                      Refund
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                className="px-4 py-2.5 bg-slate-800 text-white rounded-xl hover:bg-slate-900 font-medium text-sm transition-colors"
                onClick={() => setAdvanceModalParty(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Receipt Preview Modal (should be rendered after advance modal for higher z-index) */}
      {showReceiptModal && selectedReceipt && (
                    <div className={`${modalOverlayClass} z-[9999]`}>
                      <div className={`${modalPanelClass} max-w-4xl p-4 sm:p-6`}>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3 pb-4 border-b border-slate-100">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-xl font-bold text-slate-900">Payment Receipt — {selectedReceipt.receiptNumber}</h2>
              </div>
              
              {/* Desktop Action Bar */}
              <div className="hidden sm:flex items-center space-x-4">
                <span className="text-xs text-gray-500">Press ESC to close</span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setShowReceiptModal(false);
                      setShowPaymentDetailsModal(true);
                    }}
                    className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors"
                    title={t('backToReceiptsList')}
                  >
                    ← Back to List
                  </button>
                  <button
                    onClick={printReceipt}
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
                    title={t('printUsingBrowser')}
                  >
                    Print
                  </button>
                  <button
                    onClick={generatePDFReceipt}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                    title={t('downloadAsPDF')}
                  >
                    Download PDF
                  </button>
                  <ShareButton
                    onExportPDF={async () => {
                      // Build PDF and share as attachment
                      try {
                        let jsPDFLocal = window.jsPDF || (window.jspdf && window.jspdf.jsPDF);
                        if (!jsPDFLocal) {
                          try {
                            const mod = await import('jspdf');
                            jsPDFLocal = (mod && (mod.jsPDF || mod.default)) || undefined;
                          } catch (e) {
                            console.error('Failed to load jsPDF for share:', e);
                          }
                        }
                        if (!jsPDFLocal) { error('PDF generation library not available', { title: 'PDF Error' }); return; }
                        const doc = new jsPDFLocal();
                        let y = 15;
                        doc.setFontSize(20); doc.setFont('helvetica', 'bold'); doc.setTextColor(31,41,55);
                        doc.text('PAYMENT RECEIPT', 105, y, { align: 'center' }); y += 12;
                        doc.setFontSize(12); doc.setTextColor(59,130,246); doc.setFont('helvetica', 'bold');
                        doc.text(`Receipt No: ${selectedReceipt.receiptNumber}`, 105, y, { align: 'center' }); y += 12;
                        doc.setFontSize(14); doc.setTextColor(0,0,0); doc.setFont('helvetica', 'bold');
                        doc.text(company?.firmName || company?.name || 'Company Name', 105, y, { align: 'center' }); y += 10;
                        doc.setDrawColor(209,213,219); doc.line(25, y, 185, y); y += 10;
                        doc.setFontSize(12); doc.setFont('helvetica', 'normal');
                        const addKV = (k, v) => { doc.text(`${k}: ${v}`, 25, y); y += 8; };
                        addKV('Date', selectedReceipt.paymentDate || new Date().toISOString().split('T')[0]);
                        addKV('Received From', selectedReceipt.partyName || '');
                        addKV('Amount', `₹${parseFloat(selectedReceipt.totalAmount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);
                        addKV('Payment Mode', selectedReceipt.paymentMode || 'Cash');
                        addKV('Reference', selectedReceipt.reference || '-');
                        addKV('Payment Type', selectedReceipt.paymentType === 'bill' ? 'Bill Payment' : 'Khata Payment');
                        if (selectedReceipt.notes) addKV('Notes', selectedReceipt.notes);
                        const blob = doc.output('blob');
                        const ok = await shareBlobViaSystem(blob, `receipt-${selectedReceipt.receiptNumber}.pdf`, 'application/pdf');
                        if (!ok) { doc.save(`receipt-${selectedReceipt.receiptNumber}.pdf`); }
                      } catch (e) { console.error('Share receipt PDF failed:', e); }
                    }}
                    onExportImage={async () => {
                      try {
                        const el = document.querySelector('.receipt-container');
                        if (!el) return;
                        const html2canvas = await import('html2canvas');
                        const canvas = await html2canvas.default(el, { scale: 2, useCORS: true, allowTaint: false, backgroundColor: '#ffffff' });
                        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
                        const ok = await shareBlobViaSystem(blob, `receipt-${selectedReceipt.receiptNumber}.png`, 'image/png');
                        if (!ok) {
                          const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `receipt-${selectedReceipt.receiptNumber}.png`; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
                        }
                      } catch (e) { console.error('Share receipt image failed:', e); }
                    }}
                    onShareLink={async () => {
                      try {
                        // Reuse print html content for viewer
                        const html = (() => {
                          const el = document.querySelector('.receipt-container');
                          const inner = el ? el.outerHTML : '<div>No content</div>';
                          return `<!DOCTYPE html><html><head><meta charset=\"utf-8\"/><meta name=\"viewport\" content=\"width=device-width, initial-scale=1\"/><title>Payment Receipt</title><style>body{font-family:Arial;padding:16px;background:#fff}</style></head><body>${inner}</body></html>`;
                        })();
                        const storage = getStorage(app);
                        const token = Math.random().toString(36).slice(2);
                        const path = `publicDocs/receipts/${selectedReceipt.id || selectedReceipt.receiptNumber}-${token}.html`;
                        const sref = storageRef(storage, path);
                        await uploadBytes(sref, new Blob([html], { type: 'text/html' }), { contentType: 'text/html' });
                        const url = await getDownloadURL(sref);
                        const wrapped = `https://acctoo.com/doc-viewer.html?u=${encodeURIComponent(url)}`;
                        if (navigator.share) { await navigator.share({ title: `Receipt ${selectedReceipt.receiptNumber}`, text: 'Payment Receipt', url: wrapped }); }
                        else { await navigator.clipboard.writeText(wrapped); success('Public link copied to clipboard', { title: 'Link Copied' }); }
                      } catch (e) { console.error('Share receipt link failed:', e); }
                    }}
                  />
                  <button
                    onClick={() => setShowReceiptModal(false)}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
              
              {/* Mobile Action Bar */}
              <div className="sm:hidden w-full">
                {/* Mobile Close Button */}
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-gray-500">Press ESC to close</span>
                  <button
                    onClick={() => setShowReceiptModal(false)}
                    className="text-gray-500 hover:text-gray-700 text-2xl transition-colors p-1"
                  >
                    ✕
                  </button>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setShowReceiptModal(false);
                      setShowPaymentDetailsModal(true);
                    }}
                    className="bg-gray-500 text-white px-3 py-2 rounded-md hover:bg-gray-600 transition-colors flex-1 min-w-0"
                    title={t('backToReceiptsList')}
                  >
                    ← Back
                  </button>
                  <button
                    onClick={printReceipt}
                    className="bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 transition-colors flex-1 min-w-0"
                    title={t('printUsingBrowser')}
                  >
                    Print
                  </button>
                  <button
                    onClick={generatePDFReceipt}
                    className="bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 transition-colors flex-1 min-w-0"
                    title={t('downloadAsPDF')}
                  >
                    PDF
                  </button>
                  <ShareButton
                    onExportPDF={async () => {
                      // Build PDF and share as attachment
                      try {
                        let jsPDFLocal = window.jsPDF || (window.jspdf && window.jspdf.jsPDF);
                        if (!jsPDFLocal) {
                          try {
                            const mod = await import('jspdf');
                            jsPDFLocal = (mod && (mod.jsPDF || mod.default)) || undefined;
                          } catch (e) {
                            console.error('Failed to load jsPDF for share:', e);
                          }
                        }
                        if (!jsPDFLocal) { error('PDF generation library not available', { title: 'PDF Error' }); return; }
                        const doc = new jsPDFLocal();
                        let y = 15;
                        doc.setFontSize(20); doc.setFont('helvetica', 'bold'); doc.setTextColor(31,41,55);
                        doc.text('PAYMENT RECEIPT', 105, y, { align: 'center' }); y += 12;
                        doc.setFontSize(12); doc.setTextColor(59,130,246); doc.setFont('helvetica', 'bold');
                        doc.text(`Receipt No: ${selectedReceipt.receiptNumber}`, 105, y, { align: 'center' }); y += 12;
                        doc.setFontSize(14); doc.setTextColor(0,0,0); doc.setFont('helvetica', 'bold');
                        doc.text(company?.firmName || company?.name || 'Company Name', 105, y, { align: 'center' }); y += 10;
                        doc.setDrawColor(209,213,219); doc.line(25, y, 185, y); y += 10;
                        doc.setFontSize(12); doc.setFont('helvetica', 'normal');
                        const addKV = (k, v) => { doc.text(`${k}: ${v}`, 25, y); y += 8; };
                        addKV('Date', selectedReceipt.paymentDate || new Date().toISOString().split('T')[0]);
                        addKV('Received From', selectedReceipt.partyName || '');
                        addKV('Amount', `₹${parseFloat(selectedReceipt.totalAmount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);
                        addKV('Payment Mode', selectedReceipt.paymentMode || 'Cash');
                        addKV('Reference', selectedReceipt.reference || '-');
                        addKV('Payment Type', selectedReceipt.paymentType === 'bill' ? 'Bill Payment' : 'Khata Payment');
                        if (selectedReceipt.notes) addKV('Notes', selectedReceipt.notes);
                        const blob = doc.output('blob');
                        const ok = await shareBlobViaSystem(blob, `receipt-${selectedReceipt.receiptNumber}.pdf`, 'application/pdf');
                        if (!ok) { doc.save(`receipt-${selectedReceipt.receiptNumber}.pdf`); }
                      } catch (e) { console.error('Share receipt PDF failed:', e); }
                    }}
                    onExportImage={async () => {
                      try {
                        const el = document.querySelector('.receipt-container');
                        if (!el) return;
                        const html2canvas = await import('html2canvas');
                        const canvas = await html2canvas.default(el, { scale: 2, useCORS: true, allowTaint: false, backgroundColor: '#ffffff' });
                        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
                        const ok = await shareBlobViaSystem(blob, `receipt-${selectedReceipt.receiptNumber}.png`, 'image/png');
                        if (!ok) {
                          const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `receipt-${selectedReceipt.receiptNumber}.png`; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
                        }
                      } catch (e) { console.error('Share receipt image failed:', e); }
                    }}
                    onShareLink={async () => {
                      try {
                        // Reuse print html content for viewer
                        const html = (() => {
                          const el = document.querySelector('.receipt-container');
                          const inner = el ? el.outerHTML : '<div>No content</div>';
                          return `<!DOCTYPE html><html><head><meta charset=\"utf-8\"/><meta name=\"viewport\" content=\"width=device-width, initial-scale=1\"/><title>Payment Receipt</title><style>body{font-family:Arial;padding:16px;background:#fff}</style></head><body>${inner}</body></html>`;
                        })();
                        const storage = getStorage(app);
                        const token = Math.random().toString(36).slice(2);
                        const path = `publicDocs/receipts/${selectedReceipt.id || selectedReceipt.receiptNumber}-${token}.html`;
                        const sref = storageRef(storage, path);
                        await uploadBytes(sref, new Blob([html], { type: 'text/html' }), { contentType: 'text/html' });
                        const url = await getDownloadURL(sref);
                        const wrapped = `https://acctoo.com/doc-viewer.html?u=${encodeURIComponent(url)}`;
                        if (navigator.share) { await navigator.share({ title: `Receipt ${selectedReceipt.receiptNumber}`, text: 'Payment Receipt', url: wrapped }); }
                        else { await navigator.clipboard.writeText(wrapped); success('Public link copied to clipboard', { title: 'Link Copied' }); }
                      } catch (e) { console.error('Share receipt link failed:', e); }
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              {selectedReceipt.type === 'purchase' ? (
                <PurchaseReceiptTemplate
                  receipt={selectedReceipt}
                  bill={selectedBill}
                  company={company}
                  party={parties.find(p => p.id === selectedReceipt.partyId)}
                  receiptNumber={selectedReceipt.receiptNumber}
                  fifoAllocation={selectedReceipt.allocations}
                />
              ) : (
                <ReceiptTemplate
                  receipt={selectedReceipt}
                  bill={selectedBill}
                  company={company}
                  party={parties.find(p => p.id === selectedReceipt.partyId)}
                  receiptNumber={selectedReceipt.receiptNumber}
                  fifoAllocation={selectedReceipt.allocations}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        section={MODAL_SECTIONS.PAYMENTS}
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setPaymentToDelete(null);
        }}
        title="Delete Payment"
        message="Are you sure you want to delete this payment? This action cannot be undone."
        onConfirm={handleDeletePayment}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default Payments;

